import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Typography, Box, CircularProgress } from '@mui/material';
import ModernMainLayout from './components/layout/ModernMainLayout';
// Imported eagerly, NOT lazily: this page reads the menu definition out of
// ModernMainLayout, so a lazy chunk would have to pull that whole module (and
// its ~200 icon imports) into itself. In dev that chunk timed out building
// (ChunkLoadError); in prod it would simply duplicate code already in the main
// bundle, which is where ModernMainLayout lives anyway.
import AccountingBentoMenu from './pages/financial/AccountingBentoMenu';
import InstallPrompt from './components/pwa/InstallPrompt';
import MessageBoxHost from './components/common/MessageBoxHost';
import UpdateNotification from './components/pwa/UpdateNotification';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { Provider } from 'react-redux';
import store from './redux/store';
import { refreshToken, fetchCurrentUser } from './redux/slices/authSlice';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ForceChangePasswordPage from './pages/auth/ForceChangePasswordPage';
import MfaChallengePage from './pages/auth/MfaChallengePage';
import MfaSetupPage from './pages/auth/MfaSetupPage';
import SecuritySettingsPage from './pages/auth/SecuritySettingsPage';
import StorefrontLayout from './components/ecommerce/layout/StorefrontLayout';
import PortalLayout from './components/portal/PortalLayout';

// Dashboard Pages
const ModernDashboard = lazy(() => import('./pages/ModernDashboard'));
const MDMDashboard = lazy(() => import('./pages/common/MDMDashboard'));
const CRMDashboard = lazy(() => import('./pages/crm/CRMDashboard'));
const SupportDeskDashboard = lazy(() => import('./pages/support_desk/SupportDeskDashboard'));
const SupportTicketsList = lazy(() => import('./pages/support_desk/SupportTicketsList'));
const SupportTicketDetail = lazy(() => import('./pages/support_desk/SupportTicketDetail'));
// Biztimize Notes (apps/kb_notes) replace the Support Desk Knowledge Base and
// FAQs. The reader is the Help Center host's /notes/; these are the in-app ends.
const NoteAssistantPage = lazy(() => import('./pages/knowledge/NoteAssistantPage'));
const NotesRedirect = lazy(() => import('./pages/knowledge/NotesRedirect'));
const FinancialDashboard = lazy(() => import('./pages/financial/FinancialDashboard'));
const MaterialsDashboard = lazy(() => import('./pages/materials/MaterialsDashboard'));
const MaterialMasterList = lazy(() => import('./pages/materials/MaterialMasterList'));
const MaterialFormPage = lazy(() => import('./pages/materials/MaterialFormPage'));
const ServiceMasterList = lazy(() => import('./pages/materials/ServiceMasterList'));
const ServiceFormPage = lazy(() => import('./pages/materials/ServiceFormPage'));
const MaterialVariantsPage = lazy(() => import('./pages/materials/MaterialVariantsPage'));
const VariantAttributesPage = lazy(() => import('./pages/materials/VariantAttributesPage'));
const ProductSpecificationsDocumentsList = lazy(() => import('./pages/materials/ProductSpecificationsDocumentsList'));
const PurchaseOrderPage = lazy(() => import('./pages/materials/PurchaseOrderPage'));
const PurchaseOrderLineItemsReport = lazy(() => import('./pages/materials/PurchaseOrderLineItemsReport'));
const InventoryPage = lazy(() => import('./pages/materials/InventoryPage'));
const RejectionsPage = lazy(() => import('./pages/materials/RejectionsPage'));
const MasterDataPage = lazy(() => import('./pages/materials/MasterDataPage'));
const GoodsReceiptPage = lazy(() => import('./pages/materials/GoodsReceiptPage'));
const GRCaptureListPage = lazy(() => import('./pages/materials/gr-capture/GRCaptureListPage'));
const GRCaptureProcessPage = lazy(() => import('./pages/materials/gr-capture/GRCaptureProcessPage'));
const HandlingUnitPage = lazy(() => import('./pages/materials/HandlingUnitPage'));
const GoodsIssueProductionPage = lazy(() => import('./pages/materials/GoodsIssueProductionPage'));
const GoodsIssueSalesOrderPage = lazy(() => import('./pages/materials/GoodsIssueSalesOrderPage'));
const GoodsIssueCostCenterPage = lazy(() => import('./pages/materials/GoodsIssueCostCenterPage'));
const GoodsReceiptProductionPage = lazy(() => import('./pages/materials/GoodsReceiptProductionPage'));
const MaterialDocumentListPage = lazy(() => import('./pages/materials/MaterialDocumentListPage'));
const MaterialDocumentLineItemsReport = lazy(() => import('./pages/materials/MaterialDocumentLineItemsReport'));
const TransferPage = lazy(() => import('./pages/materials/TransferPage'));
const MaterialBatchesPage = lazy(() => import('./pages/materials/MaterialBatchesPage'));
const OpeningInventoryImportPage = lazy(() => import('./pages/materials/opening-inventory'));
const PostingSetupPage = lazy(() => import('./pages/materials/posting-setup/PostingSetupPage'));
const VariantValueMapsPage = lazy(() => import('./pages/materials/VariantValueMapsPage'));
const SupplierMaterialReferencesPage = lazy(() => import('./pages/materials/SupplierMaterialReferencesPage'));
const VariantReclassificationPage = lazy(() => import('./pages/materials/VariantReclassificationPage'));
const WeightAdjustmentListPage = lazy(() => import('./pages/materials/weight-adjustments/WeightAdjustmentListPage'));
const WeightAdjustmentFormPage = lazy(() => import('./pages/materials/weight-adjustments/WeightAdjustmentFormPage'));
const ServiceAccountingPage = lazy(() => import('./pages/materials/service-accounting/ServiceAccountingPage'));
const MaterialLedgerPage = lazy(() => import('./pages/materials/material-ledger/MaterialLedgerPage'));
const InventoryDashboardPage = lazy(() => import('./pages/materials/inventory-dashboard/InventoryDashboardPage'));
const StockAbstractPage = lazy(() => import('./pages/materials/stock-abstract/StockAbstractPage'));
const ClauseLibraryPage = lazy(() => import('./pages/contract-management/ClauseLibraryPage'));
const ContractTemplatesPage = lazy(() => import('./pages/contract-management/ContractTemplatesPage'));
const ContractsPage = lazy(() => import('./pages/contract-management/ContractsPage'));
const DigitalSignaturePage = lazy(() => import('./pages/contract-management/DigitalSignaturePage'));
const RIMPage = lazy(() => import('./pages/rim/RIMPage'));
const LifeEventsPage = lazy(() => import('./pages/rim/LifeEventsPage'));
const NodeMediaPage = lazy(() => import('./pages/rim/NodeMediaPage'));
const PeoplePage = lazy(() => import('./pages/rim/PeoplePage'));
const SupplierRegistrationPage = lazy(() => import('./pages/supplier-portal/RegistrationPage'));
const SupplierLoginPage = lazy(() => import('./pages/supplier-portal/SupplierLoginPage'));
const OAuthCallbackPage = lazy(() => import('./pages/supplier-portal/OAuthCallbackPage'));
const SupplierDashboard = lazy(() => import('./pages/supplier-portal/SupplierDashboard'));
const SupplierProfilePage = lazy(() => import('./pages/supplier-portal/ProfilePage'));
const SupplierCompliancePage = lazy(() => import('./pages/supplier-portal/CompliancePage'));
const SupplierCatalogPage = lazy(() => import('./pages/supplier-portal/CatalogPage'));
const SupplierFinancialsPage = lazy(() => import('./pages/supplier-portal/FinancialStatementsPage'));
// Phase 4 Supplier Portal pages
const SupplierRFQsPage = lazy(() => import('./pages/supplier-portal/RFQsPage'));
const SupplierRFIsPage = lazy(() => import('./pages/supplier-portal/RFIsPage'));
const SupplierPurchaseOrdersPage = lazy(() => import('./pages/supplier-portal/PurchaseOrdersPage'));
const SupplierShipmentsPage = lazy(() => import('./pages/supplier-portal/ShipmentsPage'));
const SupplierInvoicesPortalPage = lazy(() => import('./pages/supplier-portal/InvoicesPage'));
const SupplierPaymentsPage = lazy(() => import('./pages/supplier-portal/PaymentsPage'));
const BuyerDiscoveryPage = lazy(() => import('./pages/supplier-portal/BuyerDiscoveryPage'));
// Phase 4 Buyer-side pages
const IncomingShipmentsPage = lazy(() => import('./pages/business-network/IncomingShipmentsPage'));
const SupplierInvoicesReviewPage = lazy(() => import('./pages/business-network/SupplierInvoicesPage'));
const SupplierDocumentsReviewPage = lazy(() => import('./pages/business-network/SupplierDocumentsReviewPage'));
// Phase 5: Intelligence & Analytics
const SupplierAnalyticsDashboardPage = lazy(() => import('./pages/supplier-portal/AnalyticsDashboardPage'));
const SupplierReviewsPage = lazy(() => import('./pages/supplier-portal/ReviewsPage'));
// Phase 7: PWA
const SupplierNotificationPreferencesPage = lazy(() => import('./pages/supplier-portal/NotificationPreferencesPage'));
const SupplierSubscriptionPage = lazy(() => import('./pages/supplier-portal/SubscriptionPage'));
const BuyerConnectionsPage = lazy(() => import('./pages/supplier-portal/BuyerConnectionsPage'));
const SupplierMessagingPage = lazy(() => import('./pages/supplier-portal/SupplierMessagingPage'));
const BuyerSupplierAnalyticsPage = lazy(() => import('./pages/business-network/SupplierAnalyticsPage'));
const SupplierDiscoveryPage = lazy(() => import('./pages/srm/SupplierDiscoveryPage'));
const SupplierOnboardingPage = lazy(() => import('./pages/srm/SupplierOnboardingPage'));
const OnboardingTemplateBuilder = lazy(() => import('./pages/srm/OnboardingTemplateBuilder'));
const BBNMessagingPage = lazy(() => import('./pages/srm/MessagingPage'));
const RFQListPage = lazy(() => import('./pages/srm/RFQListPage'));
const RFQFormPage = lazy(() => import('./pages/srm/RFQFormPage'));
const RFQDetailPage = lazy(() => import('./pages/srm/RFQDetailPage'));
const RFIListPage = lazy(() => import('./pages/srm/RFIListPage'));
const RFIFormPage = lazy(() => import('./pages/srm/RFIFormPage'));
const RFIDetailPage = lazy(() => import('./pages/srm/RFIDetailPage'));
const EvaluationTemplateListPage = lazy(() => import('./pages/srm/EvaluationTemplateListPage'));
const EvaluationTemplateFormPage = lazy(() => import('./pages/srm/EvaluationTemplateFormPage'));
const EvaluationListPage = lazy(() => import('./pages/srm/EvaluationListPage'));
const EvaluationWorkspacePage = lazy(() => import('./pages/srm/EvaluationWorkspacePage'));
const ExpeditorPage = lazy(() => import('./pages/srm/expeditor/ExpeditorPage'));
const ProductionDashboard = lazy(() => import('./pages/production/ProductionDashboard'));
// Plant Maintenance
const PMDashboard = lazy(() => import('./pages/pm/PMDashboard'));
const MaintenancePlansList = lazy(() => import('./pages/pm/MaintenancePlansList'));
const MaintenanceSchedulesList = lazy(() => import('./pages/pm/MaintenanceSchedulesList'));
const MaintenanceScheduleForm = lazy(() => import('./pages/pm/MaintenanceScheduleForm'));
const BreakdownReportsList = lazy(() => import('./pages/pm/BreakdownReportsList'));
const BreakdownReportForm = lazy(() => import('./pages/pm/BreakdownReportForm'));
const EmergencyWorkOrdersList = lazy(() => import('./pages/pm/EmergencyWorkOrdersList'));
const ExternalServiceOrdersList = lazy(() => import('./pages/pm/ExternalServiceOrdersList'));
const ReturnableAssetsRegister = lazy(() => import('./pages/pm/ReturnableAssetsRegister'));
const BreakdownMobilePage = lazy(() => import('./pages/pm/BreakdownMobilePage'));
const MachineQrLabels = lazy(() => import('./pages/pm/MachineQrLabels'));
const EmergencyWorkOrderForm = lazy(() => import('./pages/pm/EmergencyWorkOrderForm'));
const EquipmentHealthList = lazy(() => import('./pages/pm/EquipmentHealthList'));
const EquipmentHealthForm = lazy(() => import('./pages/pm/EquipmentHealthForm'));
const MaintenancePlanForm = lazy(() => import('./pages/pm/MaintenancePlanForm'));
const EquipmentSpecificationsList = lazy(() => import('./pages/pm/EquipmentSpecificationsList'));
const EquipmentSpecificationForm = lazy(() => import('./pages/pm/EquipmentSpecificationForm'));
const EquipmentOperatingParametersList = lazy(() => import('./pages/pm/EquipmentOperatingParametersList'));
const EquipmentOperatingParameterForm = lazy(() => import('./pages/pm/EquipmentOperatingParameterForm'));
const EquipmentConditionReadingsList = lazy(() => import('./pages/pm/EquipmentConditionReadingsList'));
const EquipmentConditionReadingForm = lazy(() => import('./pages/pm/EquipmentConditionReadingForm'));
const MaintenanceTasksList = lazy(() => import('./pages/pm/MaintenanceTasksList'));
const MaintenanceTaskForm = lazy(() => import('./pages/pm/MaintenanceTaskForm'));
const MaintenanceTaskExecutionsList = lazy(() => import('./pages/pm/MaintenanceTaskExecutionsList'));
const MaintenanceTaskExecutionForm = lazy(() => import('./pages/pm/MaintenanceTaskExecutionForm'));
const MaintenanceResourcesList = lazy(() => import('./pages/pm/MaintenanceResourcesList'));
const MaintenanceResourceForm = lazy(() => import('./pages/pm/MaintenanceResourceForm'));
const WorkOrderLaborList = lazy(() => import('./pages/pm/WorkOrderLaborList'));
const WorkOrderLaborForm = lazy(() => import('./pages/pm/WorkOrderLaborForm'));
const WorkOrderPartsList = lazy(() => import('./pages/pm/WorkOrderPartsList'));
const WorkOrderPartForm = lazy(() => import('./pages/pm/WorkOrderPartForm'));
const DefectAnalysesList = lazy(() => import('./pages/pm/DefectAnalysesList'));
const DefectAnalysisForm = lazy(() => import('./pages/pm/DefectAnalysisForm'));
const FailureHistoryList = lazy(() => import('./pages/pm/FailureHistoryList'));
const FailureHistoryForm = lazy(() => import('./pages/pm/FailureHistoryForm'));
const PredictiveModelsList = lazy(() => import('./pages/pm/PredictiveModelsList'));
const PredictiveModelForm = lazy(() => import('./pages/pm/PredictiveModelForm'));
const FailurePredictionsList = lazy(() => import('./pages/pm/FailurePredictionsList'));
const FailurePredictionForm = lazy(() => import('./pages/pm/FailurePredictionForm'));
const AnomalyDetectionsList = lazy(() => import('./pages/pm/AnomalyDetectionsList'));
const AnomalyDetectionForm = lazy(() => import('./pages/pm/AnomalyDetectionForm'));
const PlantList = lazy(() => import('./pages/production/plants/PlantList'));
const PlantForm = lazy(() => import('./pages/production/plants/PlantForm'));
const WorkCenterList = lazy(() => import('./pages/production/work-centers/WorkCenterList'));
const WorkCenterForm = lazy(() => import('./pages/production/work-centers/WorkCenterForm'));
const MachineList = lazy(() => import('./pages/production/machines/MachineList'));
const MachineForm = lazy(() => import('./pages/production/machines/MachineForm'));
const OperationStandardTextList = lazy(() => import('./pages/production/operation-standard-texts/OperationStandardTextList'));
const OperationStandardTextForm = lazy(() => import('./pages/production/operation-standard-texts/OperationStandardTextForm'));
const ShiftCalendarList = lazy(() => import('./pages/production/shift-calendars/ShiftCalendarList'));
const ShiftCalendarForm = lazy(() => import('./pages/production/shift-calendars/ShiftCalendarForm'));
const HolidayTypeList = lazy(() => import('./pages/production/holiday-types/HolidayTypeList'));
const HolidayTypeForm = lazy(() => import('./pages/production/holiday-types/HolidayTypeForm'));
const HolidayCalendarList = lazy(() => import('./pages/production/holiday-calendars/HolidayCalendarList'));
const HolidayCalendarForm = lazy(() => import('./pages/production/holiday-calendars/HolidayCalendarForm'));
const HolidayList = lazy(() => import('./pages/production/holidays/HolidayList'));
const HolidayForm = lazy(() => import('./pages/production/holidays/HolidayForm'));
const CapacityProfileList = lazy(() => import('./pages/production/capacity-profiles/CapacityProfileList'));
const CapacityProfileForm = lazy(() => import('./pages/production/capacity-profiles/CapacityProfileForm'));
const BOMList = lazy(() => import('./pages/production/bom/BOMList'));
const BOMDetail = lazy(() => import('./pages/production/bom/BOMDetail'));
const BOMForm = lazy(() => import('./pages/production/bom/BOMForm'));
const BOMVariantMatrix = lazy(() => import('./pages/production/bom/BOMVariantMatrix'));
const BomStudioPage = lazy(() => import('./pages/production/bom-studio/BomStudioPage'));
const BomBetaPage = lazy(() => import('./pages/production/bom-beta/BomBetaPage'));
const ChangeOrderList = lazy(() => import('./pages/production/change-orders/ChangeOrderList'));
const ChangeOrderDetail = lazy(() => import('./pages/production/change-orders/ChangeOrderDetail'));
const ChangeOrderForm = lazy(() => import('./pages/production/change-orders/ChangeOrderForm'));
const ChangeRequestList = lazy(() => import('./pages/production/change-requests/ChangeRequestList'));
const ChangeManagementSettings = lazy(() => import('./pages/production/change-orders/ChangeManagementSettings'));
const PrePackList = lazy(() => import('./pages/production/pre-packs/PrePackList'));
const PrePackForm = lazy(() => import('./pages/production/pre-packs/PrePackForm'));
const SOBOMList = lazy(() => import('./pages/production/so-bom/SOBOMList'));
const SOBOMDetail = lazy(() => import('./pages/production/so-bom/SOBOMDetail'));
const RoutingList = lazy(() => import('./pages/production/routing/RoutingList'));
const RoutingDetail = lazy(() => import('./pages/production/routing/RoutingDetail'));
const RoutingForm = lazy(() => import('./pages/production/routing/RoutingForm'));
const ProductionOrdersList = lazy(() => import('./pages/production/orders/ProductionOrdersList'));
const ProductionOrderDetail = lazy(() => import('./pages/production/orders/ProductionOrderDetail'));
const ProductionOrderForm = lazy(() => import('./pages/production/orders/ProductionOrderForm'));
const ProductionOrderCostPage = lazy(() => import('./pages/production/orders/ProductionOrderCostPage'));
const MRPRunsList = lazy(() => import('./pages/production/mrp/MRPRunsList'));
const MRPRunDetail = lazy(() => import('./pages/production/mrp/MRPRunDetail'));
const PlannedOrdersList = lazy(() => import('./pages/production/mrp/PlannedOrdersList'));
const SOReservationLedger = lazy(() => import('./pages/production/mrp/SOReservationLedger'));
const SchedulingDashboard = lazy(() => import('./pages/production/scheduling/SchedulingDashboard'));
const GanttChartPage = lazy(() => import('./pages/production/scheduling/GanttChartPage'));
const PlanningBoardPage = lazy(() => import('./pages/production/scheduling/PlanningBoardPage'));
const CapacityPlanningPage = lazy(() => import('./pages/production/capacity/CapacityPlanningPage'));
const CRPReportPage = lazy(() => import('./pages/production/capacity/CRPReportPage'));
const CapabilityWorkspace = lazy(() => import('./pages/production/capability/CapabilityWorkspace'));
const WorkOrderList = lazy(() => import('./pages/production/shop-floor/WorkOrderList'));
const OperationExecutionPage = lazy(() => import('./pages/production/shop-floor/OperationExecutionPage'));
const ProductionConfirmation = lazy(() => import('./pages/production/confirmation/ProductionConfirmation'));
const ComponentAllocationPage = lazy(() => import('./pages/production/ComponentAllocationPage'));
const MissingPartsPage = lazy(() => import('./pages/production/reports/MissingPartsPage'));
const WIPSubledgerPage = lazy(() => import('./pages/production/cost/WIPSubledgerPage'));
const ScheduleIIIPLPage = lazy(() => import('./pages/financial/ScheduleIIIPLPage'));
const AuditTrailReportPage = lazy(() => import('./pages/financial/AuditTrailReportPage'));
const PeriodClosePage = lazy(() => import('./pages/financial/PeriodClosePage'));
const FCRevaluationPage = lazy(() => import('./pages/financial/FCRevaluationPage'));
const GSTComplianceWorkspace = lazy(() => import('./pages/financial/GSTComplianceWorkspace'));
const CreditManagementPage = lazy(() => import('./pages/credit/CreditManagementPage'));
const DemoLauncherPage = lazy(() => import('./pages/demo/DemoLauncherPage'));
const VendorCapacityPage = lazy(() => import('./pages/vendor-capacity/VendorCapacityPage'));
const PricingWorkspacePage = lazy(() => import('./pages/crm/pricing/PricingWorkspacePage'));
const LandedCostPage = lazy(() => import('./pages/landed-cost/LandedCostPage'));
const SerialNumberPage = lazy(() => import('./pages/serial-numbers/SerialNumberPage'));
// FS-P2P-04: fabric roll register with QR labels
const FabricRollsPage = lazy(() => import('./pages/materials/fabric-rolls/FabricRollsPage'));
const MobileWarehousePage = lazy(() => import('./pages/wms/MobileWarehousePage'));
const ScheduleIIIBSPage = lazy(() => import('./pages/financial/ScheduleIIIBSPage'));
const ScheduleIIICFPage = lazy(() => import('./pages/financial/ScheduleIIICFPage'));
const ScheduleIIISOCEPage = lazy(() => import('./pages/financial/ScheduleIIISOCEPage'));
const ScheduleIIIWorkspace = lazy(() => import('./pages/financial/ScheduleIIIWorkspace'));
const StatementTaggingPage = lazy(() => import('./pages/financial/StatementTaggingPage'));
const FSNotesPage = lazy(() => import('./pages/financial/FSNotesPage'));
const TasksDashboard = lazy(() => import('./pages/tasks/TasksDashboard'));
const UnplannedTasksEntry = lazy(() => import('./pages/tasks/UnplannedTasksEntry'));
const ProjectsPage = lazy(() => import('./pages/tasks/ProjectsPage'));
const TaskEditPage = lazy(() => import('./pages/tasks/TaskEditPage'));
const TaskDetailPage = lazy(() => import('./pages/tasks/TaskDetailPage'));
const TaskCostReportPage = lazy(() => import('./pages/tasks/TaskCostReportPage'));
const ReportFieldLabelsPage = lazy(() => import('./pages/admin/ReportFieldLabelsPage'));
const TasksReplanningPage = lazy(() => import('./pages/tasks/TasksReplanningPage'));
const TransferEmployeePage = lazy(() => import('./pages/tasks/TransferEmployeePage'));
const TasksExcelPage = lazy(() => import('./pages/tasks/TasksExcelPage'));
const TasksReportPage = lazy(() => import('./pages/tasks/TasksReportPage'));
const TasksUploadPage = lazy(() => import('./pages/tasks/TasksUploadPage'));
const TaskTemplatesPage = lazy(() => import('./pages/tasks/TaskTemplatesPage'));
const TaskDefinitionsPage = lazy(() => import('./pages/tasks/TaskDefinitionsPage'));
const TaskDefinitionDependenciesPage = lazy(() => import('./pages/tasks/TaskDefinitionDependenciesPage'));
const CapacityDashboardPage = lazy(() => import('./pages/tasks/CapacityDashboardPage'));
const ProjectChatMobilePage = lazy(() => import('./pages/tasks/ProjectChatMobilePage'));
const DirectMessagesPage = lazy(() => import('./pages/messaging/DirectMessagesPage'));

// Cutting Room Pages
const CuttingRoomDashboard = lazy(() => import('./pages/cutting-room/CuttingRoomDashboard'));
const MarkerListPage = lazy(() => import('./pages/cutting-room/markers/MarkerListPage'));
const MarkerFormPage = lazy(() => import('./pages/cutting-room/markers/MarkerFormPage'));
const LaySlipListPage = lazy(() => import('./pages/cutting-room/lay-slips/LaySlipListPage'));
const LaySlipFormPage = lazy(() => import('./pages/cutting-room/lay-slips/LaySlipFormPage'));
const LaySlipDetailPage = lazy(() => import('./pages/cutting-room/lay-slips/LaySlipDetailPage'));
const CuttingJobListPage = lazy(() => import('./pages/cutting-room/CuttingJobListPage'));
const CuttingJobDetailPage = lazy(() => import('./pages/cutting-room/CuttingJobDetailPage'));
const CuttingNoteListPage = lazy(() => import('./pages/cutting-room/cutting-notes/CuttingNoteListPage'));
const CuttingNoteFormPage = lazy(() => import('./pages/cutting-room/cutting-notes/CuttingNoteFormPage'));
const DownSizeListPage = lazy(() => import('./pages/cutting-room/down-size/DownSizeListPage'));
const DownSizeFormPage = lazy(() => import('./pages/cutting-room/down-size/DownSizeFormPage'));
const BundleLabelsPage = lazy(() => import('./pages/cutting-room/BundleLabelsPage'));
const QCInspectionPage = lazy(() => import('./pages/cutting-room/QCInspectionPage'));
const MasterBagListPage = lazy(() => import('./pages/cutting-room/master-bags/MasterBagListPage'));
const MasterBagDetailPage = lazy(() => import('./pages/cutting-room/master-bags/MasterBagDetailPage'));
const ReportsPage = lazy(() => import('./pages/cutting-room/ReportsPage'));
const WasteTypesPage = lazy(() => import('./pages/cutting-room/waste-types/WasteTypesPage'));

// POS Pages
const POSDashboard = lazy(() => import('./pages/pos/POSDashboard'));
const StoresDashboard = lazy(() => import('./pages/pos/StoresDashboard'));
const SalesPointsList = lazy(() => import('./pages/pos/SalesPointsList'));
const SalesPointForm = lazy(() => import('./pages/pos/SalesPointForm'));
const SalesPointPricingPage = lazy(() => import('./pages/pos/SalesPointPricingPage'));
const PriceListImportPage = lazy(() => import('./pages/pos/price-list-upload'));
const DataMigrationPage = lazy(() => import('./pages/data-migration'));
const VariantListPage = lazy(() => import('./pages/data-migration/VariantListPage'));
const VariantEditorPage = lazy(() => import('./pages/data-migration/VariantEditorPage'));
const CashSalesList = lazy(() => import('./pages/pos/CashSalesList'));
const CashSaleDetail = lazy(() => import('./pages/pos/CashSaleDetail'));
const CashSaleForm = lazy(() => import('./pages/pos/CashSaleForm'));
const POSTerminal = lazy(() => import('./pages/pos/POSTerminal'));
const ShiftsList = lazy(() => import('./pages/pos/ShiftsList'));
const ShiftForm = lazy(() => import('./pages/pos/ShiftForm'));
const SettlementsList = lazy(() => import('./pages/pos/SettlementsList'));
const SettlementForm = lazy(() => import('./pages/pos/SettlementForm'));
const ReturnsList = lazy(() => import('./pages/pos/ReturnsList'));
const ReturnForm = lazy(() => import('./pages/pos/ReturnForm'));

// HRM Pages
const HRDashboardPage = lazy(() => import('./pages/hrm/dashboard/HRDashboardPage'));
const EmployeeMasterPage = lazy(() => import('./pages/hrm/EmployeeMasterPage'));
// ID card printing is its own PAGE, not a dialog: the selection travels in
// the URL (`/hrm/id-cards?ids=1,2,3`), so a print run can be re-opened.
const IdCardPrintPage = lazy(() => import('./pages/hrm/IdCardPrintPage'));
// HR Foundation Data Gaps — separation & exit (G1)
const SeparationListPage = lazy(() => import('./pages/hrm/separation/SeparationListPage'));
// Recruitment (G3). These live under /recruitment, whose API counterpart is
// proxied on '/recruitment/api' — never the bare prefix, which is these very
// page routes.
const RequisitionListPage = lazy(() => import('./pages/hrm/recruitment/RequisitionListPage'));
const RequisitionFormPage = lazy(() => import('./pages/hrm/recruitment/RequisitionFormPage'));
const CandidateListPage = lazy(() => import('./pages/hrm/recruitment/CandidateListPage'));
const CandidateFormPage = lazy(() => import('./pages/hrm/recruitment/CandidateFormPage'));
const ResumeImportPage = lazy(() => import('./pages/hrm/recruitment/resume-import'));
const ApplicationPipelinePage = lazy(() => import('./pages/hrm/recruitment/ApplicationPipelinePage'));
const OfferListPage = lazy(() => import('./pages/hrm/recruitment/OfferListPage'));

// Performance & Learning (G4). These live under /performance, whose API
// counterpart is proxied on '/performance/api' — never the bare prefix, which
// is these very page routes.
const AppraisalCyclesPage = lazy(() => import('./pages/hrm/performance/AppraisalCyclesPage'));
const CycleFormPage = lazy(() => import('./pages/hrm/performance/CycleFormPage'));
const CycleAppraisalsPage = lazy(() => import('./pages/hrm/performance/CycleAppraisalsPage'));
const MyPerformancePage = lazy(() => import('./pages/hrm/performance/MyPerformancePage'));
const TrainingPage = lazy(() => import('./pages/hrm/performance/TrainingPage'));
const MyGoalsPage = lazy(() => import('./pages/hrm/performance/MyGoalsPage'));
const GoalPlanPage = lazy(() => import('./pages/hrm/performance/GoalPlanPage'));
const GoalSetupPage = lazy(() => import('./pages/hrm/performance/GoalSetupPage'));
const CompetencyFrameworkPage = lazy(() => import('./pages/hrm/performance/CompetencyFrameworkPage'));
const CalibrationPage = lazy(() => import('./pages/hrm/performance/CalibrationPage'));
const Feedback360Page = lazy(() => import('./pages/hrm/performance/Feedback360Page'));
const My360Page = lazy(() => import('./pages/hrm/performance/My360Page'));
// Talent (§10, Phase 5). NineBoxPage is gated on `perf.view_potential` and
// deliberately has no self-service counterpart: T-3 says no employee is shown
// their own 9-box cell by the system.
const NineBoxPage = lazy(() => import('./pages/hrm/performance/NineBoxPage'));
const SuccessionPage = lazy(() => import('./pages/hrm/performance/SuccessionPage'));
const DevelopmentPage = lazy(() => import('./pages/hrm/performance/DevelopmentPage'));
// F-9 — the external rater's tokenised form. Deliberately outside
// ProtectedRoute and ModernMainLayout: a customer or vendor reaches exactly one
// questionnaire about exactly one person and has nothing else to click into.
const ExternalFeedbackPage = lazy(() => import('./pages/hrm/performance/ExternalFeedbackPage'));
// Shift Rotation & Rostering. `/hrm/time/...` — NOT a new top-level prefix:
// the API is mounted on `/hrm/api`, which `setupProxy.js` already carries.
const ShiftMasterPage = lazy(() => import('./pages/hrm/time/ShiftMasterPage'));
const ShiftPatternsPage = lazy(() => import('./pages/hrm/time/ShiftPatternsPage'));
const ShiftCrewsPage = lazy(() => import('./pages/hrm/time/ShiftCrewsPage'));
const RosterWorkspacePage = lazy(() => import('./pages/hrm/time/RosterWorkspacePage'));
const AbsenceEntryPage = lazy(() => import('./pages/hrm/time/AbsenceEntryPage'));
const PermissionRegisterPage = lazy(() => import('./pages/hrm/time/PermissionRegisterPage'));
const CoveragePage = lazy(() => import('./pages/hrm/time/CoveragePage'));
const ComplianceRegisterPage = lazy(() => import('./pages/hrm/time/ComplianceRegisterPage'));
const AttendanceRegisterPage = lazy(() => import('./pages/hrm/time/AttendanceRegisterPage'));
const ShiftSwapsPage = lazy(() => import('./pages/hrm/time/ShiftSwapsPage'));
const AttendanceDevicesPage = lazy(() => import('./pages/hrm/time/AttendanceDevicesPage'));
const AttendanceIntegrationsPage = lazy(() => import('./pages/hrm/time/AttendanceIntegrationsPage'));
const WorkSchedulesPage = lazy(() => import('./pages/hrm/time/work-schedules/WorkSchedulesPage'));
const MyRosterPage = lazy(() => import('./pages/ess/roster/MyRosterPage'));
const MyAttendancePage = lazy(() => import('./pages/ess/roster/MyAttendancePage'));
const EssHolidaysPage = lazy(() => import('./pages/ess/holidays/HolidaysPage'));
// Employee Relations (DEV-10). API proxied on '/employee-relations/api' only —
// these paths are React page routes.
const ERCaseListPage = lazy(() => import('./pages/hrm/employee-relations/CaseListPage'));
const ERCaseFormPage = lazy(() => import('./pages/hrm/employee-relations/CaseFormPage'));
const ERCaseDetailPage = lazy(() => import('./pages/hrm/employee-relations/CaseDetailPage'));
const ERComplaintListPage = lazy(() => import('./pages/hrm/employee-relations/ComplaintListPage'));
const ERComplaintDetailPage = lazy(() => import('./pages/hrm/employee-relations/ComplaintDetailPage'));
const ERSetupPage = lazy(() => import('./pages/hrm/employee-relations/ERSetupPage'));
const ERReportsPage = lazy(() => import('./pages/hrm/employee-relations/ERReportsPage'));
const EssMyNoticesPage = lazy(() => import('./pages/ess/relations/MyNoticesPage'));
const EssMyComplaintsPage = lazy(() => import('./pages/ess/relations/MyComplaintsPage'));
// Objects on Loan (DEV-11). API proxied on '/objects-on-loan/api' only —
// these paths are React page routes.
const OBLRegisterPage = lazy(() => import('./pages/hrm/objects-on-loan/RegisterPage'));
// UAT execution. API proxied on '/uat-testing/api' only — '/uat-testing' is this page.
const UATTestingPage = lazy(() => import('./pages/uat/UATTestingPage'));
const OBLIssueFormPage = lazy(() => import('./pages/hrm/objects-on-loan/IssueFormPage'));
const OBLRecordDetailPage = lazy(() => import('./pages/hrm/objects-on-loan/RecordDetailPage'));
const OBLBatchIssuePage = lazy(() => import('./pages/hrm/objects-on-loan/BatchIssuePage'));
const OBLRecoveriesPage = lazy(() => import('./pages/hrm/objects-on-loan/RecoveriesPage'));
const OBLSetupPage = lazy(() => import('./pages/hrm/objects-on-loan/SetupPage'));
const OBLReportsPage = lazy(() => import('./pages/hrm/objects-on-loan/ReportsPage'));
const EssMyObjectsPage = lazy(() => import('./pages/ess/objects/MyObjectsPage'));
// Stock Transport Orders. API proxied on '/stock-transport/api' only; these
// '/materials/stock-transport/...' paths are React page routes.
const STOListPage = lazy(() => import('./pages/materials/stock-transport/STOListPage'));
const STOFormPage = lazy(() => import('./pages/materials/stock-transport/STOFormPage'));
const STOInboundPage = lazy(() => import('./pages/materials/stock-transport/InboundPage'));
const STOInTransitPage = lazy(() => import('./pages/materials/stock-transport/InTransitPage'));
const STOSettingsPage = lazy(() => import('./pages/materials/stock-transport/SettingsPage'));
const STOReplenishmentPage = lazy(() => import('./pages/materials/stock-transport/ReplenishmentPage'));
const STOMobilePage = lazy(() => import('./pages/materials/stock-transport/STOMobilePage'));
// Hostel Management (DEV-09). API proxied on '/hostel/api' only — the bare
// '/hostel' prefix is these React page routes.
const HostelDashboardPage = lazy(() => import('./pages/hrm/hostel/DashboardPage'));
const HostelResidentsPage = lazy(() => import('./pages/hrm/hostel/ResidentsPage'));
const HostelResidencyDetailPage = lazy(() => import('./pages/hrm/hostel/ResidencyDetailPage'));
const HostelRequestsPage = lazy(() => import('./pages/hrm/hostel/RequestsPage'));
const HostelRequestDetailPage = lazy(() => import('./pages/hrm/hostel/RequestDetailPage'));
const HostelMovementPage = lazy(() => import('./pages/hrm/hostel/MovementPage'));
const HostelStrengthPage = lazy(() => import('./pages/hrm/hostel/StrengthPage'));
const HostelVisitsPage = lazy(() => import('./pages/hrm/hostel/VisitsPage'));
const HostelIncidentsPage = lazy(() => import('./pages/hrm/hostel/IncidentsPage'));
const HostelChargesPage = lazy(() => import('./pages/hrm/hostel/ChargesPage'));
const HostelReportsPage = lazy(() => import('./pages/hrm/hostel/ReportsPage'));
const HostelSetupPage = lazy(() => import('./pages/hrm/hostel/SetupPage'));
const EssMyHostelPage = lazy(() => import('./pages/ess/hostel/MyHostelPage'));
// Employee Loans & Advances (DEV-03). API proxied on '/employee-loans/api' only —
// these page routes share the prefix.
const LoanRegisterPage = lazy(() => import('./pages/hrm/employee-loans/RegisterPage'));
const LoanRequestFormPage = lazy(() => import('./pages/hrm/employee-loans/RequestFormPage'));
const LoanDetailPage = lazy(() => import('./pages/hrm/employee-loans/LoanDetailPage'));
const LoanApprovalsPage = lazy(() => import('./pages/hrm/employee-loans/ApprovalsPage'));
const LoanSetupPage = lazy(() => import('./pages/hrm/employee-loans/SetupPage'));
const LoanReportsPage = lazy(() => import('./pages/hrm/employee-loans/ReportsPage'));
const EssMyLoansPage = lazy(() => import('./pages/ess/loans/MyLoansPage'));
// Referral Incentive Scheme (DEV-01). API proxied on '/referral-incentives/api' only —
// '/referral-incentives/cases' and friends are React page routes.
const ReferralCasesPage = lazy(() => import('./pages/hrm/referral-incentives/CasesPage'));
const ReferralCaseDetailPage = lazy(() => import('./pages/hrm/referral-incentives/CaseDetailPage'));
const ReferralRunsPage = lazy(() => import('./pages/hrm/referral-incentives/RunsPage'));
const ReferralRunDetailPage = lazy(() => import('./pages/hrm/referral-incentives/RunDetailPage'));
const ReferralSchemesPage = lazy(() => import('./pages/hrm/referral-incentives/SchemesPage'));
const ReferralReportsPage = lazy(() => import('./pages/hrm/referral-incentives/ReportsPage'));
// The acknowledgement kiosk is a PUBLIC route (a shared device, no login) —
// authenticated by the device's own credential, not a user session.
const ReferralKioskPage = lazy(() => import('./pages/hrm/referral-incentives/KioskPage'));
const EssMyReferralsPage = lazy(() => import('./pages/ess/referrals/MyReferralsPage'));
// Statutory Bonus Engine (DEV-02). API proxied on '/statutory-bonus/api' only —
// '/statutory-bonus/runs' and friends are React page routes.
const BonusRunsPage = lazy(() => import('./pages/hrm/statutory-bonus/RunsPage'));
const BonusRunDetailPage = lazy(() => import('./pages/hrm/statutory-bonus/RunDetailPage'));
const BonusSetupPage = lazy(() => import('./pages/hrm/statutory-bonus/SetupPage'));
const BonusReportsPage = lazy(() => import('./pages/hrm/statutory-bonus/ReportsPage'));
const EssMyBonusPage = lazy(() => import('./pages/ess/bonus/MyBonusPage'));
// Additional Motivation Payment (DEV-04, ex 'Un-Account OT'). API proxied on
// '/additional-motivation/api' only — these page routes share the prefix.
const AmpRunsPage = lazy(() => import('./pages/hrm/additional-motivation/RunsPage'));
const AmpRunDetailPage = lazy(() => import('./pages/hrm/additional-motivation/RunDetailPage'));
const AmpHoursPage = lazy(() => import('./pages/hrm/additional-motivation/HoursPage'));
const AmpOtherEarningsPage = lazy(() => import('./pages/hrm/additional-motivation/OtherEarningsPage'));
const AmpSetupPage = lazy(() => import('./pages/hrm/additional-motivation/SetupPage'));
const SeparationFormPage = lazy(() => import('./pages/hrm/separation/SeparationFormPage'));
const SeparationDetailPage = lazy(() => import('./pages/hrm/separation/SeparationDetailPage'));
const EmployeeProfilePage = lazy(() => import('./pages/hrm/EmployeeProfilePage'));
const EmployeeFormPage = lazy(() => import('./pages/hrm/EmployeeFormPage'));
const PurchaseRequisitionPage = lazy(() => import('./pages/srm/PurchaseRequisitionPage'));
const PurchaseOrderHistoryPage = lazy(() => import('./pages/srm/PurchaseOrderHistoryPage'));
const ReplenishmentWorklist = lazy(() => import('./pages/procurement/ReplenishmentWorklist'));
const SubContractingPage = lazy(() => import('./pages/subcontract/SubContractingPage'));
const ServiceEntrySheetPage = lazy(() => import('./pages/srm/ServiceEntrySheetPage'));
const PurchaseReturnPage = lazy(() => import('./pages/srm/purchase-returns/PurchaseReturnPage'));
const VendorStockPage = lazy(() => import('./pages/subcontract/VendorStockPage'));
const NumberingSequencesPage = lazy(() => import('./pages/subcontract/NumberingSequencesPage'));
const VendorMasterListPage = lazy(() => import('./pages/srm/vendor-master/VendorMasterListPage'));
const VendorFormPage = lazy(() => import('./pages/srm/vendor-master/VendorFormPage'));
// FS-P2P-01: vendor controls reports, import LC register, balance / stock confirmations
const P2PControlsPage = lazy(() => import('./pages/srm/p2p/P2PControlsPage'));
const P2PKPIDashboardPage = lazy(() => import('./pages/srm/p2p/P2PKPIDashboardPage'));
const ImportLCPage = lazy(() => import('./pages/srm/p2p/ImportLCPage'));
const ConfirmationsPage = lazy(() => import('./pages/srm/p2p/ConfirmationsPage'));
// FS-P2P-02: job-work lots, recovery, reports, rate cards, job workers, set-up
const JobWorkPage = lazy(() => import('./pages/srm/jobwork/JobWorkPage'));
// FS-P2P-03: chain movement, direct delivery, challan e-Way Bills, ITC-04, challan ageing
const JobWorkMovementsPage = lazy(() => import('./pages/srm/jobwork/JobWorkMovementsPage'));
const RBACAdminPage = lazy(() => import('./pages/admin/rbac/RBACAdminPage'));
const DataAccessPage = lazy(() => import('./pages/admin/data-access/DataAccessPage'));
const OpsConsolePage = lazy(() => import('./pages/admin/ops-console/OpsConsolePage'));
const TableExplorerPage = lazy(() => import('./pages/administrator/table-explorer/TableExplorerPage'));
const BackupCockpitPage = lazy(() => import('./pages/administrator/backup-cockpit/BackupCockpitPage'));
const AIProviderKeysPage = lazy(() => import('./pages/administrator/ai-keys/AIProviderKeysPage'));
const BundleLabelPrinterPage = lazy(() => import('./pages/administrator/BundleLabelPrinterPage'));
const AssetTagPrinterPage = lazy(() => import('./pages/administrator/AssetTagPrinterPage'));
const UsersAdminPage = lazy(() => import('./pages/admin/users/UsersAdminPage'));
const UserPreferencesAdminPage = lazy(() => import('./pages/admin/users/UserPreferencesAdminPage'));
const EmployeeUserAssignmentPage = lazy(() => import('./pages/admin/users/EmployeeUserAssignmentPage'));
const EssDashboard = lazy(() => import('./pages/ess/dashboard/EssDashboard'));
const MyProfilePage = lazy(() => import('./pages/ess/profile/MyProfilePage'));
const ProfileRequestsInbox = lazy(() => import('./pages/ess/hr/ProfileRequestsInbox'));
const MyLeavePage = lazy(() => import('./pages/ess/leave/MyLeavePage'));
const LeaveApprovalsInbox = lazy(() => import('./pages/ess/leave/LeaveApprovalsInbox'));
const PayslipsPage = lazy(() => import('./pages/ess/payslips/PayslipsPage'));
const DirectoryPage = lazy(() => import('./pages/ess/directory/DirectoryPage'));
const MyExpensesPage = lazy(() => import('./pages/ess/expenses/MyExpensesPage'));
const ExpenseApprovalsInbox = lazy(() => import('./pages/ess/expenses/ExpenseApprovalsInbox'));

// HRM Payroll Pages
const PayrollDashboard = lazy(() => import('./pages/hrm_payroll/PayrollDashboard'));
// Statutory payroll filings (E7)
const StatutoryFilingsDashboard = lazy(() => import('./pages/hrm_filings/StatutoryFilingsDashboard'));
const FilingCalendarPage = lazy(() => import('./pages/hrm_filings/FilingCalendarPage'));
const FilingReadinessPage = lazy(() => import('./pages/hrm_filings/FilingReadinessPage'));
const FilingsListPage = lazy(() => import('./pages/hrm_filings/FilingsListPage'));
const FilingDetailPage = lazy(() => import('./pages/hrm_filings/FilingDetailPage'));
const StatutoryRegistrationsPage = lazy(() => import('./pages/hrm_filings/StatutoryRegistrationsPage'));
const PTSlabsPage = lazy(() => import('./pages/hrm_filings/PTSlabsPage'));
const CarryInsPage = lazy(() => import('./pages/hrm_filings/CarryInsPage'));
const Form16Page = lazy(() => import('./pages/hrm_filings/Form16Page'));
const WageTypesPage = lazy(() => import('./pages/hrm_payroll/WageTypesPage'));
const PayrollAreasPage = lazy(() => import('./pages/hrm_payroll/PayrollAreasPage'));
const PayrollPeriodsPage = lazy(() => import('./pages/hrm_payroll/PayrollPeriodsPage'));
const PayrollRunsPage = lazy(() => import('./pages/hrm_payroll/PayrollRunsPage'));
const PayrollResultsPage = lazy(() => import('./pages/hrm_payroll/PayrollResultsPage'));
const TaxDeclarationsPage = lazy(() => import('./pages/hrm_payroll/TaxDeclarationsPage'));
const CompensationPlansPage = lazy(() => import('./pages/hrm_payroll/CompensationPlansPage'));
const StatutoryComponentsPage = lazy(() => import('./pages/hrm_payroll/StatutoryComponentsPage'));
const TaxSlabsPage = lazy(() => import('./pages/hrm_payroll/TaxSlabsPage'));
const CountryConfigsPage = lazy(() => import('./pages/hrm_payroll/CountryConfigsPage'));
const RecurringPaymentsPage = lazy(() => import('./pages/hrm_payroll/RecurringPaymentsPage'));
const GLConfigsPage = lazy(() => import('./pages/hrm_payroll/GLConfigsPage'));
const CompensationZonesPage = lazy(() => import('./pages/hrm_payroll/CompensationZonesPage'));
const CompensationBandsPage = lazy(() => import('./pages/hrm_payroll/CompensationBandsPage'));
const CompensationStepsPage = lazy(() => import('./pages/hrm_payroll/CompensationStepsPage'));
const CompensationGridPage = lazy(() => import('./pages/hrm_payroll/CompensationGridPage'));
const EmployeeCompensationsPage = lazy(() => import('./pages/hrm_payroll/EmployeeCompensationsPage'));
const EmployeeCompensationComponentsPage = lazy(() => import('./pages/hrm_payroll/EmployeeCompensationComponentsPage'));
const AdditionalPaymentsPage = lazy(() => import('./pages/hrm_payroll/AdditionalPaymentsPage'));
const TenantOverridesPage = lazy(() => import('./pages/hrm_payroll/TenantOverridesPage'));
const AreaAssignmentsPage = lazy(() => import('./pages/hrm_payroll/AreaAssignmentsPage'));
const EmployeeTaxConfigsPage = lazy(() => import('./pages/hrm_payroll/EmployeeTaxConfigsPage'));
const TaxProjectionsPage = lazy(() => import('./pages/hrm_payroll/TaxProjectionsPage'));
const OffCyclePayrollsPage = lazy(() => import('./pages/hrm_payroll/OffCyclePayrollsPage'));
const OffCycleItemsPage = lazy(() => import('./pages/hrm_payroll/OffCycleItemsPage'));
const FinalSettlementsPage = lazy(() => import('./pages/hrm_payroll/FinalSettlementsPage'));
const RetroCalculationsPage = lazy(() => import('./pages/hrm_payroll/RetroCalculationsPage'));
const PostingLogsPage = lazy(() => import('./pages/hrm_payroll/PostingLogsPage'));
const PayslipTemplatesPage = lazy(() => import('./pages/hrm_payroll/PayslipTemplatesPage'));
const BankFileConfigsPage = lazy(() => import('./pages/hrm_payroll/BankFileConfigsPage'));
const BankFileGenerationsPage = lazy(() => import('./pages/hrm_payroll/BankFileGenerationsPage'));

// Travel Management Pages
const TravelRequestsPage = lazy(() => import('./pages/travel/TravelRequestsPage'));
const TravelRequestDetailPage = lazy(() => import('./pages/travel/TravelRequestDetailPage'));
const ExpenseClaimPage = lazy(() => import('./pages/travel/ExpenseClaimPage'));
const TravelDeskPage = lazy(() => import('./pages/travel/TravelDeskPage'));
const TravelPoliciesPage = lazy(() => import('./pages/travel/TravelPoliciesPage'));
const TravelAdvancesPage = lazy(() => import('./pages/travel/TravelAdvancesPage'));
const ExpenseCategoriesPage = lazy(() => import('./pages/travel/ExpenseCategoriesPage'));
const TravelVendorsPage = lazy(() => import('./pages/travel/TravelVendorsPage'));
const DestinationTiersPage = lazy(() => import('./pages/travel/DestinationTiersPage'));

// CRM Pages
const LeadsList = lazy(() => import('./pages/crm/LeadsList'));
const LeadPageSimple = lazy(() => import('./pages/crm/LeadPageSimple'));
const LeadDetailPage = lazy(() => import('./pages/crm/LeadDetailPage'));
const OpportunitiesList = lazy(() => import('./pages/crm/OpportunitiesList'));
const OpportunityPageSimple = lazy(() => import('./pages/crm/OpportunityPageSimple'));
const QuotationPageSimple = lazy(() => import('./pages/crm/QuotationPageSimple'));
const QuotationsList = lazy(() => import('./pages/crm/QuotationsList'));
const QuotationEntry = lazy(() => import('./pages/crm/QuotationEntry'));
const SalesOrdersList = lazy(() => import('./pages/crm/SalesOrdersList'));
// Customer-as-Vendor / Business Partner
const BusinessPartnersList = lazy(() => import('./pages/partners/BusinessPartnersList'));
const PartnerDetail = lazy(() => import('./pages/partners/PartnerDetail'));
const PartnerForm = lazy(() => import('./pages/partners/PartnerForm'));
const DetectCandidates = lazy(() => import('./pages/partners/DetectCandidates'));
const PartnerProfitability = lazy(() => import('./pages/partners/PartnerProfitability'));
// AR/AP Netting
const SetoffRunsList = lazy(() => import('./pages/fi_netting/SetoffRunsList'));
const NetExposureDashboard = lazy(() => import('./pages/fi_netting/NetExposureDashboard'));
const WithholdingMatrix = lazy(() => import('./pages/fi_netting/WithholdingMatrix'));
const BalanceTransfer = lazy(() => import('./pages/fi_netting/BalanceTransfer'));
// Job Work
const JobWorkOrdersList = lazy(() => import('./pages/job_work/JobWorkOrdersList'));
const InwardChallansList = lazy(() => import('./pages/job_work/InwardChallansList'));
const ChallanAgeing = lazy(() => import('./pages/job_work/ChallanAgeing'));
const CustomerOwnedStock = lazy(() => import('./pages/job_work/CustomerOwnedStock'));
const SOStatusReport = lazy(() => import('./pages/crm/so-status/SOStatusReport'));
const SOStatusWorkbenchPage = lazy(() => import('./pages/crm/so-status/SOStatusWorkbenchPage'));
const SOStatusLinesReport = lazy(() => import('./pages/crm/so-status/SOStatusLinesReport'));
const SOSummaryPhotosPage = lazy(() => import('./pages/crm/so-summary/SOSummaryPhotosPage'));
const MerchandisingDashboard = lazy(() => import('./pages/crm/merchandising/MerchandisingDashboard'));
const TnATemplatesPage = lazy(() => import('./pages/crm/merchandising/TnATemplatesPage'));
const TnASetupPage = lazy(() => import('./pages/crm/merchandising/TnASetupPage'));
const TnAAdherencePage = lazy(() => import('./pages/crm/merchandising/TnAAdherencePage'));
const PPMeetingsPage = lazy(() => import('./pages/crm/merchandising/PPMeetingsPage'));
const PPMeetingDetailPage = lazy(() => import('./pages/crm/merchandising/PPMeetingDetailPage'));
const OrderPipelinePage = lazy(() => import('./pages/crm/pipeline/OrderPipelinePage'));
const ProgrammePipelinePage = lazy(() => import('./pages/crm/pipeline/ProgrammePipelinePage'));
const PipelineConfigPage = lazy(() => import('./pages/crm/pipeline/PipelineConfigPage'));
const OrderClosuresPage = lazy(() => import('./pages/crm/pipeline/OrderClosuresPage'));
const OrderClosureDetailPage = lazy(() => import('./pages/crm/pipeline/OrderClosureDetailPage'));
const SalesOrderLineItemsReport = lazy(() => import('./pages/crm/SalesOrderLineItemsReport'));
const OrderBookPage = lazy(() => import('./pages/crm/OrderBookPage'));
const SOInventoryReport = lazy(() => import('./pages/crm/SOInventoryReport'));
const SalesOrderEntry = lazy(() => import('./pages/crm/SalesOrderEntry'));
const SalesOrderDashboard = lazy(() => import('./pages/crm/SalesOrderDashboard'));
const CustomerMasterPage = lazy(() => import('./pages/crm/CustomerMasterPage'));
const DeliveriesList = lazy(() => import('./pages/crm/DeliveriesList'));
const DeliveryDetailPage = lazy(() => import('./pages/crm/DeliveryDetailPage'));
const InvoicesList = lazy(() => import('./pages/crm/InvoicesList'));
const InvoiceDetailPage = lazy(() => import('./pages/crm/InvoiceDetailPage'));
const PaymentsList = lazy(() => import('./pages/crm/PaymentsList'));
const SeasonPage = lazy(() => import('./pages/crm/SeasonPageSimple'));
const CustomerPOImportPage = lazy(() => import('./pages/crm/po-import'));
const SalesReturnsList = lazy(() => import('./pages/crm/SalesReturnsList'));
const SalesReturnDetail = lazy(() => import('./pages/crm/SalesReturnDetail'));

// Financial Pages
const CashJournalPage = lazy(() => import('./pages/cash-journal/CashJournalPage'));
const CashDashboardPage = lazy(() => import('./pages/cash-journal/CashDashboardPage'));
const CashTransactionsPage = lazy(() => import('./pages/cash-journal/CashTransactionsPage'));
const CashJournalFormPage = lazy(() => import('./pages/cash-journal/CashJournalFormPage'));
const CashTransactionFormPage = lazy(() => import('./pages/cash-journal/CashTransactionFormPage'));
const CashTransactionDetailPage = lazy(() => import('./pages/cash-journal/CashTransactionDetailPage'));
const CashPaymentFormPage = lazy(() => import('./pages/cash-journal/CashPaymentFormPage'));
const CashReceiptFormPage = lazy(() => import('./pages/cash-journal/CashReceiptFormPage'));
const CashDayClosePage = lazy(() => import('./pages/cash-journal/CashDayClosePage'));
// Management by Exception (MBE)
const MBECockpitPage = lazy(() => import('./pages/mbe/MBECockpitPage'));
const ExceptionListPage = lazy(() => import('./pages/mbe/ExceptionListPage'));
const RuleConfigPage = lazy(() => import('./pages/mbe/RuleConfigPage'));
const MBEAnalyticsPage = lazy(() => import('./pages/mbe/MBEAnalyticsPage'));
const SuppressionCalendarPage = lazy(() => import('./pages/mbe/SuppressionCalendarPage'));
// Quality Management (QM)
const InspectionLotsPage = lazy(() => import('./pages/qm/InspectionLotsPage'));
const InspectionLotDetailPage = lazy(() => import('./pages/qm/InspectionLotDetailPage'));
const InspectionPlansPage = lazy(() => import('./pages/qm/InspectionPlansPage'));
const DefectRecordsPage = lazy(() => import('./pages/qm/DefectRecordsPage'));
const InspectionSamplesPage = lazy(() => import('./pages/qm/InspectionSamplesPage'));
const UsageDecisionsPage = lazy(() => import('./pages/qm/UsageDecisionsPage'));
const QMMasterDataPage = lazy(() => import('./pages/qm/QMMasterDataPage'));
const NCRPage = lazy(() => import('./pages/qm/NCRPage'));
const CAPAPage = lazy(() => import('./pages/qm/CAPAPage'));
const CustomerComplaintsPage = lazy(() => import('./pages/qm/CustomerComplaintsPage'));
const VendorComplaintsPage = lazy(() => import('./pages/qm/VendorComplaintsPage'));
const QualityAuditsPage = lazy(() => import('./pages/qm/QualityAuditsPage'));
// Renamed from `CalibrationPage`: that identifier is already taken by the
// HRM performance calibration page above, and two module-level `const`
// declarations of one name is a SyntaxError that fails the whole build.
const QMCalibrationPage = lazy(() => import('./pages/qm/CalibrationPage'));
// FS-QM-06: impact assessments, MSA studies, daily checks, calibration reports
const QMCalibrationImpactPage = lazy(() => import('./pages/qm/CalibrationImpactPage'));
const QMMSAStudiesPage = lazy(() => import('./pages/qm/MSAStudiesPage'));
const QMDailyChecksPage = lazy(() => import('./pages/qm/DailyChecksPage'));
const QMCalibrationReportsPage = lazy(() => import('./pages/qm/CalibrationReportsPage'));
// FS-QM-07: certification register and quality document control
const QMCertificationsPage = lazy(() => import('./pages/qm/CertificationsPage'));
const QMQualityDocumentsPage = lazy(() => import('./pages/qm/QualityDocumentsPage'));
const QMControlledCopiesPage = lazy(() => import('./pages/qm/ControlledCopiesPage'));
const QMDocumentTypesPage = lazy(() => import('./pages/qm/DocumentTypesPage'));
const QMRecordRetentionPage = lazy(() => import('./pages/qm/RecordRetentionPage'));
const QMQualityRolesPage = lazy(() => import('./pages/qm/QualityRolesPage'));
const QMPublishedDocumentsPage = lazy(() => import('./pages/qm/PublishedDocumentsPage'));
// FS-QM-08: quality dashboard, KPI set-up, management review, cost of quality
const QMQualityDashboardPage = lazy(() => import('./pages/qm/QualityDashboardPage'));
const QMKPISetupPage = lazy(() => import('./pages/qm/KPISetupPage'));
const QMManagementReviewPage = lazy(() => import('./pages/qm/ManagementReviewPage'));
const QMCostOfQualityPage = lazy(() => import('./pages/qm/CostOfQualityPage'));
// FS-QM-09: quality planning (PFMEA / control plan), lab test requests, SPC
const QMQualityPlanningPage = lazy(() => import('./pages/qm/QualityPlanningPage'));
const QMLabTestsPage = lazy(() => import('./pages/qm/LabTestsPage'));
const QMSPCPage = lazy(() => import('./pages/qm/SPCPage'));
const InspectionPlanOperationsPage = lazy(() => import('./pages/qm/InspectionPlanOperationsPage'));
const QMAnalyticsDashboard = lazy(() => import('./pages/qm/QMAnalyticsDashboard'));
// FS-QM-01: AQL sampling and shop-floor capture
const QMSamplingTablesPage = lazy(() => import('./pages/qm/SamplingTablesPage'));
const QMSamplingSchemesPage = lazy(() => import('./pages/qm/SamplingSchemesPage'));
const QMSwitchingKeysPage = lazy(() => import('./pages/qm/SwitchingKeysPage'));
// FS-QM-02: incoming inspection and supplier quality
const QMIncomingSetupPage = lazy(() => import('./pages/qm/IncomingSetupPage'));
const QMIncomingWorkListPage = lazy(() => import('./pages/qm/IncomingWorkListPage'));
const QMSupplierCertificatesPage = lazy(() => import('./pages/qm/SupplierCertificatesPage'));
const QMSupplierScorecardsPage = lazy(() => import('./pages/qm/SupplierScorecardsPage'));
const QMSCARPage = lazy(() => import('./pages/qm/SCARPage'));
const QMSupplierBlocksPage = lazy(() => import('./pages/qm/SupplierBlocksPage'));
// FS-QM-03: nonconformance, concessions, complaints and CAPA
const QMConcessionsPage = lazy(() => import('./pages/qm/ConcessionsPage'));
const QMActionTrackerPage = lazy(() => import('./pages/qm/ActionTrackerPage'));
const QMNonconformanceSetupPage = lazy(() => import('./pages/qm/NonconformanceSetupPage'));
// FS-QM-04: audit management and line checks
const QMAuditDashboardPage = lazy(() => import('./pages/qm/AuditDashboardPage'));
const QMAuditProgrammePage = lazy(() => import('./pages/qm/AuditProgrammePage'));
const QMAuditTemplatesPage = lazy(() => import('./pages/qm/AuditTemplatesPage'));
const QMAuditVerificationPage = lazy(() => import('./pages/qm/AuditVerificationPage'));
const QMMyAuditActionsPage = lazy(() => import('./pages/qm/MyAuditActionsPage'));
const QMLineChecksPage = lazy(() => import('./pages/qm/LineChecksPage'));
// FS-QM-05: lot release, dispatch gate, shelf life, retained samples, traceability, containment
const QMReleasesPage = lazy(() => import('./pages/qm/ReleasesPage'));
const QMReleaseSetupPage = lazy(() => import('./pages/qm/ReleaseSetupPage'));
const QMShelfLifePage = lazy(() => import('./pages/qm/ShelfLifePage'));
const QMRetainedSamplesPage = lazy(() => import('./pages/qm/RetainedSamplesPage'));
const QMTraceabilityPage = lazy(() => import('./pages/qm/TraceabilityPage'));
const QMContainmentsPage = lazy(() => import('./pages/qm/ContainmentsPage'));
const QMReleaseReportsPage = lazy(() => import('./pages/qm/ReleaseReportsPage'));
const QMAuditSetupPage = lazy(() => import('./pages/qm/AuditSetupPage'));
const QMAuditExecutePage = lazy(() => import('./pages/qm/AuditExecutePage'));
const QMNonconformanceReportsPage = lazy(() => import('./pages/qm/NonconformanceReportsPage'));
const QMInspectionReportsPage = lazy(() => import('./pages/qm/InspectionReportsPage'));
const QMCaptureHomePage = lazy(() => import('./pages/qm/capture/CaptureHomePage'));
const QMAqlAuditPage = lazy(() => import('./pages/qm/capture/AqlAuditPage'));
const QMMeasurementCheckPage = lazy(() => import('./pages/qm/capture/MeasurementCheckPage'));
const QMPackingAuditPage = lazy(() => import('./pages/qm/capture/PackingAuditPage'));
const QMTlsRoundPage = lazy(() => import('./pages/qm/capture/TlsRoundPage'));
const MeasurementChartImportPage = lazy(() => import('./pages/quality/measurement-charts'));
const MeasurementChartsPage = lazy(() => import('./pages/quality/measurement-charts/MeasurementChartsPage'));

// Batch Traceability
const BatchTraceabilityHome = lazy(() => import('./pages/batch_traceability/BatchTraceabilityHome'));
const BatchGenealogyPage = lazy(() => import('./pages/batch_traceability/BatchGenealogyPage'));
const BatchStockVisibilityPage = lazy(() => import('./pages/batch_traceability/BatchStockVisibilityPage'));
const BatchTransactionHistoryPage = lazy(() => import('./pages/batch_traceability/BatchTransactionHistoryPage'));
const RecallWizardPage = lazy(() => import('./pages/batch_traceability/RecallWizardPage'));
const BatchQualityPage = lazy(() => import('./pages/batch_traceability/BatchQualityPage'));
const ExpiryShelfLifePage = lazy(() => import('./pages/batch_traceability/ExpiryShelfLifePage'));
const BatchDocumentsPage = lazy(() => import('./pages/batch_traceability/BatchDocumentsPage'));
const BatchRiskDashboard = lazy(() => import('./pages/batch_traceability/BatchRiskDashboard'));
const TraceabilityDashboards = lazy(() => import('./pages/batch_traceability/TraceabilityDashboards'));

// Environment, Health & Safety (EHS)
const EHSDashboard = lazy(() => import('./pages/ehs/EHSDashboard'));
const IncidentsList = lazy(() => import('./pages/ehs/IncidentsList'));
const IncidentForm = lazy(() => import('./pages/ehs/IncidentForm'));
const IncidentTypesList = lazy(() => import('./pages/ehs/IncidentTypesList'));
const SeverityLevelsList = lazy(() => import('./pages/ehs/SeverityLevelsList'));
const IncidentStatusesList = lazy(() => import('./pages/ehs/IncidentStatusesList'));

// Customer Service
const CustomerServiceDashboard = lazy(() => import('./pages/customer_service/CustomerServiceDashboard'));
const ServiceTicketsList = lazy(() => import('./pages/customer_service/ServiceTicketsList'));
const ServiceTicketForm = lazy(() => import('./pages/customer_service/ServiceTicketForm'));
const CustomerAssetsList = lazy(() => import('./pages/customer_service/CustomerAssetsList'));
const CustomerAssetForm = lazy(() => import('./pages/customer_service/CustomerAssetForm'));
const RepairJobsList = lazy(() => import('./pages/customer_service/RepairJobsList'));
const RepairJobForm = lazy(() => import('./pages/customer_service/RepairJobForm'));

// Administrator
const AuditLogList = lazy(() => import('./pages/administrator/AuditLogList'));

// Risk Management (RM)
const RiskRegisterPage = lazy(() => import('./pages/rm/RiskRegisterPage'));
const RiskDashboardPage = lazy(() => import('./pages/rm/RiskDashboardPage'));
const ObligationRegisterPage = lazy(() => import('./pages/cm/ObligationRegisterPage'));
const ComplianceFindingsPage = lazy(() => import('./pages/cm/ComplianceFindingsPage'));
const ComplianceCalendarPage = lazy(() => import('./pages/cm/ComplianceCalendarPage'));
const ComplianceTasksPage = lazy(() => import('./pages/cm/ComplianceTasksPage'));
const PolicyManagementPage = lazy(() => import('./pages/cm/PolicyManagementPage'));
const ComplianceDashboardPage = lazy(() => import('./pages/cm/ComplianceDashboardPage'));

// Design Studio (generic design backbone) + PLM
const DesignStudioPage = lazy(() => import('./pages/design_studio/DesignStudioPage'));
const PlmStylesPage = lazy(() => import('./pages/plm/PlmStylesPage'));
const PlmSampleReportsPage = lazy(() => import('./pages/plm/SampleReportsPage'));
const VendorPortalPage = lazy(() => import('./pages/plm/VendorPortalPage'));

// Cash Management
const CashManagementPage = lazy(() => import('./pages/cash_mgmt/CashManagementPage'));

const InternalOrdersPage = lazy(() => import('./pages/finance/internal-orders/InternalOrdersPage'));
const InternalOrderDetailPage = lazy(() => import('./pages/finance/internal-orders/InternalOrderDetailPage'));
const CostPostingPage = lazy(() => import('./pages/finance/internal-orders/CostPostingPage'));

const ParameterDefinitionsPage = lazy(() => import('./pages/user-defaults/ParameterDefinitionsPage'));
const MyDefaultsPage = lazy(() => import('./pages/user-defaults/MyDefaultsPage'));
const NewsletterSubscriptionPage = lazy(() => import('./pages/newsletter/NewsletterSubscriptionPage'));
const RoleDefaultsPage = lazy(() => import('./pages/user-defaults/RoleDefaultsPage'));
const DepartmentDefaultsPage = lazy(() => import('./pages/user-defaults/DepartmentDefaultsPage'));
const ResolutionViewerPage = lazy(() => import('./pages/user-defaults/ResolutionViewerPage'));

const BudgetTypesPage = lazy(() => import('./pages/bcs/BudgetTypesPage'));
const BudgetDocumentsPage = lazy(() => import('./pages/bcs/BudgetDocumentsPage'));
const BudgetDocumentForm = lazy(() => import('./pages/bcs/BudgetDocumentForm'));
const BudgetTransferForm = lazy(() => import('./pages/bcs/BudgetTransferForm'));
const CostCenterMonthlyPage = lazy(() => import('./pages/bcs/CostCenterMonthlyPage'));
const ProjectCarryForwardPage = lazy(() => import('./pages/bcs/ProjectCarryForwardPage'));
const BudgetReportsPage = lazy(() => import('./pages/bcs/BudgetReportsPage'));
const BudgetPivotView = lazy(() => import('./pages/bcs/BudgetPivotView'));
const BudgetDashboardPage = lazy(() => import('./pages/bcs/BudgetDashboardPage'));
const FinancialStatementsPage = lazy(() => import('./pages/finance/statements/FinancialStatementsPage'));
const ProfitabilityAnalysisPage = lazy(() => import('./pages/finance/profitability/ProfitabilityAnalysisPage'));
const ProfitabilityMappingPage = lazy(() => import('./pages/finance/profitability/ProfitabilityMappingPage'));
const RatioAnalysisPage = lazy(() => import('./pages/ma/RatioAnalysisPage'));
const ProcessMiningPage = lazy(() => import('./pages/process-mining/ProcessMiningPage'));
const ProcessCopilotPage = lazy(() => import('./pages/process-copilot/ProcessCopilotPage'));
const ProjectListPage = lazy(() => import('./pages/proman/ProjectListPage'));
const ProjectDetailPage = lazy(() => import('./pages/proman/ProjectDetailPage'));
const TimesheetsPage = lazy(() => import('./pages/proman/TimesheetsPage'));
const TimesheetEntryPage = lazy(() => import('./pages/proman/TimesheetEntryPage'));
const ProjectPortfolioPage = lazy(() => import('./pages/proman/PortfolioPage'));
const ProjectDashboardPage = lazy(() => import('./pages/proman/ProjectDashboardPage'));
const MeetingsPage = lazy(() => import('./pages/proman/MeetingsPage'));
const ProjectResourcesPage = lazy(() => import('./pages/proman/ProjectResourcesPage'));
const MeetingDetailPage = lazy(() => import('./pages/proman/MeetingDetailPage'));
const PromanProjectChatPage = lazy(() => import('./pages/proman/ProjectChatPage'));
const ProjectCalendarsPage = lazy(() => import('./pages/proman/ProjectCalendarsPage'));
const ProjectMastersPage = lazy(() => import('./pages/proman/ProjectMastersPage'));

// Piece-Level Production Tracking. Pages live under '/production'; the API is
// mounted on '/piece-tracking/api'. The two prefixes are deliberately
// different — a proxy mount that collides with a page route breaks hard
// refresh, because the *document* request goes through the same proxy table.
const LineBoardPage = lazy(() => import('./pages/piece-tracking/LineBoardPage'));
const PieceScanPage = lazy(() => import('./pages/piece-tracking/ScanPage'));
const PiecesPage = lazy(() => import('./pages/piece-tracking/PiecesPage'));
const PieceAnalyticsPage = lazy(() => import('./pages/piece-tracking/AnalyticsPage'));
const PieceTrackingSetupPage = lazy(() => import('./pages/piece-tracking/SetupPage'));
const JournalEntriesPage = lazy(() => import('./pages/financial/JournalEntriesPage'));
const JournalEntryFormPage = lazy(() => import('./pages/financial/JournalEntryFormPage'));
const AssetRegisterPage = lazy(() => import('./pages/financial/AssetRegisterPage'));
const CompanyMasterList = lazy(() => import('./pages/financial/CompanyMasterList'));
const CompanyFormPage = lazy(() => import('./pages/financial/CompanyFormPage'));
const FiscalYearPageSimple = lazy(() => import('./pages/financial/FiscalYearPageSimple'));
const ChartOfAccountsPage = lazy(() => import('./pages/financial/ChartOfAccountsPage'));
const GLAccountsPage = lazy(() => import('./pages/financial/GLAccountsPage'));
const VendorReconciliation = lazy(() => import('./pages/fi-ap/reconciliation/ReconDashboard'));
const VendorLedgerPage = lazy(() => import('./pages/fi-ap/vendor-ledger/VendorLedgerPage'));
const VendorOpenItemsPage = lazy(() => import('./pages/fi-ap/open-items/VendorOpenItemsPage'));
const GLLedgerPage = lazy(() => import('./pages/financial/gl-ledger/GLLedgerPage'));
const BankBookPage = lazy(() => import('./pages/financial/bank-book/BankBookPage'));
const BankTransactionsPage = lazy(() => import('./pages/financial/bank-transactions/BankTransactionsPage'));
const BankTransactionFormPage = lazy(() => import('./pages/financial/bank-transactions/BankTransactionFormPage'));
const GLOpenItemsPage = lazy(() => import('./pages/financial/gl-open-items/GLOpenItemsPage'));
const PaymentInstrumentListPage = lazy(() => import('./pages/financial/payment-instruments/PaymentInstrumentListPage'));
const PaymentInstrumentFormPage = lazy(() => import('./pages/financial/payment-instruments/PaymentInstrumentFormPage'));
const PaymentInstrumentDetailPage = lazy(() => import('./pages/financial/payment-instruments/PaymentInstrumentDetailPage'));
const GLPaymentsPage = lazy(() => import('./pages/financial/gl-payments/GLPaymentsPage'));
const GLPaymentFormPage = lazy(() => import('./pages/financial/gl-payments/GLPaymentFormPage'));
const VendorInvoiceListPage = lazy(() => import('./pages/fi-ap/vendor-invoices/VendorInvoiceListPage'));
const VendorInvoiceFormPage = lazy(() => import('./pages/fi-ap/vendor-invoices/VendorInvoiceFormPage'));
const NonPOInvoiceFormPage = lazy(() => import('./pages/fi-ap/vendor-invoices/NonPOInvoiceFormPage'));
const PaymentRunsPage = lazy(() => import('./pages/fi-ap/payment-runs/PaymentRunsPage'));
const PaymentRunDetailPage = lazy(() => import('./pages/fi-ap/payment-runs/PaymentRunDetailPage'));
const DueInvoicesWorklist = lazy(() => import('./pages/fi-ap/due-invoices/DueInvoicesWorklist'));
const PaymentRegisterPage = lazy(() => import('./pages/fi-ap/payments/PaymentRegisterPage'));
const PaymentDetailPage = lazy(() => import('./pages/fi-ap/payments/PaymentDetailPage'));
const ManualPaymentPage = lazy(() => import('./pages/fi-ap/manual-payment/ManualPaymentPage'));
const AdvancePaymentPage = lazy(() => import('./pages/fi-ap/advance-payment/AdvancePaymentPage'));
const HouseBankAccountsPage = lazy(() => import('./pages/fi-ap/house-banks/HouseBankAccountsPage'));
const APPostingConfigPage = lazy(() => import('./pages/fi-ap/posting-config/APPostingConfigPage'));
const VendorAllocationPage = lazy(() => import('./pages/fi-ap/allocation/VendorAllocationPage'));
const DownPaymentListPage = lazy(() => import('./pages/fi-ap/down-payments/DownPaymentListPage'));
const DownPaymentFormPage = lazy(() => import('./pages/fi-ap/down-payments/DownPaymentFormPage'));
const DownPaymentDetailPage = lazy(() => import('./pages/fi-ap/down-payments/DownPaymentDetailPage'));
const DownPaymentAgingPage = lazy(() => import('./pages/fi-ap/down-payments/DownPaymentAgingPage'));
const VendorAdvanceStatementPage = lazy(() => import('./pages/fi-ap/down-payments/VendorAdvanceStatementPage'));
const RcmDashboardPage = lazy(() => import('./pages/fi-ap/rcm/RcmDashboardPage'));
const AdjustmentNoteListPage = lazy(() => import('./pages/fi-notes/AdjustmentNoteListPage'));
const AdjustmentNoteFormPage = lazy(() => import('./pages/fi-notes/AdjustmentNoteFormPage'));
const AdjustmentNoteDetailPage = lazy(() => import('./pages/fi-notes/AdjustmentNoteDetailPage'));
const FOCPage = lazy(() => import('./pages/foc/FOCPage'));
const PrintFormsPage = lazy(() => import('./pages/printForms/PrintFormsPage'));
const EmailChannelsPage = lazy(() => import('./pages/emailChannels/EmailChannelsPage'));
const DocumentNumberingPage = lazy(() => import('./pages/docNumbering/DocumentNumberingPage'));
const FieldStatusPage = lazy(() => import('./pages/fieldStatus/FieldStatusPage'));
const WritingAssistantSettingsPage = lazy(() => import('./pages/writingAssistant/WritingAssistantSettingsPage'));
const PISheetListPage = lazy(() => import('./pages/pi-sheet/PISheetListPage'));
const PISheetDetailPage = lazy(() => import('./pages/pi-sheet/PISheetDetailPage'));
const RecipeTemplatesPage = lazy(() => import('./pages/pi-sheet/RecipeTemplatesPage'));
const ShopfloorDataPage = lazy(() => import('./pages/pi-sheet/ShopfloorDataPage'));
const ShopfloorAnalyticsPage = lazy(() => import('./pages/pi-sheet/ShopfloorAnalyticsPage'));
const OutstandingPayablesPage = lazy(() => import('./pages/fi-ap/ap-reports/OutstandingPayablesPage'));
const VendorBalanceListPage = lazy(() => import('./pages/finance/balance-lists/PartnerBalanceListPage').then(m => ({ default: m.VendorBalanceListPage })));
const CustomerBalanceListPage = lazy(() => import('./pages/finance/balance-lists/PartnerBalanceListPage').then(m => ({ default: m.CustomerBalanceListPage })));
const CashForecastPage = lazy(() => import('./pages/fi-ap/ap-reports/CashForecastPage'));
const TdsReportPage = lazy(() => import('./pages/fi-ap/ap-reports/TdsReportPage'));
const MSMEOutstandingPage = lazy(() => import('./pages/fi-ap/ap-reports/MSMEOutstandingPage'));
const GrIrReportPage = lazy(() => import('./pages/fi-ap/ap-reports/GrIrReportPage'));
const AssetListPage = lazy(() => import('./pages/fi-aa/AssetListPage'));
const AssetFormPage = lazy(() => import('./pages/fi-aa/AssetFormPage'));
const AssetDetailPage = lazy(() => import('./pages/fi-aa/AssetDetailPage'));
const AssetClassPage = lazy(() => import('./pages/fi-aa/AssetClassPage'));
const DepreciationKeyPage = lazy(() => import('./pages/fi-aa/DepreciationKeyPage'));
const DepreciationKeyFormPage = lazy(() => import('./pages/fi-aa/DepreciationKeyFormPage'));
const AcquisitionPage = lazy(() => import('./pages/fi-aa/AcquisitionPage'));
const AcquisitionFormPage = lazy(() => import('./pages/fi-aa/AcquisitionFormPage'));
const AssetReceiptPage = lazy(() => import('./pages/fi-aa/AssetReceiptPage'));
const AssetReceiptFormPage = lazy(() => import('./pages/fi-aa/AssetReceiptFormPage'));
const CapexOnOrderPage = lazy(() => import('./pages/fi-aa/CapexOnOrderPage'));
const DepreciationRunPage = lazy(() => import('./pages/fi-aa/DepreciationRunPage'));
const AssetTakeoverPage = lazy(() => import('./pages/fi-aa/AssetTakeoverPage'));
const IncomeTaxDepreciationPage = lazy(() => import('./pages/fi-aa/IncomeTaxDepreciationPage'));
const AssetTransferPage = lazy(() => import('./pages/fi-aa/AssetTransferPage'));
const AssetTransferDetailPage = lazy(() => import('./pages/fi-aa/AssetTransferDetailPage'));
const AssetTransferFormPage = lazy(() => import('./pages/fi-aa/AssetTransferFormPage'));
const AssetDisposalPage = lazy(() => import('./pages/fi-aa/AssetDisposalPage'));
const AssetDisposalFormPage = lazy(() => import('./pages/fi-aa/AssetDisposalFormPage'));
const AssetRevaluationPage = lazy(() => import('./pages/fi-aa/AssetRevaluationPage'));
const AssetRevaluationFormPage = lazy(() => import('./pages/fi-aa/AssetRevaluationFormPage'));
const AssetReportsPage = lazy(() => import('./pages/fi-aa/AssetReportsPage'));
const AssetSchedulePage = lazy(() => import('./pages/fi-aa/AssetSchedulePage'));
const TdsTcsPage = lazy(() => import('./pages/financial/TdsTcsPage'));
const GstReconPage = lazy(() => import('./pages/financial/GstReconPage'));
const RelatedPartiesPage = lazy(() => import('./pages/financial/RelatedPartiesPage'));
const ConsolidationWorkspace = lazy(() => import('./pages/financial/ConsolidationWorkspace'));
const XbrlPage = lazy(() => import('./pages/financial/XbrlPage'));
const GSTRefundPendingPage = lazy(() => import('./pages/fi-gst/GSTRefundPendingPage'));
const CustomerReconciliation = lazy(() => import('./pages/fi-ar/customer-reconciliation'));
const DunningPage = lazy(() => import('./pages/fi-ar/dunning'));
const ReceivablesDashboard = lazy(() => import('./pages/fi-ar/receivables-dashboard'));
const PayablesDashboard = lazy(() => import('./pages/fi-ap/payables-dashboard'));
const PaymentsReceivedPage = lazy(() => import('./pages/fi-ar/payments-received').then(m => ({ default: m.PaymentsReceivedPage })));
const PaymentFormPage = lazy(() => import('./pages/fi-ar/payments-received').then(m => ({ default: m.PaymentFormPage })));
const GSTAdvanceConfigPage = lazy(() => import('./pages/fi-ar/gst-advance-config'));
const GSTReceiptVouchersPage = lazy(() => import('./pages/fi-ar/gst-receipt-vouchers'));
const PaymentAllocationPage = lazy(() => import('./pages/fi-ar/payment-allocation'));
const UnpaidInvoicesPage = lazy(() => import('./pages/fi-ar/unpaid-invoices'));
const CustomerLedgerPage = lazy(() => import('./pages/fi-ar/customer-ledger'));
const DirectInvoiceListPage = lazy(() => import('./pages/fi-ar/direct-invoices/DirectInvoiceListPage'));
const DirectInvoiceFormPage = lazy(() => import('./pages/fi-ar/direct-invoices/DirectInvoiceFormPage'));
const BankReconciliation = lazy(() => import('./pages/financial/bank-reconciliation'));
const ExportBenefitsDashboard = lazy(() => import('./pages/exim/export-benefits-dashboard'));
// EXIM — imports (Bill of Entry), duty-free authorisations, export obligations
const ImportConsignmentListPage = lazy(() => import('./pages/exim/imports/ImportConsignmentListPage'));
const ImportConsignmentFormPage = lazy(() => import('./pages/exim/imports/ImportConsignmentFormPage'));
const AuthorisationListPage = lazy(() => import('./pages/exim/authorisations/AuthorisationListPage'));
const AuthorisationFormPage = lazy(() => import('./pages/exim/authorisations/AuthorisationFormPage'));
const ExportObligationPage = lazy(() => import('./pages/exim/ExportObligationPage'));
const EximDutySetupPage = lazy(() => import('./pages/exim/DutySetupPage'));
const CustomsInvoiceListPage = lazy(() => import('./pages/exim/CustomsInvoiceListPage'));
const ExchangeRatePage = lazy(() => import('./pages/forex/ExchangeRatePage'));

// Search Pages
const SearchPage = lazy(() => import('./pages/search/SearchPage'));

// Chat Pages
const AskBiztimizePage = lazy(() => import('./pages/ask-biztimize/AskBiztimizePage'));

// Common Pages
const CurrencyPage = lazy(() => import('./pages/common/CurrencyPageSimple'));
const CountryPage = lazy(() => import('./pages/common/CountryPageSimple'));
const RegionPage = lazy(() => import('./pages/common/RegionPageSimple'));
const DistrictPage = lazy(() => import('./pages/common/DistrictPageSimple'));
const PaymentTermPage = lazy(() => import('./pages/common/PaymentTermPageSimple'));
const TDSSectionPage = lazy(() => import('./pages/common/TDSSectionPage'));
const IncotermPage = lazy(() => import('./pages/common/IncotermPage'));
const UnitOfMeasurePage = lazy(() => import('./pages/common/UnitOfMeasurePageSimple'));
const HierarchyManagement = lazy(() => import('./pages/common/HierarchyManagement'));
const HierarchyWorkspace = lazy(() => import('./pages/common/hierarchy/HierarchyWorkspace'));
const OrgStructurePage = lazy(() => import('./pages/hrm-om/OrgStructurePage'));
const GSTPage = lazy(() => import('./pages/common/GSTPage'));

// REM Pages
const RemDashboard = lazy(() => import('./pages/rem/RemDashboard'));
const PropertiesList = lazy(() => import('./pages/rem/PropertiesList'));
const PropertyForm = lazy(() => import('./pages/rem/PropertyForm'));
const PropertyMapView = lazy(() => import('./pages/rem/PropertyMapView'));
const TenantsList = lazy(() => import('./pages/rem/TenantsList'));
const TenantForm = lazy(() => import('./pages/rem/TenantForm'));
const LeasesList = lazy(() => import('./pages/rem/LeasesList'));
const LeaseForm = lazy(() => import('./pages/rem/LeaseForm'));
const RentPaymentsList = lazy(() => import('./pages/rem/RentPaymentsList'));
const RentPaymentForm = lazy(() => import('./pages/rem/RentPaymentForm'));
const MaintenanceRequestsList = lazy(() => import('./pages/rem/MaintenanceRequestsList'));
const MaintenanceRequestForm = lazy(() => import('./pages/rem/MaintenanceRequestForm'));
const InspectionSchedulesList = lazy(() => import('./pages/rem/InspectionSchedulesList'));
const InspectionScheduleForm = lazy(() => import('./pages/rem/InspectionScheduleForm'));
const InspectionReportsList = lazy(() => import('./pages/rem/InspectionReportsList'));
const InspectionReportForm = lazy(() => import('./pages/rem/InspectionReportForm'));
const CommonAreasList = lazy(() => import('./pages/rem/CommonAreasList'));
const CommonAreaForm = lazy(() => import('./pages/rem/CommonAreaForm'));
const FloorsList = lazy(() => import('./pages/rem/FloorsList'));
const FloorForm = lazy(() => import('./pages/rem/FloorForm'));
const SpacesList = lazy(() => import('./pages/rem/SpacesList'));
const SpaceForm = lazy(() => import('./pages/rem/SpaceForm'));
const SpaceTypesList = lazy(() => import('./pages/rem/SpaceTypesList'));
const SpaceTypeForm = lazy(() => import('./pages/rem/SpaceTypeForm'));
const SpaceAmenitiesList = lazy(() => import('./pages/rem/SpaceAmenitiesList'));
const SpaceAmenityForm = lazy(() => import('./pages/rem/SpaceAmenityForm'));
const SpaceModificationsList = lazy(() => import('./pages/rem/SpaceModificationsList'));
const SpaceModificationForm = lazy(() => import('./pages/rem/SpaceModificationForm'));
const SpaceOccupanciesList = lazy(() => import('./pages/rem/SpaceOccupanciesList'));
const SpaceOccupancyForm = lazy(() => import('./pages/rem/SpaceOccupancyForm'));
const PropertyTypesList = lazy(() => import('./pages/rem/PropertyTypesList'));
const PropertyTypeForm = lazy(() => import('./pages/rem/PropertyTypeForm'));
const PropertyStatusesList = lazy(() => import('./pages/rem/PropertyStatusesList'));
const PropertyStatusForm = lazy(() => import('./pages/rem/PropertyStatusForm'));
const PropertySFNumbersList = lazy(() => import('./pages/rem/PropertySFNumbersList'));
const PropertySFNumberForm = lazy(() => import('./pages/rem/PropertySFNumberForm'));
const PropertyDocumentsList = lazy(() => import('./pages/rem/PropertyDocumentsList'));
const PropertyDocumentForm = lazy(() => import('./pages/rem/PropertyDocumentForm'));
const PropertyOwnershipsList = lazy(() => import('./pages/rem/PropertyOwnershipsList'));
const PropertyOwnershipForm = lazy(() => import('./pages/rem/PropertyOwnershipForm'));
const PropertyValuationsList = lazy(() => import('./pages/rem/PropertyValuationsList'));
const PropertyValuationForm = lazy(() => import('./pages/rem/PropertyValuationForm'));
const PropertyTaxesList = lazy(() => import('./pages/rem/PropertyTaxesList'));
const PropertyTaxForm = lazy(() => import('./pages/rem/PropertyTaxForm'));
const PropertyInsurancesList = lazy(() => import('./pages/rem/PropertyInsurancesList'));
const PropertyInsuranceForm = lazy(() => import('./pages/rem/PropertyInsuranceForm'));
const PropertyExpensesList = lazy(() => import('./pages/rem/PropertyExpensesList'));
const PropertyExpenseForm = lazy(() => import('./pages/rem/PropertyExpenseForm'));
const SharedDocumentsList = lazy(() => import('./pages/rem/SharedDocumentsList'));
const SharedDocumentForm = lazy(() => import('./pages/rem/SharedDocumentForm'));
const LeaseTypesList = lazy(() => import('./pages/rem/LeaseTypesList'));
const LeaseTypeForm = lazy(() => import('./pages/rem/LeaseTypeForm'));
const LeaseRenewalOffersList = lazy(() => import('./pages/rem/LeaseRenewalOffersList'));
const LeaseRenewalOfferForm = lazy(() => import('./pages/rem/LeaseRenewalOfferForm'));
const LeaseTerminationsList = lazy(() => import('./pages/rem/LeaseTerminationsList'));
const LeaseTerminationForm = lazy(() => import('./pages/rem/LeaseTerminationForm'));
const MaintenanceActivitiesList = lazy(() => import('./pages/rem/MaintenanceActivitiesList'));
const MaintenanceActivityForm = lazy(() => import('./pages/rem/MaintenanceActivityForm'));
const TenantTypesList = lazy(() => import('./pages/rem/TenantTypesList'));
const TenantTypeForm = lazy(() => import('./pages/rem/TenantTypeForm'));
const TenantContactsList = lazy(() => import('./pages/rem/TenantContactsList'));
const TenantAddressesList = lazy(() => import('./pages/rem/TenantAddressesList'));
const TenantAddressForm = lazy(() => import('./pages/rem/TenantAddressForm'));
const TenantContactForm = lazy(() => import('./pages/rem/TenantContactForm'));
const TenantApprovalsList = lazy(() => import('./pages/rem/TenantApprovalsList'));
const TenantApprovalForm = lazy(() => import('./pages/rem/TenantApprovalForm'));
const TenantCommunicationsList = lazy(() => import('./pages/rem/TenantCommunicationsList'));
const TenantCommunicationForm = lazy(() => import('./pages/rem/TenantCommunicationForm'));
const TenantDocumentsList = lazy(() => import('./pages/rem/TenantDocumentsList'));
const TenantDocumentForm = lazy(() => import('./pages/rem/TenantDocumentForm'));
const TenantHistoriesList = lazy(() => import('./pages/rem/TenantHistoriesList'));
const TenantHistoryForm = lazy(() => import('./pages/rem/TenantHistoryForm'));
const TenantIncidentsList = lazy(() => import('./pages/rem/TenantIncidentsList'));
const TenantIncidentForm = lazy(() => import('./pages/rem/TenantIncidentForm'));
const TenantNotificationsList = lazy(() => import('./pages/rem/TenantNotificationsList'));
const TenantNotificationForm = lazy(() => import('./pages/rem/TenantNotificationForm'));
const TenantScreeningsList = lazy(() => import('./pages/rem/TenantScreeningsList'));
const TenantScreeningForm = lazy(() => import('./pages/rem/TenantScreeningForm'));
const UtilityChargesList = lazy(() => import('./pages/rem/UtilityChargesList'));
const UtilityChargeForm = lazy(() => import('./pages/rem/UtilityChargeForm'));
// Real Estate reports — derived views over the leases, payments and spaces the
// CRUD screens maintain (apps/REM/reports.py).
const RentRollReport = lazy(() => import('./pages/rem/reports/RentRollReport'));
const LeaseExpiryReport = lazy(() => import('./pages/rem/reports/LeaseExpiryReport'));
const RentArrearsReport = lazy(() => import('./pages/rem/reports/RentArrearsReport'));
const OccupancyReport = lazy(() => import('./pages/rem/reports/OccupancyReport'));
const PropertyIncomeExpenseReport = lazy(() => import('./pages/rem/reports/PropertyIncomeExpenseReport'));
const TenantLedgerReport = lazy(() => import('./pages/rem/reports/TenantLedgerReport'));
const RealEstateSettingsList = lazy(() => import('./pages/rem/RealEstateSettingsList'));
const RealEstateSettingsForm = lazy(() => import('./pages/rem/RealEstateSettingsForm'));

// Textile Costing Pages
const ConsolidatedCostingView = lazy(() => import('./pages/textile_costing').then(m => ({ default: m.ConsolidatedCostingView })));
const CostingOpportunitiesList = lazy(() => import('./pages/textile_costing').then(m => ({ default: m.CostingOpportunitiesList })));
const TextileCostingDashboard = lazy(() => import('./pages/textile_costing').then(m => ({ default: m.TextileCostingDashboard })));
const CostingListPage = lazy(() => import('./pages/textile_costing').then(m => ({ default: m.CostingListPage })));
const CutPlansList = lazy(() => import('./pages/textile_costing').then(m => ({ default: m.CutPlansList })));
const GreigeCostingsList = lazy(() => import('./pages/textile_costing').then(m => ({ default: m.GreigeCostingsList })));
const FinishedCostingsList = lazy(() => import('./pages/textile_costing').then(m => ({ default: m.FinishedCostingsList })));
const MadeUpCostingsList = lazy(() => import('./pages/textile_costing').then(m => ({ default: m.MadeUpCostingsList })));
const CutPlanWorksheetPage = lazy(() => import('./pages/textile_costing').then(m => ({ default: m.CutPlanWorksheetPage })));
const TerryFabricCostingsList = lazy(() => import('./pages/textile_costing').then(m => ({ default: m.TerryFabricCostingsList })));
const TerryMadeUpCostingsList = lazy(() => import('./pages/textile_costing').then(m => ({ default: m.TerryMadeUpCostingsList })));
const SetCostingsList = lazy(() => import('./pages/textile_costing').then(m => ({ default: m.SetCostingsList })));
const TextileCostingWorkspace = lazy(() => import('./pages/textile_costing').then(m => ({ default: m.TextileCostingWorkspace })));
const TCComponentMasterPage = lazy(() => import('./pages/textile_costing').then(m => ({ default: m.ComponentMasterPage })));
const TCPriceRevisionsPage = lazy(() => import('./pages/textile_costing').then(m => ({ default: m.PriceRevisionsPage })));
const TCPriceRevisionDetailPage = lazy(() => import('./pages/textile_costing').then(m => ({ default: m.PriceRevisionDetailPage })));

// Garment Costing Pages
const GarmentCostingWorkspace = lazy(() => import('./pages/garment_costing').then(m => ({ default: m.GarmentCostingWorkspace })));
const GarmentCostingWorkspaceV2 = lazy(() => import('./pages/garment_costing').then(m => ({ default: m.GarmentCostingWorkspaceV2 })));
const GarmentCostingDashboard = lazy(() => import('./pages/garment_costing').then(m => ({ default: m.GarmentCostingDashboard })));
const StyleCostingsList = lazy(() => import('./pages/garment_costing').then(m => ({ default: m.StyleCostingsList })));
const GcCommercialMastersPage = lazy(() => import('./pages/garment_costing').then(m => ({ default: m.CommercialMastersPage })));
const GcCostingResultsPage = lazy(() => import('./pages/garment_costing').then(m => ({ default: m.CostingResultsPage })));

// Document Pages
const DocumentViewPage = lazy(() => import('./pages/documents/DocumentViewPage'));

// Workflow Pages
const MyApprovalsPage = lazy(() => import('./pages/workflow/MyApprovalsPage'));
const PendingApprovalsAdminPage = lazy(() => import('./pages/workflow/PendingApprovalsAdminPage'));
const WorkflowListPage = lazy(() => import('./pages/workflow/WorkflowListPage'));
const WorkflowDesignerPage = lazy(() => import('./pages/workflow/WorkflowDesignerPage'));
const WorkflowInstancePage = lazy(() => import('./pages/workflow/WorkflowInstancePage'));
const NotificationPreferencesPage = lazy(() => import('./pages/workflow/NotificationPreferencesPage'));

// Admin Pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const LicenseDashboard = lazy(() => import('./pages/admin/LicenseDashboard'));
const DataSeedingPage = lazy(() => import('./pages/admin/data-seeding/DataSeedingPage'));
const AIUsageDashboard = lazy(() => import('./pages/admin/AIUsageDashboard'));
const GSTUsageDashboard = lazy(() => import('./pages/admin/GSTUsageDashboard'));

// Investment Portfolio Pages
const PortfolioDashboard = lazy(() => import('./pages/investment/PortfolioDashboard'));

// SIM – Supplier Invoice Management Pages
const SIMDashboardPage = lazy(() => import('./pages/sim/SIMDashboardPage'));
const SIMInvoiceListPage = lazy(() => import('./pages/sim/SIMInvoiceListPage'));
const SIMInvoiceDetailPage = lazy(() => import('./pages/sim/SIMInvoiceDetailPage'));
const SIMApprovalInboxPage = lazy(() => import('./pages/sim/SIMApprovalInboxPage'));
const SIMExceptionPage = lazy(() => import('./pages/sim/SIMExceptionPage'));
const SIMApprovalRulesPage = lazy(() => import('./pages/sim/SIMApprovalRulesPage'));
const SIMInvoiceProcessPage = lazy(() => import('./pages/sim/process/SIMInvoiceProcessPage'));

// Visitor Management Pages
const VisitorDashboard = lazy(() => import('./pages/visitor-management/VisitorDashboard'));
const VisitorListPage = lazy(() => import('./pages/visitor-management/VisitorListPage'));
const VisitRequestListPage = lazy(() => import('./pages/visitor-management/VisitRequestListPage'));
const VisitRequestFormPage = lazy(() => import('./pages/visitor-management/VisitRequestFormPage'));
const CheckInPage = lazy(() => import('./pages/visitor-management/CheckInPage'));
const GatekeeperPage = lazy(() => import('./pages/visitor-management/GatekeeperPage'));
const VisitLogPage = lazy(() => import('./pages/visitor-management/VisitLogPage'));
const LocationsPage = lazy(() => import('./pages/visitor-management/LocationsPage'));
const BlacklistPage = lazy(() => import('./pages/visitor-management/BlacklistPage'));

// Stock Audit Pages
const StockAuditDashboard = lazy(() => import('./pages/stock-audit/StockAuditDashboard'));
const AuditDocumentListPage = lazy(() => import('./pages/stock-audit/AuditDocumentListPage'));
const AuditDocumentDetailPage = lazy(() => import('./pages/stock-audit/AuditDocumentDetailPage'));
const CountEntryPage = lazy(() => import('./pages/stock-audit/CountEntryPage'));

// Front Desk Pages
const FrontDeskDashboard = lazy(() => import('./pages/front-desk/FrontDeskDashboard'));
const EntryListPage = lazy(() => import('./pages/front-desk/EntryListPage'));
const EntryFormPage = lazy(() => import('./pages/front-desk/EntryFormPage'));
const EntryDetailPage = lazy(() => import('./pages/front-desk/EntryDetailPage'));
const MyInboxPage = lazy(() => import('./pages/front-desk/MyInboxPage'));
const DelegationSettingsPage = lazy(() => import('./pages/front-desk/DelegationSettingsPage'));
const PendingGoodsReceiptPage = lazy(() => import('./pages/front-desk/PendingGoodsReceiptPage'));

// Storefront Pages
const StorefrontHomePage = lazy(() => import('./pages/ecommerce/StorefrontHomePage'));
const ShopLoginPage = lazy(() => import('./pages/ecommerce/ShopLoginPage'));
const ProductListPage = lazy(() => import('./pages/ecommerce/ProductListPage'));
const ProductDetailPage = lazy(() => import('./pages/ecommerce/ProductDetailPage'));
const PolicyPage = lazy(() => import('./pages/ecommerce/PolicyPage'));
const CartPage = lazy(() => import('./pages/ecommerce/CartPage'));
const CheckoutPage = lazy(() => import('./pages/ecommerce/CheckoutPage'));
const OrderConfirmationPage = lazy(() => import('./pages/ecommerce/OrderConfirmationPage'));
const MyAccountPage = lazy(() => import('./pages/ecommerce/MyAccountPage'));
const MyOrdersPage = lazy(() => import('./pages/ecommerce/MyOrdersPage'));
const OrderDetailPage = lazy(() => import('./pages/ecommerce/OrderDetailPage'));
const MyAddressesPage = lazy(() => import('./pages/ecommerce/MyAddressesPage'));
const WishlistPage = lazy(() => import('./pages/ecommerce/WishlistPage'));
const ComparisonPage = lazy(() => import('./pages/ecommerce/ComparisonPage'));

// Customer Portal (E12) — its own shell and its own auth; deliberately outside
// ProtectedRoute/ModernMainLayout, which are built for ERP users.
const PortalLoginPage = lazy(() => import('./pages/portal/PortalLoginPage'));
const PortalActivatePage = lazy(() => import('./pages/portal/PortalActivatePage'));
const PortalDashboardPage = lazy(() => import('./pages/portal/PortalDashboardPage'));
const PortalOrdersPage = lazy(() => import('./pages/portal/PortalOrdersPage'));
const PortalOrderDetailPage = lazy(() => import('./pages/portal/PortalOrderDetailPage'));
const PortalShipmentsPage = lazy(() => import('./pages/portal/PortalShipmentsPage'));
const PortalFinancePage = lazy(() => import('./pages/portal/PortalFinancePage'));
const PortalDocumentsPage = lazy(() => import('./pages/portal/PortalDocumentsPage'));
const PortalAnalyticsPage = lazy(() => import('./pages/portal/PortalAnalyticsPage'));
const PortalSupportPage = lazy(() => import('./pages/portal/PortalSupportPage'));
const PortalChatPage = lazy(() => import('./pages/portal/PortalChatPage'));
const PortalNotificationsPage = lazy(() => import('./pages/portal/PortalNotificationsPage'));
const PortalProfilePage = lazy(() => import('./pages/portal/PortalProfilePage'));
const CustomerPortalAdminPage = lazy(() => import('./pages/admin/CustomerPortalAdminPage'));

const DisputeDashboard = lazy(() => import('./pages/dispute/DisputeDashboard'));
const DisputesList = lazy(() => import('./pages/dispute/DisputesList'));
const DisputeDetail = lazy(() => import('./pages/dispute/DisputeDetail'));
const DisputeForm = lazy(() => import('./pages/dispute/DisputeForm'));

// Modern Enterprise ERP theme - aligned with MasterCSS design tokens
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2C3E50', // Deep Slate
      light: '#3d5a73',
      dark: '#1A242F',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#3498db', // Accent / Cerulean
      light: '#5dade2',
      dark: '#2980b9',
      contrastText: '#ffffff',
    },
    error: {
      main: '#e74c3c',
      light: '#f1948a',
      dark: '#c0392b',
    },
    warning: {
      main: '#f1c40f',
      light: '#f4d03f',
      dark: '#d4ac0d',
    },
    info: {
      main: '#17a2b8',
      light: '#3fc1d3',
      dark: '#117a8b',
    },
    success: {
      main: '#2bad64',
      light: '#58d68d',
      dark: '#239b56',
    },
    background: {
      default: '#F5F7FA',
      paper: '#ffffff',
    },
    text: {
      primary: '#212529',
      secondary: '#6c757d',
    },
    divider: '#e9ecef',
  },
  typography: {
    fontFamily: [
      '"Inter"',
      '"Noto Sans Tamil"',
      '"Roboto"',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.3,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 1px 3px rgba(0, 0, 0, 0.05)',
    '0px 4px 6px rgba(0, 0, 0, 0.07)',
    '0px 5px 15px rgba(0, 0, 0, 0.08)',
    '0px 10px 24px rgba(0, 0, 0, 0.1)',
    '0px 15px 35px rgba(0, 0, 0, 0.12)',
    '0px 20px 40px rgba(0, 0, 0, 0.14)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
    '0px 25px 50px rgba(0, 0, 0, 0.16)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.12)',
          },
        },
        contained: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid #e9ecef',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        },
      },
    },
  },
});

// Decode a JWT and return its expiry timestamp (ms), or 0 if unreadable.
function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return (payload.exp || 0) * 1000;
  } catch {
    return 0;
  }
}

/**
 * Shown while a route's chunk is downloading. Every page below is code-split
 * (React.lazy), so the first paint no longer waits for the whole application.
 * Deliberately unstyled and layout-free: it renders for a few hundred
 * milliseconds and must not shift the page the chunk is about to draw.
 */
const RouteLoader = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      width: '100%',
    }}
  >
    <CircularProgress size={32} />
  </Box>
);

function App() {
  // App initialization: check token freshness, then refresh user permissions.
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedRefresh = localStorage.getItem('refreshToken');

    if (!token) return; // Not logged in — nothing to do.

    const now = Date.now();
    const accessExpired = getTokenExpiry(token) < now;
    // In cookie mode (SECURITY 2.1) the refresh token is an HttpOnly cookie we can't
    // read here, so a missing localStorage value does NOT mean "no refresh possible".
    // Only treat the session as definitively dead when a stored refresh token exists
    // AND is expired (legacy mode); otherwise let the refresh attempt hit the backend.
    const storedRefreshExpired = !!storedRefresh && getTokenExpiry(storedRefresh) < now;

    if (accessExpired && storedRefreshExpired) {
      // Both tokens stale — clear session and show login immediately (no API round-trip).
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      window.location.replace('/auth/login');
      return;
    }

    if (accessExpired) {
      // Access token expired — refresh silently (localStorage refresh or HttpOnly cookie).
      store.dispatch(refreshToken());
    } else {
      // Token still valid — re-fetch profile so permission changes are picked up.
      store.dispatch(fetchCurrentUser());
    }

    // Add global error handler
    window.addEventListener('error', (event) => {
      console.error('Global error caught:', event.error);
    });

    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection:', event.reason);
    });
  }, []);

  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Suspense fallback={<RouteLoader />}>
          <Routes>
            {/* PWA start_url legacy redirect: old manifest used /app/ as start_url */}
            <Route path="/app" element={<Navigate to="/" replace />} />
            <Route path="/app/" element={<Navigate to="/" replace />} />

            {/* Public vendor portal — token-authenticated, no app shell / no login */}
            <Route path="/vendor-portal/techpack/:token" element={<VendorPortalPage />} />
            {/* Referral slip kiosk (DEV-05) — device-credential authenticated, no login */}
            <Route path="/kiosk/referrals" element={<ReferralKioskPage />} />

            {/* Auth Routes - These routes don't use ModernMainLayout */}
            <Route path="/auth/login" element={<LoginPage />} />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/change-password" element={<ForceChangePasswordPage />} />
            <Route path="/auth/mfa" element={<MfaChallengePage />} />
            <Route path="/auth/mfa-setup" element={<MfaSetupPage />} />

            {/* Protected Routes - These routes use ModernMainLayout */}
            <Route path="/" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ModernDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Security settings (two-factor authentication) */}
            <Route path="/security" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SecuritySettingsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* MDM Route */}
            <Route path="/mdm" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MDMDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* CRM Routes */}
            <Route path="/crm" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CRMDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/crm/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Routes>
                    <Route path="leads" element={<LeadPageSimple />} />
                    <Route path="leads/:id" element={<LeadDetailPage />} />
                    <Route path="opportunities" element={<OpportunitiesList />} />
                    <Route path="opportunities/:id" element={<div>Opportunity Detail - Coming Soon</div>} />
                    <Route path="customers" element={<CustomerMasterPage />} />
                    <Route path="quotations" element={<QuotationsList />} />
                    <Route path="quotations/new" element={<QuotationEntry />} />
                    <Route path="quotations/:id/edit" element={<QuotationEntry />} />
                    <Route path="quotations/:id" element={<QuotationEntry />} />
                    <Route path="sales-dashboard" element={<SalesOrderDashboard />} />
                    <Route path="sales-orders" element={<SalesOrdersList />} />
                    <Route path="sales-orders/line-items" element={<SalesOrderLineItemsReport />} />
                    <Route path="order-book" element={<OrderBookPage />} />
                    <Route path="so-inventory" element={<SOInventoryReport />} />
                    <Route path="sales-orders/summary-photos" element={<SOSummaryPhotosPage />} />
                    <Route path="so-status" element={<SOStatusReport />} />
                    <Route path="so-status/detailed" element={<SOStatusLinesReport />} />
                    <Route path="so-status/journey" element={<SOStatusWorkbenchPage lens="journey" />} />
                    <Route path="so-status/reconciliation" element={<SOStatusWorkbenchPage lens="recon" />} />
                    <Route path="so-status/pnl" element={<SOStatusWorkbenchPage lens="pnl" />} />
                    <Route path="so-status/:id" element={<SOStatusReport />} />
                    <Route path="merchandising" element={<MerchandisingDashboard />} />
                    <Route path="merchandising/tna-templates" element={<TnATemplatesPage />} />
                    <Route path="merchandising/tna-setup" element={<TnASetupPage />} />
                    <Route path="merchandising/tna-adherence" element={<TnAAdherencePage />} />
                    <Route path="merchandising/pp-meetings" element={<PPMeetingsPage />} />
                    <Route path="merchandising/pp-meetings/:id" element={<PPMeetingDetailPage />} />
                    <Route path="order-pipeline" element={<OrderPipelinePage />} />
                    <Route path="order-pipeline/programme" element={<ProgrammePipelinePage />} />
                    <Route path="order-pipeline/setup" element={<PipelineConfigPage />} />
                    <Route path="order-closures" element={<OrderClosuresPage />} />
                    <Route path="order-closures/:id" element={<OrderClosureDetailPage />} />
                    <Route path="sales-orders/new" element={<SalesOrderEntry />} />
                    <Route path="sales-orders/:id" element={<SalesOrderEntry viewOnly />} />
                    <Route path="sales-orders/:id/edit" element={<SalesOrderEntry />} />
                    <Route path="delivery" element={<DeliveriesList />} />
                    <Route path="delivery/:id" element={<DeliveryDetailPage />} />
                    <Route path="invoices" element={<InvoicesList />} />
                    <Route path="invoices/:id" element={<InvoiceDetailPage />} />
                    <Route path="payments" element={<PaymentsList />} />
                    <Route path="payments/:id" element={<div>Payment Detail - Coming Soon</div>} />
                    <Route path="seasons" element={<SeasonPage />} />
                    <Route path="po-import" element={<CustomerPOImportPage />} />
                    <Route path="po-import/:id" element={<CustomerPOImportPage />} />
                    <Route path="returns" element={<SalesReturnsList />} />
                    <Route path="returns/:id" element={<SalesReturnDetail />} />
                    {/* Job Work (we as job worker) */}
                    <Route path="job-work-orders" element={<JobWorkOrdersList />} />
                    <Route path="job-work/inward-challans" element={<InwardChallansList />} />
                    <Route path="job-work/challan-ageing" element={<ChallanAgeing />} />
                  </Routes>
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Business Partner (Customer-as-Vendor) Routes */}
            <Route path="/partners/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Routes>
                    <Route path="business-partners" element={<BusinessPartnersList />} />
                    <Route path="business-partners/new" element={<PartnerForm />} />
                    <Route path="business-partners/:id" element={<PartnerDetail />} />
                    <Route path="detect-candidates" element={<DetectCandidates />} />
                    <Route path="profitability" element={<PartnerProfitability />} />
                  </Routes>
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* AR/AP Netting & Set-off Routes */}
            <Route path="/fi-netting/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Routes>
                    <Route path="setoff-runs" element={<SetoffRunsList />} />
                    <Route path="net-exposure" element={<NetExposureDashboard />} />
                    <Route path="withholding-matrix" element={<WithholdingMatrix />} />
                    <Route path="balance-transfer" element={<BalanceTransfer />} />
                  </Routes>
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Financial Routes */}
            <Route path="/financial" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <FinancialDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Accounting bento launcher — a static segment outranks the
                /financial/* splat below, so this wins the match. */}
            <Route path="/financial/menu" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AccountingBentoMenu />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/financial/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Routes>
                    <Route path="chart-of-accounts" element={<ChartOfAccountsPage />} />
                    <Route path="gl-accounts" element={<GLAccountsPage />} />
                    <Route path="journal-entries" element={<JournalEntriesPage />} />
                    <Route path="journal-entries/new" element={<JournalEntryFormPage />} />
                    <Route path="journal-entries/:id" element={<JournalEntryFormPage />} />
                    <Route path="asset-register" element={<AssetRegisterPage />} />
                    <Route path="companies" element={<CompanyMasterList />} />
                    <Route path="companies/new" element={<CompanyFormPage />} />
                    <Route path="companies/:id" element={<CompanyFormPage />} />
                    <Route path="fiscal-years" element={<FiscalYearPageSimple />} />
                    <Route path="payments" element={<div>Payments - Coming Soon</div>} />
                    <Route path="payments-received" element={<PaymentsReceivedPage />} />
                    <Route path="payments-received/new" element={<PaymentFormPage />} />
                    {/* Same form, opened already switched to "the invoice is
                        not known". Declared before `payments-received/:id` —
                        a static segment out-ranks the dynamic one either way,
                        but the order says so rather than relying on it. */}
                    <Route path="payments-received/on-account/new" element={<PaymentFormPage />} />
                    <Route path="payments-received/:id" element={<PaymentFormPage />} />
                    <Route path="payments-received/:id/edit" element={<PaymentFormPage />} />
                    {/* Where the receipt voucher's "contra account is not configured"
                        refusal points. Read gated on `fi_ar.view_gst_advance` by the
                        navigation registry; the write buttons on
                        `fi_ar.configure_gst_advance` inside the page. */}
                    <Route path="gst-advance-config" element={<GSTAdvanceConfigPage />} />
                    <Route path="gst-receipt-vouchers" element={<GSTReceiptVouchersPage />} />
                    <Route path="bank-book" element={<BankBookPage />} />
                    <Route path="bank-transactions" element={<BankTransactionsPage />} />
                    <Route path="bank-transactions/new" element={<BankTransactionFormPage />} />
                    <Route path="bank-transactions/:id" element={<BankTransactionFormPage />} />
                    {/* `new` must precede `:id` or the id route swallows it. */}
                    <Route path="payment-instruments" element={<PaymentInstrumentListPage />} />
                    <Route path="payment-instruments/new" element={<PaymentInstrumentFormPage />} />
                    <Route path="payment-instruments/:id" element={<PaymentInstrumentDetailPage />} />
                    <Route path="gl-open-items" element={<GLOpenItemsPage />} />
                    <Route path="gl-payments" element={<GLPaymentsPage />} />
                    <Route path="gl-payments/new" element={<GLPaymentFormPage />} />
                    <Route path="gl-payments/:id" element={<GLPaymentFormPage />} />
                  </Routes>
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Vendor Reconciliation Route */}
            <Route path="/vendor-reconciliation" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VendorReconciliation />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Vendor Invoice Routes */}
            <Route path="/fi-ap/vendor-invoices" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VendorInvoiceListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/fi-ap/vendor-invoices/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VendorInvoiceFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/fi-ap/vendor-invoices/new-gl" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <NonPOInvoiceFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/fi-ap/vendor-invoices/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VendorInvoiceFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/fi-ap/vendor-invoices/:id/edit" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VendorInvoiceFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/fi-ap/vendor-invoices/:id/edit-gl" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <NonPOInvoiceFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* AP Due Invoices worklist — entry point for paying vendors */}
            <Route path="/fi-ap/due-invoices" element={<ProtectedRoute><ModernMainLayout><DueInvoicesWorklist /></ModernMainLayout></ProtectedRoute>} />

            {/* AP Payment Runs */}
            <Route path="/fi-ap/payment-runs" element={<ProtectedRoute><ModernMainLayout><PaymentRunsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-ap/payment-runs/new" element={<Navigate to="/fi-ap/due-invoices" replace />} />
            <Route path="/fi-ap/payment-runs/:id" element={<ProtectedRoute><ModernMainLayout><PaymentRunDetailPage /></ModernMainLayout></ProtectedRoute>} />

            {/* AP Payments */}
            <Route path="/fi-ap/payments" element={<ProtectedRoute><ModernMainLayout><PaymentRegisterPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-ap/payments/:id" element={<ProtectedRoute><ModernMainLayout><PaymentDetailPage /></ModernMainLayout></ProtectedRoute>} />

            {/* Manual & Advance Payments */}
            <Route path="/fi-ap/manual-payment" element={<ProtectedRoute><ModernMainLayout><ManualPaymentPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-ap/advance-payment" element={<ProtectedRoute><ModernMainLayout><AdvancePaymentPage /></ModernMainLayout></ProtectedRoute>} />

            {/* Clearing invoices with advances / on-account / down payments.
                A reclassification, not a payment — see VendorAllocationPage. */}
            <Route path="/fi-ap/allocation" element={<ProtectedRoute><ModernMainLayout><VendorAllocationPage /></ModernMainLayout></ProtectedRoute>} />

            {/* House Bank master — every payment form picks from it */}
            <Route path="/fi-ap/house-banks" element={<ProtectedRoute><ModernMainLayout><HouseBankAccountsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-ap/posting-config" element={<ProtectedRoute><ModernMainLayout><APPostingConfigPage /></ModernMainLayout></ProtectedRoute>} />

            {/* Vendor Down Payments */}
            <Route path="/fi-ap/down-payments" element={<ProtectedRoute><ModernMainLayout><DownPaymentListPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-ap/down-payments/new" element={<ProtectedRoute><ModernMainLayout><DownPaymentFormPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-ap/down-payments/aging" element={<ProtectedRoute><ModernMainLayout><DownPaymentAgingPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-ap/down-payments/statement" element={<ProtectedRoute><ModernMainLayout><VendorAdvanceStatementPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-ap/down-payments/:id" element={<ProtectedRoute><ModernMainLayout><DownPaymentDetailPage /></ModernMainLayout></ProtectedRoute>} />

            {/* GST Reverse Charge (RCM) */}
            <Route path="/fi-ap/rcm" element={<ProtectedRoute><ModernMainLayout><RcmDashboardPage /></ModernMainLayout></ProtectedRoute>} />

            {/* Debit / Credit Notes (AP & AR) */}
            <Route path="/fi-notes" element={<ProtectedRoute><ModernMainLayout><AdjustmentNoteListPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-notes/new" element={<ProtectedRoute><ModernMainLayout><AdjustmentNoteFormPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-notes/:id" element={<ProtectedRoute><ModernMainLayout><AdjustmentNoteDetailPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-notes/:id/edit" element={<ProtectedRoute><ModernMainLayout><AdjustmentNoteFormPage /></ModernMainLayout></ProtectedRoute>} />

            {/* Free of Cost (FOC) Delivery */}
            <Route path="/foc" element={<ProtectedRoute><ModernMainLayout><FOCPage /></ModernMainLayout></ProtectedRoute>} />

            {/* Tenant & Customer Specific Print Formats */}
            <Route path="/print-forms" element={<ProtectedRoute><ModernMainLayout><PrintFormsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/settings/email-channels" element={<ProtectedRoute><ModernMainLayout><EmailChannelsPage /></ModernMainLayout></ProtectedRoute>} />

            {/* Configurable Document Numbering */}
            <Route path="/settings/document-numbering" element={<ProtectedRoute><ModernMainLayout><DocumentNumberingPage /></ModernMainLayout></ProtectedRoute>} />
            {/* Field Status: suppress / required / optional per screen field (fieldstatus.* on the API) */}
            <Route path="/settings/field-status" element={<ProtectedRoute><ModernMainLayout><FieldStatusPage /></ModernMainLayout></ProtectedRoute>} />
            {/* Administrator -> Report Field Labels: column captions for every screen's grids.
                Under /settings, not /administrator: Django's catch-all skips every path
                starting with "admin", so that would not survive a hard refresh in prd. */}
            <Route path="/settings/report-field-labels" element={<ProtectedRoute><ModernMainLayout><ReportFieldLabelsPage /></ModernMainLayout></ProtectedRoute>} />
            {/* Administrator - Asset Tag Printer: the tenant-wide sticker layout the
                Asset Register's Print Asset Tags uses. Saving is tenant.admin on the API. */}
            {/* Administrator - Change Management Settings. Under /settings for the same reason as above. */}
            <Route path="/settings/change-management" element={<ProtectedRoute><ModernMainLayout><ChangeManagementSettings /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/settings/asset-tag-printer" element={<ProtectedRoute><ModernMainLayout><AssetTagPrinterPage /></ModernMainLayout></ProtectedRoute>} />

            {/* Writing Assistant — spell/grammar checking for free-text fields */}
            <Route path="/settings/writing-assistant" element={<ProtectedRoute><ModernMainLayout><WritingAssistantSettingsPage /></ModernMainLayout></ProtectedRoute>} />

            {/* Process Instruction Sheet (PI Sheet) — specific routes before :id */}
            <Route path="/pi-sheet" element={<ProtectedRoute><ModernMainLayout><PISheetListPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/pi-sheet/recipes" element={<ProtectedRoute><ModernMainLayout><RecipeTemplatesPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/pi-sheet/shopfloor" element={<ProtectedRoute><ModernMainLayout><ShopfloorDataPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/pi-sheet/analytics" element={<ProtectedRoute><ModernMainLayout><ShopfloorAnalyticsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/pi-sheet/:id" element={<ProtectedRoute><ModernMainLayout><PISheetDetailPage /></ModernMainLayout></ProtectedRoute>} />

            {/* AP Reports */}
            <Route path="/fi-ap/reports/outstanding" element={<ProtectedRoute><ModernMainLayout><OutstandingPayablesPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/finance/reports/vendor-balances" element={<ProtectedRoute><ModernMainLayout><VendorBalanceListPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/finance/reports/customer-balances" element={<ProtectedRoute><ModernMainLayout><CustomerBalanceListPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-ap/reports/cash-forecast" element={<ProtectedRoute><ModernMainLayout><CashForecastPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-ap/reports/tds" element={<ProtectedRoute><ModernMainLayout><TdsReportPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-ap/reports/msme-outstanding" element={<ProtectedRoute><ModernMainLayout><MSMEOutstandingPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-ap/reports/gr-ir" element={<ProtectedRoute><ModernMainLayout><GrIrReportPage /></ModernMainLayout></ProtectedRoute>} />

            {/* Asset Accounting (FI-AA) */}
            <Route path="/fi-aa/assets" element={<ProtectedRoute><ModernMainLayout><AssetListPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/assets/new" element={<ProtectedRoute><ModernMainLayout><AssetFormPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/assets/:id" element={<ProtectedRoute><ModernMainLayout><AssetDetailPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/assets/:id/edit" element={<ProtectedRoute><ModernMainLayout><AssetFormPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/asset-classes" element={<ProtectedRoute><ModernMainLayout><AssetClassPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/depreciation-keys" element={<ProtectedRoute><ModernMainLayout><DepreciationKeyPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/depreciation-keys/new" element={<ProtectedRoute><ModernMainLayout><DepreciationKeyFormPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/depreciation-keys/:id/edit" element={<ProtectedRoute><ModernMainLayout><DepreciationKeyFormPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/acquisitions" element={<ProtectedRoute><ModernMainLayout><AcquisitionPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/acquisitions/new" element={<ProtectedRoute><ModernMainLayout><AcquisitionFormPage /></ModernMainLayout></ProtectedRoute>} />
            {/* Asset procurement. `/new` is declared BEFORE `/:id` — the route
                matcher would otherwise resolve "new" as an id and try to load
                asset receipt number "new". */}
            <Route path="/fi-aa/asset-receipts" element={<ProtectedRoute><ModernMainLayout><AssetReceiptPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/asset-receipts/new" element={<ProtectedRoute><ModernMainLayout><AssetReceiptFormPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/asset-receipts/:id" element={<ProtectedRoute><ModernMainLayout><AssetReceiptFormPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/capex-on-order" element={<ProtectedRoute><ModernMainLayout><CapexOnOrderPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/depreciation-runs" element={<ProtectedRoute><ModernMainLayout><DepreciationRunPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/opening-balances" element={<ProtectedRoute><ModernMainLayout><AssetTakeoverPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/income-tax-depreciation" element={<ProtectedRoute><ModernMainLayout><IncomeTaxDepreciationPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/transfers" element={<ProtectedRoute><ModernMainLayout><AssetTransferPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/transfers/new" element={<ProtectedRoute><ModernMainLayout><AssetTransferFormPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/transfers/:id" element={<ProtectedRoute><ModernMainLayout><AssetTransferDetailPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/transfers/:id/edit" element={<ProtectedRoute><ModernMainLayout><AssetTransferFormPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/disposals" element={<ProtectedRoute><ModernMainLayout><AssetDisposalPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/disposals/new" element={<ProtectedRoute><ModernMainLayout><AssetDisposalFormPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/revaluations" element={<ProtectedRoute><ModernMainLayout><AssetRevaluationPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/revaluations/new" element={<ProtectedRoute><ModernMainLayout><AssetRevaluationFormPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/reports" element={<ProtectedRoute><ModernMainLayout><AssetReportsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/fi-aa/asset-schedule" element={<ProtectedRoute><ModernMainLayout><AssetSchedulePage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/financial/tds-tcs" element={<ProtectedRoute><ModernMainLayout><TdsTcsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/financial/gst-recon" element={<ProtectedRoute><ModernMainLayout><GstReconPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/financial/related-parties" element={<ProtectedRoute><ModernMainLayout><RelatedPartiesPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/financial/consolidation" element={<ProtectedRoute><ModernMainLayout><ConsolidationWorkspace /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/financial/xbrl" element={<ProtectedRoute><ModernMainLayout><XbrlPage /></ModernMainLayout></ProtectedRoute>} />

            {/* GST Refund Tracking */}
            <Route path="/fi-gst/refund" element={<ProtectedRoute><ModernMainLayout><GSTRefundPendingPage /></ModernMainLayout></ProtectedRoute>} />

            {/* Budget Control System (BCS) */}
            <Route path="/bcs/budget-types" element={<ProtectedRoute><ModernMainLayout><BudgetTypesPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/bcs/budget-documents" element={<ProtectedRoute><ModernMainLayout><BudgetDocumentsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/bcs/budget-documents/new" element={<ProtectedRoute><ModernMainLayout><BudgetDocumentForm /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/bcs/budget-documents/transfer/new" element={<ProtectedRoute><ModernMainLayout><BudgetTransferForm /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/bcs/budget-documents/:id" element={<ProtectedRoute><ModernMainLayout><BudgetDocumentForm /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/bcs/cost-center-monthly" element={<ProtectedRoute><ModernMainLayout><CostCenterMonthlyPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/bcs/project-carry-forward" element={<ProtectedRoute><ModernMainLayout><ProjectCarryForwardPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/bcs/reports" element={<ProtectedRoute><ModernMainLayout><BudgetReportsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/bcs/pivot" element={<ProtectedRoute><ModernMainLayout><BudgetPivotView /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/bcs/dashboard" element={<ProtectedRoute><ModernMainLayout><BudgetDashboardPage /></ModernMainLayout></ProtectedRoute>} />

            {/* User Defaults / Parameter IDs (SAP-style) */}
            <Route path="/user-defaults/definitions" element={<ProtectedRoute><ModernMainLayout><ParameterDefinitionsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/user-defaults/my" element={<ProtectedRoute><ModernMainLayout><MyDefaultsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/newsletter-subscription" element={<ProtectedRoute><ModernMainLayout><NewsletterSubscriptionPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/user-defaults/role" element={<ProtectedRoute><ModernMainLayout><RoleDefaultsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/user-defaults/department" element={<ProtectedRoute><ModernMainLayout><DepartmentDefaultsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/user-defaults/resolution" element={<ProtectedRoute><ModernMainLayout><ResolutionViewerPage /></ModernMainLayout></ProtectedRoute>} />

            {/* GL Account Ledger Route */}
            <Route path="/gl-ledger" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <GLLedgerPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Vendor Ledger Route */}
            <Route path="/vendor-ledger" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VendorLedgerPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Vendor Open Items — uncleared payables sub-ledger, server-paged */}
            <Route path="/fi-ap/open-items" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VendorOpenItemsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Customer Ledger Route */}
            <Route path="/customer-ledger" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CustomerLedgerPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Direct Customer Invoices Routes */}
            <Route path="/fi-ar/direct-invoices" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DirectInvoiceListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/fi-ar/direct-invoices/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DirectInvoiceFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/fi-ar/direct-invoices/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DirectInvoiceFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Unpaid Invoices Route */}
            <Route path="/unpaid-invoices" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <UnpaidInvoicesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Payment Allocation Route */}
            <Route path="/payment-allocation" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PaymentAllocationPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Receivables Dashboard Route */}
            <Route path="/receivables-dashboard" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ReceivablesDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Payables Dashboard Route */}
            <Route path="/payables-dashboard" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PayablesDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Forex Routes */}
            <Route path="/forex/exchange-rates" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ExchangeRatePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* EXIM Routes */}
            <Route path="/exim/export-benefits-dashboard" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ExportBenefitsDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/exim/import-consignments" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ImportConsignmentListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/exim/import-consignments/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ImportConsignmentFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/exim/import-consignments/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ImportConsignmentFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/exim/authorisations" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AuthorisationListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/exim/authorisations/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AuthorisationFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/exim/authorisations/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AuthorisationFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/exim/export-obligations" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ExportObligationPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/exim/duty-setup" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EximDutySetupPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/exim/customs-invoices" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CustomsInvoiceListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Customer Reconciliation Route */}
            <Route path="/customer-reconciliation" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CustomerReconciliation />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Dunning Letters Route */}
            <Route path="/dunning-letters" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DunningPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Bank Reconciliation Route */}
            <Route path="/bank-reconciliation" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <BankReconciliation />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Materials Management Routes */}
            <Route path="/materials" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MaterialsDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/materials/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Routes>
                    <Route path="material-master" element={<MaterialMasterList />} />
                    <Route path="materials/:id" element={<MaterialFormPage />} />
                    <Route path="service-master" element={<ServiceMasterList />} />
                    <Route path="services/:id" element={<ServiceFormPage />} />
                    <Route path="material-variants" element={<MaterialVariantsPage />} />
                    <Route path="variant-attributes" element={<VariantAttributesPage />} />
                    <Route path="product-specifications" element={<ProductSpecificationsDocumentsList />} />
                    <Route path="inventory" element={<InventoryPage />} />
                    <Route path="inventory-dashboard" element={<InventoryDashboardPage />} />
                    <Route path="stock-abstract" element={<StockAbstractPage />} />
                    <Route path="goods-receipt" element={<GoodsReceiptPage />} />
                    <Route path="gr-capture" element={<GRCaptureListPage />} />
                    <Route path="gr-capture/process" element={<GRCaptureProcessPage />} />
                    <Route path="gr-capture/process/:id" element={<GRCaptureProcessPage />} />
                    <Route path="purchase-orders" element={<PurchaseOrderPage />} />
                    <Route path="purchase-orders/line-items" element={<PurchaseOrderLineItemsReport />} />
                    <Route path="handling-units" element={<HandlingUnitPage />} />
                    <Route path="goods-issue-production" element={<GoodsIssueProductionPage />} />
                    <Route path="goods-issue-sales-order" element={<GoodsIssueSalesOrderPage />} />
                    <Route path="goods-issue-cost-center" element={<GoodsIssueCostCenterPage />} />
                    <Route path="goods-receipt-production" element={<GoodsReceiptProductionPage />} />
                    <Route path="transfer" element={<TransferPage />} />
                    <Route path="material-documents" element={<MaterialDocumentListPage />} />
                    <Route path="material-documents/line-items" element={<MaterialDocumentLineItemsReport />} />
                    <Route path="material-batches" element={<MaterialBatchesPage />} />
                    <Route path="material-ledger" element={<MaterialLedgerPage />} />
                    <Route path="opening-inventory" element={<OpeningInventoryImportPage />} />
                    <Route path="opening-inventory/:id" element={<OpeningInventoryImportPage />} />
                    <Route path="posting-setup" element={<PostingSetupPage />} />
                    <Route path="service-accounting" element={<ServiceAccountingPage />} />
                    <Route path="rejections" element={<RejectionsPage />} />
                    <Route path="variant-reclassification" element={<VariantReclassificationPage />} />
                    <Route path="supplier-material-references" element={<SupplierMaterialReferencesPage />} />
                    <Route path="variant-value-maps" element={<VariantValueMapsPage />} />
                    <Route path="weight-adjustments" element={<WeightAdjustmentListPage />} />
                    <Route path="weight-adjustments/new" element={<WeightAdjustmentFormPage />} />
                    <Route path="weight-adjustments/:id" element={<WeightAdjustmentFormPage />} />
                    <Route path="vendor-stock" element={<VendorStockPage />} />
                    <Route path="customer-owned-stock" element={<CustomerOwnedStock />} />
                  </Routes>
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Master Data Routes (Warehouses / Storage Locations / Bins) */}
            <Route path="/master-data/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Routes>
                    <Route index element={<MasterDataPage />} />
                    <Route path=":section" element={<MasterDataPage />} />
                  </Routes>
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* SRM / Procurement Routes */}
            <Route path="/srm/rfq" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RFQListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/rfq/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RFQFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/rfq/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RFQDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/rfq/:id/edit" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RFQFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* RFI Routes */}
            <Route path="/srm/rfi" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RFIListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/rfi/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RFIFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/rfi/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RFIDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/rfi/:id/edit" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RFIFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Evaluation Routes */}
            <Route path="/srm/evaluation-templates" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EvaluationTemplateListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/evaluation-templates/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EvaluationTemplateFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/evaluation-templates/:id/edit" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EvaluationTemplateFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/evaluations" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EvaluationListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/evaluations/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EvaluationWorkspacePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Production Planning Routes */}
            <Route path="/production" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ProductionDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/production/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Routes>
                    {/* Master Data Routes - Phase 1 */}
                    <Route path="plants" element={<PlantList />} />
                    <Route path="plants/new" element={<PlantForm />} />
                    <Route path="plants/:id/edit" element={<PlantForm />} />

                    <Route path="work-centers" element={<WorkCenterList />} />
                    <Route path="work-centers/new" element={<WorkCenterForm />} />
                    <Route path="work-centers/:id/edit" element={<WorkCenterForm />} />

                    <Route path="machines" element={<MachineList />} />
                    <Route path="machines/new" element={<MachineForm />} />
                    <Route path="machines/:id/edit" element={<MachineForm />} />

                    <Route path="operation-standard-texts" element={<OperationStandardTextList />} />
                    <Route path="operation-standard-texts/new" element={<OperationStandardTextForm />} />
                    <Route path="operation-standard-texts/:id/edit" element={<OperationStandardTextForm />} />

                    <Route path="shift-calendars" element={<ShiftCalendarList />} />
                    <Route path="shift-calendars/new" element={<ShiftCalendarForm />} />
                    <Route path="shift-calendars/:id/edit" element={<ShiftCalendarForm />} />

                    <Route path="holiday-types" element={<HolidayTypeList />} />
                    <Route path="holiday-types/new" element={<HolidayTypeForm />} />
                    <Route path="holiday-types/:id/edit" element={<HolidayTypeForm />} />

                    <Route path="holiday-calendars" element={<HolidayCalendarList />} />
                    <Route path="holiday-calendars/new" element={<HolidayCalendarForm />} />
                    <Route path="holiday-calendars/:id/edit" element={<HolidayCalendarForm />} />

                    <Route path="holidays" element={<HolidayList />} />
                    <Route path="holidays/new" element={<HolidayForm />} />
                    <Route path="holidays/:id/edit" element={<HolidayForm />} />

                    <Route path="capacity-profiles" element={<CapacityProfileList />} />
                    <Route path="capacity-profiles/new" element={<CapacityProfileForm />} />
                    <Route path="capacity-profiles/:id/edit" element={<CapacityProfileForm />} />

                    {/* Phase 2: BOM Management */}
                    <Route path="component-allocation" element={<ComponentAllocationPage />} />
                    <Route path="bom" element={<BOMList />} />
                    <Route path="bom/:id" element={<BOMDetail />} />
                    <Route path="bom/:id/variants" element={<BOMVariantMatrix />} />
                    <Route path="bom/new" element={<BOMForm />} />
                    <Route path="bom/:id/edit" element={<BOMForm />} />
                    {/*
                      Engineering change management. `change-orders/new` MUST
                      precede `change-orders/:id`, or the id route swallows it
                      and "new" is looked up as a change order.
                    */}
                    <Route path="change-requests" element={<ChangeRequestList />} />
                    <Route path="change-orders" element={<ChangeOrderList />} />
                    <Route path="change-orders/new" element={<ChangeOrderForm />} />
                    <Route path="change-orders/settings" element={<Navigate to="/settings/change-management" replace />} />
                    <Route path="change-orders/:id/edit" element={<ChangeOrderForm />} />
                    <Route path="change-orders/:id" element={<ChangeOrderDetail />} />

                    {/* BOM Studio — unified variant-BOM workspace (supersedes
                        the classic BOM detail + separate SO BOM pages) */}
                    <Route path="bom-studio" element={<BomStudioPage />} />
                    <Route path="bom-studio/:id" element={<BomStudioPage />} />

                    {/* BOM Beta — isolated clone of BOM Studio, under active UI
                        redesign (30-colour x 5-size styles, pre-packs). Shares the
                        backend; touches no bom-studio file. See
                        docs/ai_driven_bom_creation.md */}
                    <Route path="bom-beta" element={<BomBetaPage />} />
                    <Route path="bom-beta/:id" element={<BomBetaPage />} />

                    {/* Pre-packs — the sellable identity of a pack recipe
                        (docs/ai_driven_bom_creation.md Phase 2) */}
                    <Route path="pre-packs" element={<PrePackList />} />
                    <Route path="pre-packs/new" element={<PrePackForm />} />
                    <Route path="pre-packs/:id" element={<PrePackForm />} />

                    {/* Sales Order BOM */}
                    <Route path="so-bom" element={<SOBOMList />} />
                    <Route path="so-bom/:id" element={<SOBOMDetail />} />

                    {/* Phase 2: Routing Management */}
                    <Route path="routing" element={<RoutingList />} />
                    <Route path="routing/:id" element={<RoutingDetail />} />
                    <Route path="routing/new" element={<RoutingForm />} />
                    <Route path="routing/:id/edit" element={<RoutingForm />} />

                    {/* Phase 3: Production Orders */}
                    <Route path="orders" element={<ProductionOrdersList />} />
                    <Route path="orders/new" element={<ProductionOrderForm />} />
                    <Route path="orders/:id" element={<ProductionOrderDetail />} />
                    <Route path="orders/:id/edit" element={<ProductionOrderForm />} />
                    <Route path="orders/:id/costs" element={<ProductionOrderCostPage />} />

                    {/* Phase 4: MRP */}
                    <Route path="mrp/runs" element={<MRPRunsList />} />
                    <Route path="mrp/runs/:id" element={<MRPRunDetail />} />
                    <Route path="mrp/planned-orders" element={<PlannedOrdersList />} />
                    <Route path="mrp/so-reservations" element={<SOReservationLedger />} />

                    {/* Phase 4: Scheduling & Capacity Planning */}
                    <Route path="scheduling" element={<SchedulingDashboard />} />
                    {/* The scheduling dashboard has linked here since it was
                        written; until now no such route existed and the click
                        fell through to the SPA catchall. */}
                    <Route path="scheduling/gantt" element={<GanttChartPage />} />
                    {/* The finite-capacity planning board (PP Phase 2). The RUN
                        ID is in the URL so a plan pastes into a ticket and Back
                        closes it. `board` must precede nothing else under
                        `scheduling/` that could swallow it. */}
                    <Route path="scheduling/board" element={<PlanningBoardPage />} />
                    <Route path="scheduling/board/:runId" element={<PlanningBoardPage />} />
                    <Route path="capacity-planning" element={<CapacityPlanningPage />} />
                    <Route path="capacity/crp-report" element={<CRPReportPage />} />
                    {/* Scheduling capability master data. Declared per work
                        centre, not per machine — see capability_models.py. */}
                    <Route path="capability" element={<CapabilityWorkspace />} />

                    {/* Phase 5: Shop Floor Execution */}
                    <Route path="shop-floor" element={<WorkOrderList />} />
                    <Route path="shop-floor/:id" element={<OperationExecutionPage />} />
                    <Route path="confirmation" element={<ProductionConfirmation />} />

                    {/* Phase 6: Reports */}
                    <Route path="reports/missing-parts" element={<MissingPartsPage />} />
                    <Route path="cost/wip-ledger" element={<WIPSubledgerPage />} />

                    {/* Legacy/deprecated routes */}
                    <Route path="work-orders" element={<Navigate to="/production/orders" replace />} />
                  </Routes>
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Plant Maintenance Routes */}
            <Route path="/pm" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PMDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/pm/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Routes>
                    <Route path="dashboard" element={<PMDashboard />} />
                    <Route path="maintenance-plans" element={<MaintenancePlansList />} />
                    <Route path="maintenance-plans/new" element={<MaintenancePlanForm />} />
                    <Route path="maintenance-plans/:id" element={<MaintenancePlanForm />} />
                    <Route path="maintenance-plans/:id/edit" element={<MaintenancePlanForm />} />
                    <Route path="maintenance-schedules" element={<MaintenanceSchedulesList />} />
                    <Route path="maintenance-schedules/new" element={<MaintenanceScheduleForm />} />
                    <Route path="maintenance-schedules/:id" element={<MaintenanceScheduleForm />} />
                    <Route path="maintenance-schedules/:id/edit" element={<MaintenanceScheduleForm />} />
                    <Route path="breakdown-reports" element={<BreakdownReportsList />} />
                    <Route path="breakdown-reports/new" element={<BreakdownReportForm />} />
                    <Route path="breakdown-reports/:id" element={<BreakdownReportForm />} />
                    <Route path="breakdown-reports/:id/edit" element={<BreakdownReportForm />} />
                    <Route path="work-orders" element={<EmergencyWorkOrdersList />} />
                    <Route path="work-orders/new" element={<EmergencyWorkOrderForm />} />
                    <Route path="work-orders/:id" element={<EmergencyWorkOrderForm />} />
                    <Route path="work-orders/:id/edit" element={<EmergencyWorkOrderForm />} />
                    <Route path="external-service" element={<ExternalServiceOrdersList />} />
                    <Route path="returnable-register" element={<ReturnableAssetsRegister />} />
                    {/* Shop-floor breakdown flow (mobile): supervisor scan, manager assigns, engineer accepts */}
                    <Route path="breakdown-mobile" element={<BreakdownMobilePage />} />
                    <Route path="machine-qr-labels" element={<MachineQrLabels />} />
                    <Route path="equipment-health" element={<EquipmentHealthList />} />
                    <Route path="equipment-health/new" element={<EquipmentHealthForm />} />
                    <Route path="equipment-health/:id" element={<EquipmentHealthForm />} />
                    <Route path="equipment-health/:id/edit" element={<EquipmentHealthForm />} />
                    <Route path="equipment-specifications" element={<EquipmentSpecificationsList />} />
                    <Route path="equipment-specifications/new" element={<EquipmentSpecificationForm />} />
                    <Route path="equipment-specifications/:id/edit" element={<EquipmentSpecificationForm />} />
                    <Route path="equipment-parameters" element={<EquipmentOperatingParametersList />} />
                    <Route path="equipment-parameters/new" element={<EquipmentOperatingParameterForm />} />
                    <Route path="equipment-parameters/:id/edit" element={<EquipmentOperatingParameterForm />} />
                    <Route path="condition-readings" element={<EquipmentConditionReadingsList />} />
                    <Route path="condition-readings/new" element={<EquipmentConditionReadingForm />} />
                    <Route path="condition-readings/:id/edit" element={<EquipmentConditionReadingForm />} />
                    <Route path="maintenance-tasks" element={<MaintenanceTasksList />} />
                    <Route path="maintenance-tasks/new" element={<MaintenanceTaskForm />} />
                    <Route path="maintenance-tasks/:id/edit" element={<MaintenanceTaskForm />} />
                    <Route path="task-executions" element={<MaintenanceTaskExecutionsList />} />
                    <Route path="task-executions/new" element={<MaintenanceTaskExecutionForm />} />
                    <Route path="task-executions/:id/edit" element={<MaintenanceTaskExecutionForm />} />
                    <Route path="maintenance-resources" element={<MaintenanceResourcesList />} />
                    <Route path="maintenance-resources/create" element={<MaintenanceResourceForm />} />
                    <Route path="maintenance-resources/:id/edit" element={<MaintenanceResourceForm />} />
                    <Route path="work-order-labor" element={<WorkOrderLaborList />} />
                    <Route path="work-order-labor/new" element={<WorkOrderLaborForm />} />
                    <Route path="work-order-labor/:id/edit" element={<WorkOrderLaborForm />} />
                    <Route path="work-order-parts" element={<WorkOrderPartsList />} />
                    <Route path="work-order-parts/new" element={<WorkOrderPartForm />} />
                    <Route path="work-order-parts/:id/edit" element={<WorkOrderPartForm />} />
                    <Route path="defect-analyses" element={<DefectAnalysesList />} />
                    <Route path="defect-analyses/new" element={<DefectAnalysisForm />} />
                    <Route path="defect-analyses/:id/edit" element={<DefectAnalysisForm />} />
                    <Route path="failure-history" element={<FailureHistoryList />} />
                    <Route path="failure-history/new" element={<FailureHistoryForm />} />
                    <Route path="failure-history/:id" element={<FailureHistoryForm />} />
                    <Route path="failure-history/:id/edit" element={<FailureHistoryForm />} />
                    <Route path="predictive-models" element={<PredictiveModelsList />} />
                    <Route path="predictive-models/new" element={<PredictiveModelForm />} />
                    <Route path="predictive-models/:id/edit" element={<PredictiveModelForm />} />
                    <Route path="failure-predictions" element={<FailurePredictionsList />} />
                    <Route path="failure-predictions/create" element={<FailurePredictionForm />} />
                    <Route path="failure-predictions/:id/edit" element={<FailurePredictionForm />} />
                    <Route path="anomaly-detections" element={<AnomalyDetectionsList />} />
                    <Route path="anomaly-detections/create" element={<AnomalyDetectionForm />} />
                    <Route path="anomaly-detections/:id" element={<AnomalyDetectionForm />} />
                    <Route path="anomaly-detections/:id/edit" element={<AnomalyDetectionForm />} />
                  </Routes>
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Cutting Room Routes */}
            <Route path="/cutting/dashboard" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CuttingRoomDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/markers" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MarkerListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/markers/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MarkerFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/markers/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MarkerFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/lay-slips" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <LaySlipListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/lay-slips/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <LaySlipFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/lay-slips/:id/edit" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <LaySlipFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/lay-slips/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <LaySlipDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/jobs" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CuttingJobListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/jobs/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CuttingJobDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/cutting-notes" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CuttingNoteListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/cutting-notes/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CuttingNoteFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/cutting-notes/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CuttingNoteFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/down-size" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DownSizeListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/down-size/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DownSizeFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/down-size/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DownSizeFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/bundle-labels" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <BundleLabelsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/qc" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QCInspectionPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/master-bags" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MasterBagListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/master-bags/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MasterBagDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/master-bags/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MasterBagDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cutting/reports" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ReportsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* The rates behind the lay-wise waste breakdown. The master
                itself lives in apps/inventory_management and keeps its one
                write door there; this is the screen for it. */}
            <Route path="/cutting/waste-types" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <WasteTypesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Tasks Management Routes */}
            <Route path="/tasks" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <TasksDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/tasks/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Routes>
                    <Route path="projects" element={<ProjectsPage />} />
                    <Route path="chat" element={<ProjectChatMobilePage />} />
                    <Route path="dashboard" element={<TasksDashboard />} />
                    {/* Declared BEFORE `:id` — that param route would otherwise
                        match "excel" and try to open a task with that id. */}
                    <Route path="excel" element={<TasksExcelPage />} />
                    {/* Same reason as "excel": declared before `:id`. */}
                    <Route path="report" element={<TasksReportPage />} />
                    {/* Same reason as "excel": declared before `:id`. */}
                    <Route path="upload" element={<TasksUploadPage />} />
                    <Route path="create-unplanned" element={<UnplannedTasksEntry />} />
                    <Route path="cost-report" element={<TaskCostReportPage />} />
                    {/* Moved: Report Field Labels now covers every menu. */}
                    <Route path="report-labels" element={<Navigate to="/settings/report-field-labels" replace />} />
                    {/* Declared before `:id`, like "excel". Not "/tasks/projects/:id":
                        Django serves that path (the admin project page), so a hard
                        refresh would never reach React. */}
                    <Route path="replanning" element={<TasksReplanningPage />} />
                    <Route path="replanning/:projectId" element={<TasksReplanningPage />} />
                    <Route path="transfer-employee" element={<TransferEmployeePage />} />
                    <Route path="templates" element={<TaskTemplatesPage />} />
                    <Route path="definitions" element={<TaskDefinitionsPage />} />
                    <Route path="definition-dependencies" element={<TaskDefinitionDependenciesPage />} />
                    <Route path="capacity" element={<CapacityDashboardPage />} />
                    <Route path=":id" element={<TaskDetailPage />} />
                    <Route path=":id/edit" element={<TaskEditPage />} />
                  </Routes>
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Direct Messaging */}
            <Route path="/messages" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DirectMessagesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Data Migration Workbench (Excel upload wizard) */}
            <Route path="/data-migration/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Routes>
                    {/* Templates first — a literal segment must be declared
                        before the ":id" pattern that would also match it. */}
                    <Route path="templates" element={<VariantListPage />} />
                    <Route path="templates/new" element={<VariantEditorPage />} />
                    <Route path="templates/:id" element={<VariantEditorPage />} />
                    <Route path="" element={<DataMigrationPage />} />
                    <Route path="new" element={<DataMigrationPage />} />
                    <Route path=":id" element={<DataMigrationPage />} />
                  </Routes>
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* POS Routes */}
            <Route path="/pos" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <POSDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/pos/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Routes>
                    <Route path="stores-dashboard" element={<StoresDashboard />} />
                    <Route path="terminal" element={<POSTerminal />} />
                    <Route path="sales-points" element={<SalesPointsList />} />
                    <Route path="sales-points/new" element={<SalesPointForm />} />
                    <Route path="sales-points/:id" element={<SalesPointForm />} />
                    <Route path="sales-points/:id/edit" element={<SalesPointForm />} />
                    <Route path="sales-point-pricing" element={<SalesPointPricingPage />} />
                    <Route path="price-list-upload" element={<PriceListImportPage />} />
                    <Route path="price-list-upload/:id" element={<PriceListImportPage />} />
                    <Route path="cash-sales" element={<CashSalesList />} />
                    <Route path="cash-sales/new" element={<CashSaleForm />} />
                    <Route path="cash-sales/:id" element={<CashSaleDetail />} />
                    <Route path="shifts" element={<ShiftsList />} />
                    <Route path="shifts/open" element={<ShiftForm />} />
                    <Route path="shifts/:id" element={<ShiftForm />} />
                    <Route path="settlements" element={<SettlementsList />} />
                    <Route path="settlements/new" element={<SettlementForm />} />
                    <Route path="settlements/:id" element={<SettlementForm />} />
                    <Route path="returns" element={<ReturnsList />} />
                    <Route path="returns/new" element={<ReturnForm />} />
                    <Route path="returns/:id" element={<ReturnForm />} />
                  </Routes>
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Search Routes */}
            <Route path="/search" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SearchPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Chat Routes */}
            <Route path="/chat" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AskBiztimizePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ask-biztimize" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AskBiztimizePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* HRM - Organisation & Staffing Routes */}
            <Route path="/org-structure" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <OrgStructurePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* HR Dashboard — read-only analytics across the HR apps */}
            <Route path="/hrm/dashboard" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <HRDashboardPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* HRM - Employee Master Routes */}
            <Route path="/hrm/employees" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EmployeeMasterPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/id-cards" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <IdCardPrintPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* "/new" before "/:id" — v6 ranks the static segment first anyway. */}
            <Route path="/hrm/employees/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EmployeeFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/employees/:id/edit" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EmployeeFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/employees/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EmployeeProfilePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* HRM - Separation & Exit (G1). NOTE the ordering: "/new" must
                come BEFORE "/:id" or it would be swallowed by the id route. */}
            <Route path="/hrm/separations" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SeparationListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/separations/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SeparationFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/separations/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SeparationDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Shift Rotation & Rostering. Every page is gated by its own
                codename in the sidebar; the API gates each action again, and
                publishing takes `hrm_tm.publish_roster` rather than the
                change codename — publishing is the compliance act. */}
            <Route path="/hrm/time/shifts" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ShiftMasterPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/time/patterns" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ShiftPatternsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/time/crews" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ShiftCrewsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/time/roster" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RosterWorkspacePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/time/coverage" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CoveragePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/time/compliance" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ComplianceRegisterPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/time/attendance" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AttendanceRegisterPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* HR records an absence on behalf of an employee. Not an ESS
                screen: the employee whose absence this is may have no login
                at all, which is precisely why the page exists. */}
            <Route path="/hrm/time/absences" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AbsenceEntryPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Short permission — late-in, early-out, gate pass. The same
                `EmployeeAbsence` record as the register above, denominated in
                HOURS against a rostered shift. A separate page because the
                register's day columns say nothing useful about a two-hour
                window, and because the excused/unexcused reports live here. */}
            <Route path="/hrm/time/permissions" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PermissionRegisterPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/time/swaps" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ShiftSwapsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/time/devices" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AttendanceDevicesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* The on-premise agents, one layer out from the terminals: a
                Windows service inside the customer's network. Registering one
                mints a credential that can write attendance for the whole
                tenant, which is why it has its own permissions rather than
                riding on the terminal page's. */}
            <Route path="/hrm/time/integrations" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AttendanceIntegrationsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Employee work schedules (IT0007) + the default ladder + the
                `backfill_work_schedules` command on screen. API under
                `/hrm/api/work-schedule*` — already proxied. */}
            <Route path="/hrm/time/work-schedules" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <WorkSchedulesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* ESS. Scoped to the caller's own employee record ON THE SERVER —
                neither page sends an employee id. */}
            <Route path="/ess/roster" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MyRosterPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ess/attendance" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MyAttendancePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Holiday calendar — read-only, scoped to the caller's own plant
                on the server. */}
            <Route path="/ess/holidays" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EssHolidaysPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Recruitment (G3). NOTE the ordering: "/pipeline" is a static
                sub-path under ":id" and must be declared with it, and any
                "/new" route must precede "/:id" or the id route swallows it. */}
            <Route path="/recruitment/requisitions" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RequisitionListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/recruitment/requisitions/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RequisitionFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/recruitment/requisitions/:id/pipeline" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ApplicationPipelinePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/recruitment/requisitions/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RequisitionFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/recruitment/candidates" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CandidateListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/recruitment/candidates/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CandidateFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* AI resume capture. NOTE the ordering — "/import" and
                "/import/:importId" must both precede "/:id" or the id route
                swallows them and renders the candidate form on "import". */}
            <Route path="/recruitment/candidates/import" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ResumeImportPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/recruitment/candidates/import/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ResumeImportPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/recruitment/candidates/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CandidateFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/recruitment/offers" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <OfferListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Employee Relations (DEV-10). "/new" precedes "/:id" or the id
                route swallows it. */}
            <Route path="/employee-relations" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ERCaseListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/employee-relations/cases" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ERCaseListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/employee-relations/cases/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ERCaseFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/employee-relations/cases/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ERCaseDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/employee-relations/complaints" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ERComplaintListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/employee-relations/complaints/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ERComplaintDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/employee-relations/setup" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ERSetupPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/employee-relations/reports" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ERReportsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ess/notices" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EssMyNoticesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ess/complaints" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EssMyComplaintsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Objects on Loan (DEV-11). "/new" precedes "/:id" or the id
                route swallows it. */}
            <Route path="/uat-testing" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <UATTestingPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/objects-on-loan" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <OBLRegisterPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/objects-on-loan/register" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <OBLRegisterPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/objects-on-loan/records/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <OBLIssueFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/objects-on-loan/records/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <OBLRecordDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/objects-on-loan/batch-issue" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <OBLBatchIssuePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/objects-on-loan/recoveries" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <OBLRecoveriesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/objects-on-loan/setup" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <OBLSetupPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/objects-on-loan/reports" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <OBLReportsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/materials/stock-transport" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <STOListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/materials/stock-transport/inbound" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <STOInboundPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/materials/stock-transport/in-transit" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <STOInTransitPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/materials/stock-transport/settings" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <STOSettingsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/materials/stock-transport/replenishment" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <STOReplenishmentPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/materials/stock-transport/mobile" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <STOMobilePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/materials/stock-transport/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <STOFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/materials/stock-transport/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <STOFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ess/my-objects" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EssMyObjectsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Hostel Management (DEV-09). */}
            <Route path="/hostel" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <HostelDashboardPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hostel/dashboard" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <HostelDashboardPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hostel/residents" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <HostelResidentsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hostel/residents/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <HostelResidencyDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hostel/requests" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <HostelRequestsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hostel/requests/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <HostelRequestDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hostel/movement" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <HostelMovementPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hostel/strength" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <HostelStrengthPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hostel/visits" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <HostelVisitsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hostel/incidents" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <HostelIncidentsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hostel/charges" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <HostelChargesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hostel/reports" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <HostelReportsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hostel/setup" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <HostelSetupPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ess/my-hostel" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EssMyHostelPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Additional Motivation Payment (DEV-04). Every page checks the caller's OWN
                amp.* codenames — there is no administrator bypass (BR-44). */}
            <Route path="/additional-motivation" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AmpRunsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/additional-motivation/runs" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AmpRunsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/additional-motivation/runs/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AmpRunDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/additional-motivation/hours" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AmpHoursPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/additional-motivation/other-earnings" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AmpOtherEarningsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/additional-motivation/setup" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AmpSetupPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Employee Loans & Advances (DEV-03). "/requests/new" precedes
                "/loans/:id"; both are distinct so neither swallows the other. */}
            <Route path="/employee-loans" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <LoanRegisterPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/employee-loans/register" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <LoanRegisterPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/employee-loans/requests/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <LoanRequestFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/employee-loans/loans/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <LoanDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/employee-loans/approvals" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <LoanApprovalsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/employee-loans/setup" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <LoanSetupPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/employee-loans/reports" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <LoanReportsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ess/my-loans" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EssMyLoansPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Referral Incentive Scheme (DEV-01). */}
            <Route path="/referral-incentives" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ReferralCasesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/referral-incentives/cases" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ReferralCasesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/referral-incentives/cases/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ReferralCaseDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/referral-incentives/runs" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ReferralRunsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/referral-incentives/runs/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ReferralRunDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/referral-incentives/schemes" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ReferralSchemesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/referral-incentives/reports" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ReferralReportsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ess/my-referrals" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EssMyReferralsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Statutory Bonus Engine (DEV-02). */}
            <Route path="/statutory-bonus" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <BonusRunsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/statutory-bonus/runs" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <BonusRunsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/statutory-bonus/runs/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <BonusRunDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/statutory-bonus/setup" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <BonusSetupPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/statutory-bonus/reports" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <BonusReportsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ess/my-bonus" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EssMyBonusPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Performance & Learning (G4). NOTE the ordering: "/new" must
                precede "/:id" or the id route swallows it. */}
            <Route path="/performance/cycles" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AppraisalCyclesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/performance/cycles/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CycleFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/performance/cycles/:id/edit" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CycleFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/performance/cycles/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CycleAppraisalsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/performance/me" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MyPerformancePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/performance/training" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <TrainingPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Goals (Goals & Performance Management BRD §5). The plan id is
                in the URL so a goal sheet can be pasted into a ticket and the
                Back button works. */}
            <Route path="/performance/goals" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MyGoalsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/performance/goal-setup" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <GoalSetupPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/performance/goal-plans/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <GoalPlanPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/performance/competencies" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CompetencyFrameworkPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* The session id is in the URL so a panel's record pastes into a
                ticket. The list route must come FIRST — an :id route declared
                above it would swallow the bare path. */}
            <Route path="/performance/calibration" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CalibrationPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/performance/calibration/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CalibrationPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Multi-source (360°) feedback (§9). Same ordering rule: the
                list route must come BEFORE "/performance/360/:id". */}
            <Route path="/performance/360" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Feedback360Page />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/performance/360/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Feedback360Page />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/performance/my-360" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <My360Page />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Talent — potential, the 9-box, succession and development
                (§10, Phase 5). Same ordering rule throughout: the list route
                comes BEFORE its ":id" sibling, or the id route swallows it. */}
            <Route path="/performance/nine-box" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <NineBoxPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/performance/succession" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SuccessionPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/performance/succession/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SuccessionPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/performance/development" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DevelopmentPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/performance/development/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DevelopmentPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Unauthenticated by design (F-9). NOT under /performance — that
                prefix is gated by the navigation registry, and an external
                rater holds no account for a permission to be resolved against. */}
            <Route path="/feedback/external" element={<ExternalFeedbackPage />} />

            {/* HRM Payroll Routes */}
            <Route path="/hrm/payroll" element={<ProtectedRoute><ModernMainLayout><PayrollDashboard /></ModernMainLayout></ProtectedRoute>} />
            {/* Statutory payroll filings (E7). NOTE the ordering: the
                static sub-paths must come BEFORE "/hrm/payroll/filings/:id",
                or "list" / "calendar" would be swallowed by the :id route. */}
            <Route path="/hrm/payroll/filings" element={<ProtectedRoute><ModernMainLayout><StatutoryFilingsDashboard /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/filings/calendar" element={<ProtectedRoute><ModernMainLayout><FilingCalendarPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/filings/readiness" element={<ProtectedRoute><ModernMainLayout><FilingReadinessPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/filings/list" element={<ProtectedRoute><ModernMainLayout><FilingsListPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/filings/registrations" element={<ProtectedRoute><ModernMainLayout><StatutoryRegistrationsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/filings/pt-slabs" element={<ProtectedRoute><ModernMainLayout><PTSlabsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/filings/carry-ins" element={<ProtectedRoute><ModernMainLayout><CarryInsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/filings/form16" element={<ProtectedRoute><ModernMainLayout><Form16Page /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/filings/:id" element={<ProtectedRoute><ModernMainLayout><FilingDetailPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/wage-types" element={<ProtectedRoute><ModernMainLayout><WageTypesPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/areas" element={<ProtectedRoute><ModernMainLayout><PayrollAreasPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/periods" element={<ProtectedRoute><ModernMainLayout><PayrollPeriodsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/runs" element={<ProtectedRoute><ModernMainLayout><PayrollRunsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/results" element={<ProtectedRoute><ModernMainLayout><PayrollResultsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/tax-declarations" element={<ProtectedRoute><ModernMainLayout><TaxDeclarationsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/compensation-plans" element={<ProtectedRoute><ModernMainLayout><CompensationPlansPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/statutory-components" element={<ProtectedRoute><ModernMainLayout><StatutoryComponentsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/tax-slabs" element={<ProtectedRoute><ModernMainLayout><TaxSlabsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/country-configs" element={<ProtectedRoute><ModernMainLayout><CountryConfigsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/recurring-payments" element={<ProtectedRoute><ModernMainLayout><RecurringPaymentsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/gl-configs" element={<ProtectedRoute><ModernMainLayout><GLConfigsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/compensation-zones" element={<ProtectedRoute><ModernMainLayout><CompensationZonesPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/compensation-bands" element={<ProtectedRoute><ModernMainLayout><CompensationBandsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/compensation-steps" element={<ProtectedRoute><ModernMainLayout><CompensationStepsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/compensation-grid" element={<ProtectedRoute><ModernMainLayout><CompensationGridPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/employee-compensations" element={<ProtectedRoute><ModernMainLayout><EmployeeCompensationsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/compensation-components" element={<ProtectedRoute><ModernMainLayout><EmployeeCompensationComponentsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/additional-payments" element={<ProtectedRoute><ModernMainLayout><AdditionalPaymentsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/tenant-overrides" element={<ProtectedRoute><ModernMainLayout><TenantOverridesPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/area-assignments" element={<ProtectedRoute><ModernMainLayout><AreaAssignmentsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/employee-tax-configs" element={<ProtectedRoute><ModernMainLayout><EmployeeTaxConfigsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/tax-projections" element={<ProtectedRoute><ModernMainLayout><TaxProjectionsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/off-cycle-payrolls" element={<ProtectedRoute><ModernMainLayout><OffCyclePayrollsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/off-cycle-items" element={<ProtectedRoute><ModernMainLayout><OffCycleItemsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/final-settlements" element={<ProtectedRoute><ModernMainLayout><FinalSettlementsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/retro-calculations" element={<ProtectedRoute><ModernMainLayout><RetroCalculationsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/posting-logs" element={<ProtectedRoute><ModernMainLayout><PostingLogsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/payslip-templates" element={<ProtectedRoute><ModernMainLayout><PayslipTemplatesPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/bank-file-configs" element={<ProtectedRoute><ModernMainLayout><BankFileConfigsPage /></ModernMainLayout></ProtectedRoute>} />
            <Route path="/hrm/payroll/bank-file-generations" element={<ProtectedRoute><ModernMainLayout><BankFileGenerationsPage /></ModernMainLayout></ProtectedRoute>} />

            {/* SRM - Purchase Requisition Routes */}
            <Route path="/srm/purchase-requisitions" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PurchaseRequisitionPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/*
              Procurement - PO History (reverse traceability).
              Two declarations, not one optional param: `/srm/po-history` is the
              picker and `/srm/po-history/:id` is a specific order, so the URL
              always says what is on screen and can be pasted into a ticket.
              Both are governed by the same registry entry — the route matcher
              falls back to the longest segment-boundary prefix.
            */}
            <Route path="/srm/po-history" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PurchaseOrderHistoryPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/po-history/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PurchaseOrderHistoryPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Procurement - Replenishment (low-stock worklist) */}
            <Route path="/srm/replenishment" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ReplenishmentWorklist />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* FS-P2P-05 - the P2P KPI page */}
            <Route path="/srm/p2p-kpis" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <P2PKPIDashboardPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* FS-P2P-01 */}
            <Route path="/srm/p2p-controls" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <P2PControlsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/import-lc" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ImportLCPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/confirmations" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ConfirmationsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* FS-P2P-02 */}
            <Route path="/srm/job-work" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <JobWorkPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* FS-P2P-03 */}
            <Route path="/srm/job-work-movements" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <JobWorkMovementsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* SRM - Vendor Master */}
            <Route path="/srm/vendor-master" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VendorMasterListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/vendor-master/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VendorFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/vendor-master/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VendorFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Admin - RBAC */}
            <Route path="/admin/rbac" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RBACAdminPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Admin - User Management */}
            <Route path="/admin/users" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <UsersAdminPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/user-preferences" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <UserPreferencesAdminPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/employee-user-assignment" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EmployeeUserAssignmentPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Admin - Operations Console: run registered management commands.
                Gated by ops.* on the API; the sidebar hides it otherwise. */}
            <Route path="/admin/ops-console" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <OpsConsolePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Administrator - Table Explorer (SE16N): read-only browsing of
                tenant tables. Gated by explorer.view_table on the API; the
                sidebar hides it otherwise. */}
            {/* Administrator - Backup & DB Cockpit (DBACOCKPIT / DB12 / DB13):
                the tenant's own backup schedules, history and database health.
                Gated by backup.* on the API; the sidebar hides it otherwise. */}
            <Route path="/administrator/backups" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <BackupCockpitPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Administrator - AI Provider Keys: the organisation's own
                Anthropic / OpenAI key every AI feature is billed to. Gated by
                aikeys.* on the API; the sidebar hides it otherwise. */}
            <Route path="/administrator/ai-keys" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AIProviderKeysPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Administrator - Bundle Label Printer: the tenant-wide sticker
                layout Cutting Room -> Bundle Labels prints with. Saving is
                tenant.admin on the API. */}
            <Route path="/administrator/bundle-label-printer" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <BundleLabelPrinterPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            <Route path="/administrator/table-explorer" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <TableExplorerPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Admin - Data Access Authorization */}
            <Route path="/admin/data-access" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DataAccessPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Customer Portal administration (E12) — the ERP-side screen.
                Gated by cportal.* on the API; the sidebar hides it otherwise. */}
            <Route path="/admin/customer-portal" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CustomerPortalAdminPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Employee Self-Service (ESS) */}
            <Route path="/ess" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EssDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ess/profile" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MyProfilePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ess/hr/profile-requests" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ProfileRequestsInbox />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ess/leave" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MyLeavePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ess/leave/approvals" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <LeaveApprovalsInbox />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ess/payslips" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PayslipsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ess/directory" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DirectoryPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ess/expenses" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MyExpensesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ess/expenses/approvals" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ExpenseApprovalsInbox />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* SRM - Vendor Expeditor */}
            <Route path="/srm/expeditor" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ExpeditorPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* SRM - Sub-Contracting */}
            <Route path="/srm/subcontract" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SubContractingPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/service-entry-sheets" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ServiceEntrySheetPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/purchase-returns" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PurchaseReturnPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/srm/numbering-sequences" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <NumberingSequencesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Contract Management Routes */}
            <Route path="/contract-management/clauses" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ClauseLibraryPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/contract-management/templates" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ContractTemplatesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/contract-management/contracts" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ContractsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/contract-management/digital-signatures" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DigitalSignaturePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* RIM - Relationship Intelligence */}
            <Route path="/rim" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RIMPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/rim/life-events" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <LifeEventsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/rim/media" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <NodeMediaPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/rim/people" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PeoplePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Business Network — Supplier Registration (public) */}
            <Route path="/supplier-portal/register" element={<SupplierRegistrationPage />} />
            {/* Business Network — Supplier Login (public) */}
            <Route path="/supplier-portal/login" element={<SupplierLoginPage />} />
            {/* Business Network — OAuth Callback (public) */}
            <Route path="/supplier-portal/oauth/callback" element={<OAuthCallbackPage />} />

            {/* Business Network — Supplier Portal (authenticated) */}
            <Route path="/supplier-portal" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-portal/dashboard" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-portal/profile" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierProfilePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-portal/documents" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierCompliancePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-portal/catalog" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierCatalogPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-portal/financials" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierFinancialsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Business Network — Buyer Side */}
            <Route path="/supplier-discovery" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierDiscoveryPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-onboarding" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierOnboardingPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/onboarding-templates" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <OnboardingTemplateBuilder />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-messages" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <BBNMessagingPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Phase 4: Supplier Portal Transaction Routes */}
            <Route path="/supplier-portal/rfqs" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierRFQsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-portal/rfis" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierRFIsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-portal/purchase-orders" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierPurchaseOrdersPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-portal/shipments" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierShipmentsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-portal/invoices" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierInvoicesPortalPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-portal/payments" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierPaymentsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/buyer-discovery" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <BuyerDiscoveryPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Phase 4: Buyer-side Routes */}
            <Route path="/incoming-shipments" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <IncomingShipmentsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-invoices" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierInvoicesReviewPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-documents" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierDocumentsReviewPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Phase 5: Supplier Portal — Analytics & Reviews */}
            <Route path="/supplier-portal/analytics" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierAnalyticsDashboardPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-portal/reviews" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierReviewsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Buyer Connections — approve/reject incoming requests */}
            <Route path="/supplier-portal/connections" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <BuyerConnectionsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-portal/messages" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierMessagingPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Phase 7: Supplier Notification Preferences */}
            <Route path="/supplier-portal/notifications" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierNotificationPreferencesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Phase 7.2: Subscription & Billing */}
            <Route path="/supplier-portal/subscription" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupplierSubscriptionPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Phase 5: Buyer-side — Supplier Analytics */}
            <Route path="/supplier-analytics" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <BuyerSupplierAnalyticsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-analytics/:supplierId" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <BuyerSupplierAnalyticsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Travel Management Routes */}
            <Route path="/hrm/travel/requests" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <TravelRequestsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/travel/requests/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <TravelRequestDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/travel/claims" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ExpenseClaimPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/travel/desk" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <TravelDeskPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/travel/policies" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <TravelPoliciesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/travel/advances" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <TravelAdvancesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/travel/expense-categories" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ExpenseCategoriesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/travel/vendors" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <TravelVendorsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/hrm/travel/destination-tiers" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DestinationTiersPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Common Reference Data Routes */}
            <Route path="/common" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Typography variant="h4" sx={{ p: 3 }}>Common Reference Data</Typography>
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/common/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Routes>
                    <Route path="currencies" element={<CurrencyPage />} />
                    <Route path="countries" element={<CountryPage />} />
                    <Route path="regions" element={<RegionPage />} />
                    <Route path="districts" element={<DistrictPage />} />
                    <Route path="payment-terms" element={<PaymentTermPage />} />
                    <Route path="incoterms" element={<IncotermPage />} />
                    <Route path="units-of-measure" element={<UnitOfMeasurePage />} />
                    <Route path="seasons" element={<SeasonPage />} />
                    <Route path="hierarchy" element={<HierarchyWorkspace />} />
                    <Route path="hierarchy-graph" element={<HierarchyManagement />} />
                    <Route path="gst" element={<GSTPage />} />
                    <Route path="tds-sections" element={<TDSSectionPage />} />
                  </Routes>
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Document Routes */}
            <Route path="/document/:documentId" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DocumentViewPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Workflow Routes */}
            <Route path="/workflow/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Routes>
                    {/* User Approval Routes */}
                    <Route path="my-approvals" element={<MyApprovalsPage />} />
                    <Route path="pending-approvals" element={<PendingApprovalsAdminPage />} />
                    <Route path="instance/:id" element={<WorkflowInstancePage />} />
                    <Route path="notification-preferences" element={<NotificationPreferencesPage />} />

                    {/* Admin Workflow Management Routes */}
                    <Route path="admin/workflows" element={<WorkflowListPage />} />
                    <Route path="admin/workflows/new" element={<WorkflowDesignerPage />} />
                    <Route path="admin/workflows/:id/edit" element={<WorkflowDesignerPage />} />

                    {/* Default redirect */}
                    <Route path="" element={<MyApprovalsPage />} />
                  </Routes>
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Admin Routes */}
            <Route path="/admin/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Routes>
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="license" element={<LicenseDashboard />} />
                    <Route path="data-seeding" element={<DataSeedingPage />} />
                    <Route path="data-seeding/:id" element={<DataSeedingPage />} />
                    <Route path="ai-usage" element={<AIUsageDashboard />} />
                    <Route path="gst-usage" element={<GSTUsageDashboard />} />
                    <Route path="" element={<AdminDashboard />} />
                  </Routes>
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* REM Routes */}
            <Route path="/rem" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RemDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/rem/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Routes>
                    <Route path="properties" element={<PropertiesList />} />
                    <Route path="property-map" element={<PropertyMapView />} />
                    <Route path="properties/new" element={<PropertyForm />} />
                    <Route path="properties/:id" element={<PropertyForm />} />
                    <Route path="tenants" element={<TenantsList />} />
                    <Route path="tenants/new" element={<TenantForm />} />
                    <Route path="tenants/:id" element={<TenantForm />} />
                    <Route path="leases" element={<LeasesList />} />
                    <Route path="leases/new" element={<LeaseForm />} />
                    <Route path="leases/:id" element={<LeaseForm />} />
                    <Route path="rent-payments" element={<RentPaymentsList />} />
                    <Route path="rent-payments/new" element={<RentPaymentForm />} />
                    <Route path="rent-payments/:id" element={<RentPaymentForm />} />
                    <Route path="maintenance" element={<MaintenanceRequestsList />} />
                    <Route path="maintenance/new" element={<MaintenanceRequestForm />} />
                    <Route path="maintenance/:id" element={<MaintenanceRequestForm />} />
                    <Route path="inspection-schedules" element={<InspectionSchedulesList />} />
                    <Route path="inspection-schedules/new" element={<InspectionScheduleForm />} />
                    <Route path="inspection-schedules/:id" element={<InspectionScheduleForm />} />
                    <Route path="inspection-reports" element={<InspectionReportsList />} />
                    <Route path="inspection-reports/new" element={<InspectionReportForm />} />
                    <Route path="inspection-reports/:id" element={<InspectionReportForm />} />
                    {/* Legacy redirect: old single "Inspections" page now split into schedules + reports */}
                    <Route path="inspections" element={<Navigate to="/rem/inspection-schedules" replace />} />
                    <Route path="inspections/*" element={<Navigate to="/rem/inspection-schedules" replace />} />
                    {/* Property Reference Data */}
                    <Route path="property-types" element={<PropertyTypesList />} />
                    <Route path="property-types/new" element={<PropertyTypeForm />} />
                    <Route path="property-types/:id" element={<PropertyTypeForm />} />
                    <Route path="property-statuses" element={<PropertyStatusesList />} />
                    <Route path="property-statuses/new" element={<PropertyStatusForm />} />
                    <Route path="property-statuses/:id" element={<PropertyStatusForm />} />
                    <Route path="property-sf-numbers" element={<PropertySFNumbersList />} />
                    <Route path="property-sf-numbers/new" element={<PropertySFNumberForm />} />
                    <Route path="property-sf-numbers/:id" element={<PropertySFNumberForm />} />
                    <Route path="property-documents" element={<PropertyDocumentsList />} />
                    <Route path="property-documents/new" element={<PropertyDocumentForm />} />
                    <Route path="property-documents/:id" element={<PropertyDocumentForm />} />
                    <Route path="property-ownerships" element={<PropertyOwnershipsList />} />
                    <Route path="property-ownerships/new" element={<PropertyOwnershipForm />} />
                    <Route path="property-ownerships/:id" element={<PropertyOwnershipForm />} />
                    <Route path="property-valuations" element={<PropertyValuationsList />} />
                    <Route path="property-valuations/new" element={<PropertyValuationForm />} />
                    <Route path="property-valuations/:id" element={<PropertyValuationForm />} />
                    <Route path="property-taxes" element={<PropertyTaxesList />} />
                    <Route path="property-taxes/new" element={<PropertyTaxForm />} />
                    <Route path="property-taxes/:id" element={<PropertyTaxForm />} />
                    <Route path="property-insurances" element={<PropertyInsurancesList />} />
                    <Route path="property-insurances/new" element={<PropertyInsuranceForm />} />
                    <Route path="property-insurances/:id" element={<PropertyInsuranceForm />} />
                    <Route path="property-expenses" element={<PropertyExpensesList />} />
                    <Route path="property-expenses/new" element={<PropertyExpenseForm />} />
                    <Route path="property-expenses/:id" element={<PropertyExpenseForm />} />
                    <Route path="shared-documents" element={<SharedDocumentsList />} />
                    <Route path="shared-documents/new" element={<SharedDocumentForm />} />
                    <Route path="shared-documents/:id" element={<SharedDocumentForm />} />
                    {/* Space Management */}
                    <Route path="floors" element={<FloorsList />} />
                    <Route path="floors/new" element={<FloorForm />} />
                    <Route path="floors/:id" element={<FloorForm />} />
                    <Route path="spaces" element={<SpacesList />} />
                    <Route path="spaces/new" element={<SpaceForm />} />
                    <Route path="spaces/:id" element={<SpaceForm />} />
                    <Route path="space-types" element={<SpaceTypesList />} />
                    <Route path="space-types/new" element={<SpaceTypeForm />} />
                    <Route path="space-types/:id" element={<SpaceTypeForm />} />
                    <Route path="space-amenities" element={<SpaceAmenitiesList />} />
                    <Route path="space-amenities/new" element={<SpaceAmenityForm />} />
                    <Route path="space-amenities/:id" element={<SpaceAmenityForm />} />
                    <Route path="space-modifications" element={<SpaceModificationsList />} />
                    <Route path="space-modifications/new" element={<SpaceModificationForm />} />
                    <Route path="space-modifications/:id" element={<SpaceModificationForm />} />
                    <Route path="space-occupancies" element={<SpaceOccupanciesList />} />
                    <Route path="space-occupancies/new" element={<SpaceOccupancyForm />} />
                    <Route path="space-occupancies/:id" element={<SpaceOccupancyForm />} />
                    <Route path="common-areas" element={<CommonAreasList />} />
                    <Route path="common-areas/new" element={<CommonAreaForm />} />
                    <Route path="common-areas/:id" element={<CommonAreaForm />} />
                    {/* Lease Management */}
                    <Route path="lease-types" element={<LeaseTypesList />} />
                    <Route path="lease-types/new" element={<LeaseTypeForm />} />
                    <Route path="lease-types/:id" element={<LeaseTypeForm />} />
                    <Route path="lease-renewal-offers" element={<LeaseRenewalOffersList />} />
                    <Route path="lease-renewal-offers/new" element={<LeaseRenewalOfferForm />} />
                    <Route path="lease-renewal-offers/:id" element={<LeaseRenewalOfferForm />} />
                    <Route path="lease-terminations" element={<LeaseTerminationsList />} />
                    <Route path="lease-terminations/new" element={<LeaseTerminationForm />} />
                    <Route path="lease-terminations/:id" element={<LeaseTerminationForm />} />
                    {/* Maintenance */}
                    <Route path="maintenance-activities" element={<MaintenanceActivitiesList />} />
                    <Route path="maintenance-activities/new" element={<MaintenanceActivityForm />} />
                    <Route path="maintenance-activities/:id" element={<MaintenanceActivityForm />} />
                    {/* Tenant Management */}
                    <Route path="tenant-types" element={<TenantTypesList />} />
                    <Route path="tenant-types/new" element={<TenantTypeForm />} />
                    <Route path="tenant-types/:id" element={<TenantTypeForm />} />
                    <Route path="tenant-contacts" element={<TenantContactsList />} />
                    <Route path="tenant-contacts/new" element={<TenantContactForm />} />
                    <Route path="tenant-contacts/:id" element={<TenantContactForm />} />
                    <Route path="tenant-addresses" element={<TenantAddressesList />} />
                    <Route path="tenant-addresses/new" element={<TenantAddressForm />} />
                    <Route path="tenant-addresses/:id" element={<TenantAddressForm />} />
                    <Route path="tenant-approvals" element={<TenantApprovalsList />} />
                    <Route path="tenant-approvals/new" element={<TenantApprovalForm />} />
                    <Route path="tenant-approvals/:id" element={<TenantApprovalForm />} />
                    <Route path="tenant-communications" element={<TenantCommunicationsList />} />
                    <Route path="tenant-communications/new" element={<TenantCommunicationForm />} />
                    <Route path="tenant-communications/:id" element={<TenantCommunicationForm />} />
                    <Route path="tenant-documents" element={<TenantDocumentsList />} />
                    <Route path="tenant-documents/new" element={<TenantDocumentForm />} />
                    <Route path="tenant-documents/:id" element={<TenantDocumentForm />} />
                    <Route path="tenant-histories" element={<TenantHistoriesList />} />
                    <Route path="tenant-histories/new" element={<TenantHistoryForm />} />
                    <Route path="tenant-histories/:id" element={<TenantHistoryForm />} />
                    <Route path="tenant-incidents" element={<TenantIncidentsList />} />
                    <Route path="tenant-incidents/new" element={<TenantIncidentForm />} />
                    <Route path="tenant-incidents/:id" element={<TenantIncidentForm />} />
                    <Route path="tenant-notifications" element={<TenantNotificationsList />} />
                    <Route path="tenant-notifications/new" element={<TenantNotificationForm />} />
                    <Route path="tenant-notifications/:id" element={<TenantNotificationForm />} />
                    <Route path="tenant-screenings" element={<TenantScreeningsList />} />
                    <Route path="tenant-screenings/new" element={<TenantScreeningForm />} />
                    <Route path="tenant-screenings/:id" element={<TenantScreeningForm />} />
                    {/* Financial */}
                    <Route path="utility-charges" element={<UtilityChargesList />} />
                    <Route path="utility-charges/new" element={<UtilityChargeForm />} />
                    <Route path="utility-charges/:id" element={<UtilityChargeForm />} />
                    {/* Reports */}
                    <Route path="reports/rent-roll" element={<RentRollReport />} />
                    <Route path="reports/lease-expiry" element={<LeaseExpiryReport />} />
                    <Route path="reports/rent-arrears" element={<RentArrearsReport />} />
                    <Route path="reports/occupancy" element={<OccupancyReport />} />
                    <Route path="reports/income-expense" element={<PropertyIncomeExpenseReport />} />
                    <Route path="reports/tenant-ledger" element={<TenantLedgerReport />} />
                    {/* Settings */}
                    <Route path="settings" element={<RealEstateSettingsList />} />
                    <Route path="settings/new" element={<RealEstateSettingsForm />} />
                    <Route path="settings/:id" element={<RealEstateSettingsForm />} />
                  </Routes>
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Dispute Management Routes */}
            <Route path="/dispute-management" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DisputeDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/dispute-management/list" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DisputesList />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/dispute-management/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DisputeForm />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/dispute-management/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DisputeDetail />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/dispute-management/:id/edit" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DisputeForm />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* SIM – Supplier Invoice Management Routes */}
            <Route path="/supplier-invoice-management" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SIMDashboardPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-invoice-management/invoices" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SIMInvoiceListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-invoice-management/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SIMInvoiceDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-invoice-management/approval-inbox" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SIMApprovalInboxPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-invoice-management/exceptions" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SIMExceptionPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-invoice-management/approval-rules" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SIMApprovalRulesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-invoice-management/process" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SIMInvoiceProcessPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-invoice-management/process/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SIMInvoiceProcessPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Visitor Management Routes */}
            <Route path="/visitor-management" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VisitorDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/visitor-management/visitors" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VisitorListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/visitor-management/visit-requests" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VisitRequestListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/visitor-management/visit-requests/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VisitRequestFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/visitor-management/visit-requests/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VisitRequestFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/visitor-management/visit-requests/:id/edit" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VisitRequestFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/visitor-management/checkin" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CheckInPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/visitor-management/active" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CheckInPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/visitor-management/logs" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VisitLogPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/visitor-management/locations" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <LocationsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/visitor-management/blacklist" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <BlacklistPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/visitor-management/gatekeeper" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <GatekeeperPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Front Desk Routes */}
            <Route path="/front-desk" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <FrontDeskDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/front-desk/entries" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EntryListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/front-desk/entries/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EntryFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/front-desk/entries/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EntryDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/front-desk/entries/:id/edit" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EntryFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/front-desk/inbox" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MyInboxPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/front-desk/delegation" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DelegationSettingsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/front-desk/pending-goods-receipt" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PendingGoodsReceiptPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Stock Audit Routes */}
            <Route path="/stock-audit" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <StockAuditDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/stock-audit/documents" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AuditDocumentListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/stock-audit/documents/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AuditDocumentDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/stock-audit/documents/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AuditDocumentDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/stock-audit/count/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CountEntryPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Investment Portfolio Routes */}
            <Route path="/investment" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Navigate to="/investment/overview" replace />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/investment/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PortfolioDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Financial Statements (Enterprise Hierarchy rollup) */}
            <Route path="/financial/statements" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <FinancialStatementsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Period-end close & posting-period locking (E4) */}
            <Route path="/financial/period-close" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PeriodClosePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* GST Compliance (E1) — e-Invoice / e-Way Bill / Returns */}
            <Route path="/financial/gst-compliance" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <GSTComplianceWorkspace />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Credit Management (E5) — exposure, blocked orders, profiles */}
            <Route path="/credit-management" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CreditManagementPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Demo walkthrough launcher: needs the explicit codename demo.run_walkthrough (checked in the page; no
                admin bypass) and a demo/QA/localhost host. Not in the sidebar - reached by URL. */}
            <Route path="/demo" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DemoLauncherPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Vendor Capacity & Capability (SRM) — commitments, utilisation, breaches */}
            <Route path="/srm/vendor-capacity" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VendorCapacityPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* SD Pricing Engine (E11) — price lists, conditions, floors, simulator */}
            <Route path="/crm/pricing" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PricingWorkspacePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Landed cost / import purchase costing (E8) — capitalise duty/freight/CHA onto GR lines */}
            <Route path="/materials/landed-cost" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <LandedCostPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Serial number management (E9) — per-unit identity, history, warranty */}
            {/* FS-P2P-04 */}
            <Route path="/materials/fabric-rolls" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <FabricRollsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/materials/serial-numbers" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SerialNumberPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Mobile warehouse operations (E10) — scan-driven GR/put-away/transfer/pick/count */}
            <Route path="/wms" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MobileWarehousePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Foreign-currency revaluation run (E6) */}
            <Route path="/financial/fc-revaluation" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <FCRevaluationPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* MCA Audit Trail (Rule 11(g)) statutory report */}
            <Route path="/financial/audit-trail" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <AuditTrailReportPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Schedule III Statement of Profit & Loss */}
            <Route path="/financial/schedule-iii-pl" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ScheduleIIIPLPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Schedule III Balance Sheet */}
            <Route path="/financial/schedule-iii-bs" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ScheduleIIIBSPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Schedule III Statement of Cash Flows */}
            <Route path="/financial/schedule-iii-cf" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ScheduleIIICFPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Schedule III Statement of Changes in Equity */}
            <Route path="/financial/schedule-iii-soce" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ScheduleIIISOCEPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Schedule III Financial Statements Pack workspace */}
            <Route path="/financial/schedule-iii" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ScheduleIIIWorkspace />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* GL Tagging Workbench (strict tag-only classification) */}
            <Route path="/financial/statement-tagging" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <StatementTaggingPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Accounting Policies + FS Notes editor */}
            <Route path="/financial/fs-notes" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <FSNotesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Profitability Analysis (CO-PA) */}
            <Route path="/finance/profitability" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ProfitabilityAnalysisPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* CO-PA Value Field Mapping Workbench */}
            <Route path="/financial/profitability-mapping" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ProfitabilityMappingPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Management Accounting — Ratio Analysis */}
            <Route path="/management-accounting/ratio-analysis" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RatioAnalysisPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Process Mining */}
            <Route path="/process-mining" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ProcessMiningPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Continuous Process-Mining Copilot (U7).
                The open finding is in the URL so a reviewer can paste it into a
                ticket and the Back button closes the drawer. */}
            <Route path="/process-copilot" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ProcessCopilotPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/process-copilot/findings/:findingId" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ProcessCopilotPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Piece-Level Production Tracking.
                The line is IN THE URL on the board, so a board pastes into a
                ticket and a floor display bookmarks one line. Literal segments
                are declared before the parameterised ones. */}
            <Route path="/production/line-board" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <LineBoardPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/production/line-board/:workCenterId" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <LineBoardPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/production/piece-tracking/scan" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PieceScanPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/production/piece-tracking/pieces" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PiecesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/production/piece-tracking/pieces/:pieceCode" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PiecesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/production/piece-tracking/analytics" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PieceAnalyticsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/production/piece-tracking/setup" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PieceTrackingSetupPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Project System (PS) Routes.
                Pages live under '/projects'; the API is mounted on
                '/proman/api'. The two prefixes are deliberately different — a
                proxy mount that collides with a page route breaks hard refresh.
                The project id is in the URL so a workspace can be shared. */}
            <Route path="/projects" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ProjectListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Declared BEFORE '/projects/:id' so the literal segment wins —
                otherwise 'timesheets' is captured as a project id. */}
            <Route path="/projects/dashboard" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ProjectDashboardPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/projects/portfolio" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ProjectPortfolioPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/projects/timesheets" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <TimesheetsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Regularising time the task timer missed — lines land on the
                employee's draft week and take the same approval path. */}
            <Route path="/projects/timesheets/entry" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <TimesheetEntryPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Also literal-before-':id'. The working pattern the scheduler
                counts days in; holidays stay in production planning. */}
            <Route path="/projects/calendars" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ProjectCalendarsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Literal-before-':id': project types, WBS templates and activity rates. */}
            <Route path="/projects/masters" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ProjectMastersPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Also before '/projects/:id', or 'meetings' is captured as a
                project id. A meeting need not belong to a project at all, which
                is why these are siblings of the project routes rather than
                nested inside one. */}
            <Route path="/projects/resources" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ProjectResourcesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Literal segment, so it must be declared BEFORE '/projects/:id'
                or 'chat' is read as a project id. */}
            <Route path="/projects/chat" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PromanProjectChatPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/projects/meetings" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MeetingsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/projects/meetings/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MeetingDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/projects/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ProjectDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Internal Orders Routes */}
            <Route path="/finance/internal-orders" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <InternalOrdersPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/finance/internal-orders/post-cost" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CostPostingPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/finance/internal-orders/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <InternalOrderDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Quality Management (QM) Routes */}
            <Route path="/rm/risk-register" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RiskRegisterPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Cash Management */}
            <Route path="/cash-management" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CashManagementPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            <Route path="/rm/dashboard" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RiskDashboardPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Compliance Management (CM) Routes */}
            <Route path="/cm/dashboard" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ComplianceDashboardPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cm/obligations" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ObligationRegisterPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cm/calendar" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ComplianceCalendarPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cm/tasks" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ComplianceTasksPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cm/policies" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PolicyManagementPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cm/findings" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ComplianceFindingsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* Design Studio (generic design backbone) */}
            <Route path="/design-studio" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DesignStudioPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* PLM — garment Product Lifecycle Management */}
            <Route path="/plm/styles" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PlmStylesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/plm/sample-reports" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <PlmSampleReportsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/inspection-lots" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <InspectionLotsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/inspection-lots/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <InspectionLotDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/defect-records" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <DefectRecordsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/inspection-samples" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <InspectionSamplesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/usage-decisions" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <UsageDecisionsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/inspection-plans" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <InspectionPlansPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/master-data" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMMasterDataPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/ncr" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <NCRPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/capa" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CAPAPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/complaints/customer" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CustomerComplaintsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/complaints/vendor" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <VendorComplaintsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/audits" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QualityAuditsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/audits/:id/execute" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMAuditExecutePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/audit-dashboard" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMAuditDashboardPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/audit-programme" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMAuditProgrammePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/audit-templates" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMAuditTemplatesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/audit-verification" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMAuditVerificationPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/my-audit-actions" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMMyAuditActionsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/releases" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMReleasesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/release-setup" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMReleaseSetupPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/shelf-life" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMShelfLifePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/retained-samples" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMRetainedSamplesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/traceability" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMTraceabilityPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/containments" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMContainmentsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/release-reports" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMReleaseReportsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/containments/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMContainmentsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/line-checks" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMLineChecksPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/audit-setup" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMAuditSetupPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/calibration-impact" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMCalibrationImpactPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/msa-studies" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMMSAStudiesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/daily-checks" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMDailyChecksPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/calibration-reports" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMCalibrationReportsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/certifications" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMCertificationsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/documents" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMQualityDocumentsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/controlled-copies" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMControlledCopiesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/document-types" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMDocumentTypesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/record-retention" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMRecordRetentionPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/quality-roles" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMQualityRolesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/published-documents" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMPublishedDocumentsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/quality-dashboard" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMQualityDashboardPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/kpi-setup" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMKPISetupPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/management-review" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMManagementReviewPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/quality-planning" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMQualityPlanningPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/lab-tests" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMLabTestsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/spc" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMSPCPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/cost-of-quality" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMCostOfQualityPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/calibration" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMCalibrationPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/inspection-plan-operations" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <InspectionPlanOperationsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/sampling-tables" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMSamplingTablesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/incoming-setup" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMIncomingSetupPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/incoming-worklist" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMIncomingWorkListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/supplier-certificates" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMSupplierCertificatesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/supplier-scorecards" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMSupplierScorecardsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/scars" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMSCARPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/supplier-blocks" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMSupplierBlocksPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/concessions" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMConcessionsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/action-tracker" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMActionTrackerPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/nc-setup" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMNonconformanceSetupPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/nc-reports" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMNonconformanceReportsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/sampling-schemes" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMSamplingSchemesPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/switching-keys" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMSwitchingKeysPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/inspection-reports" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMInspectionReportsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/capture" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMCaptureHomePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/capture/audit/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMAqlAuditPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/capture/measure/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMMeasurementCheckPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/capture/packing/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMPackingAuditPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/capture/round/:stationId" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMTlsRoundPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/analytics" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <QMAnalyticsDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/quality/measurement-charts" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MeasurementChartImportPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/quality/measurement-charts/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MeasurementChartImportPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/measurement-charts" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MeasurementChartsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/qm/measurement-charts/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MeasurementChartsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Batch Traceability Routes */}
            <Route path="/trace" element={
              <ProtectedRoute><ModernMainLayout><BatchTraceabilityHome /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/trace/genealogy" element={
              <ProtectedRoute><ModernMainLayout><BatchGenealogyPage /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/trace/genealogy/:batchId" element={
              <ProtectedRoute><ModernMainLayout><BatchGenealogyPage /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/trace/stock" element={
              <ProtectedRoute><ModernMainLayout><BatchStockVisibilityPage /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/trace/history" element={
              <ProtectedRoute><ModernMainLayout><BatchTransactionHistoryPage /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/trace/recalls" element={
              <ProtectedRoute><ModernMainLayout><RecallWizardPage /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/trace/quality" element={
              <ProtectedRoute><ModernMainLayout><BatchQualityPage /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/trace/quality/:batchId" element={
              <ProtectedRoute><ModernMainLayout><BatchQualityPage /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/trace/expiry" element={
              <ProtectedRoute><ModernMainLayout><ExpiryShelfLifePage /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/trace/documents" element={
              <ProtectedRoute><ModernMainLayout><BatchDocumentsPage /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/trace/risk" element={
              <ProtectedRoute><ModernMainLayout><BatchRiskDashboard /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/trace/dashboard" element={
              <ProtectedRoute><ModernMainLayout><TraceabilityDashboards /></ModernMainLayout></ProtectedRoute>
            } />

            {/* EHS Routes */}
            <Route path="/ehs" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <EHSDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ehs/incidents" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <IncidentsList />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ehs/incidents/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <IncidentForm />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ehs/incidents/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <IncidentForm />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ehs/incidents/:id/edit" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <IncidentForm />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ehs/incident-types" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <IncidentTypesList />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ehs/severity-levels" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SeverityLevelsList />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/ehs/incident-statuses" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <IncidentStatusesList />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Customer Service Routes */}
            <Route path="/customer-service" element={
              <ProtectedRoute><ModernMainLayout><CustomerServiceDashboard /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/customer-service/tickets" element={
              <ProtectedRoute><ModernMainLayout><ServiceTicketsList /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/customer-service/tickets/new" element={
              <ProtectedRoute><ModernMainLayout><ServiceTicketForm /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/customer-service/tickets/:id" element={
              <ProtectedRoute><ModernMainLayout><ServiceTicketForm /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/customer-service/tickets/:id/edit" element={
              <ProtectedRoute><ModernMainLayout><ServiceTicketForm /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/customer-service/assets" element={
              <ProtectedRoute><ModernMainLayout><CustomerAssetsList /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/customer-service/assets/new" element={
              <ProtectedRoute><ModernMainLayout><CustomerAssetForm /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/customer-service/assets/:id" element={
              <ProtectedRoute><ModernMainLayout><CustomerAssetForm /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/customer-service/assets/:id/edit" element={
              <ProtectedRoute><ModernMainLayout><CustomerAssetForm /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/customer-service/repair-jobs" element={
              <ProtectedRoute><ModernMainLayout><RepairJobsList /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/customer-service/repair-jobs/new" element={
              <ProtectedRoute><ModernMainLayout><RepairJobForm /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/customer-service/repair-jobs/:id" element={
              <ProtectedRoute><ModernMainLayout><RepairJobForm /></ModernMainLayout></ProtectedRoute>
            } />
            <Route path="/customer-service/repair-jobs/:id/edit" element={
              <ProtectedRoute><ModernMainLayout><RepairJobForm /></ModernMainLayout></ProtectedRoute>
            } />

            {/* Administrator Routes */}
            <Route path="/administrator/audit-log" element={
              <ProtectedRoute><ModernMainLayout><AuditLogList /></ModernMainLayout></ProtectedRoute>
            } />

            {/* MBE Routes */}
            <Route path="/mbe" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MBECockpitPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/mbe/exceptions" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <ExceptionListPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/mbe/rules" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <RuleConfigPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/mbe/analytics" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <MBEAnalyticsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/mbe/suppression" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SuppressionCalendarPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Cash Journal Routes */}
            <Route path="/finance/cash-journal/dashboard" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CashDashboardPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/finance/cash-journal" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CashJournalPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/finance/cash-journal/journals/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CashJournalFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/finance/cash-journal/journals/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CashJournalFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/finance/cash-journal/journals/:id/edit" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CashJournalFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/finance/cash-journal/day-close" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CashDayClosePage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/finance/cash-journal/transactions" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CashTransactionsPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/finance/cash-journal/transactions/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CashTransactionFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/finance/cash-journal/payment/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CashPaymentFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/finance/cash-journal/receipt/new" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CashReceiptFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/finance/cash-journal/transactions/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CashTransactionDetailPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/finance/cash-journal/transactions/:id/edit" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <CashTransactionFormPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Support Desk Routes */}
            <Route path="/support-desk" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupportDeskDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/support-desk/tickets" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupportTicketsList />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/support-desk/tickets/:id" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <SupportTicketDetail />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            {/* The Knowledge Base and FAQs are retired: Biztimize Notes replace them. */}
            <Route path="/support-desk/knowledge-base" element={<Navigate to="/knowledge" replace />} />
            <Route path="/support-desk/knowledge-base/:slug" element={<Navigate to="/knowledge" replace />} />
            <Route path="/support-desk/faqs" element={<Navigate to="/knowledge" replace />} />

            {/* Biztimize Notes — the Note Assistant here; reading on the Help Center host */}
            <Route path="/knowledge/assistant" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <NoteAssistantPage />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/knowledge" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <NotesRedirect target="home" />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/knowledge/open/:number" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <NotesRedirect target="note" />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/knowledge/subscriptions" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <NotesRedirect target="subscriptions" />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/knowledge/local/:localNumber" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <NotesRedirect target="local" />
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Textile Costing Routes */}
            <Route path="/textile-costing" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <TextileCostingDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/textile-costing/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Routes>
                    <Route path="opportunities" element={<CostingOpportunitiesList />} />
                    <Route path="consolidated/:opportunityId" element={<ConsolidatedCostingView />} />
                    <Route path="cutplans" element={<CutPlansList />} />
                    <Route path="cut-plan-worksheet" element={<CutPlanWorksheetPage />} />
                    <Route path="greige" element={<GreigeCostingsList />} />
                    <Route path="finished" element={<FinishedCostingsList />} />
                    <Route path="madeup" element={<MadeUpCostingsList />} />
                    <Route path="terry-fabric" element={<TerryFabricCostingsList />} />
                    <Route path="terry-madeup" element={<TerryMadeUpCostingsList />} />
                    <Route path="set" element={<SetCostingsList />} />
                    <Route path="workspace" element={<TextileCostingWorkspace />} />
                    <Route path="component-master" element={<TCComponentMasterPage />} />
                    <Route path="price-revisions" element={<TCPriceRevisionsPage />} />
                    <Route path="price-revisions/:id" element={<TCPriceRevisionDetailPage />} />
                  </Routes>
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* Garment Costing Routes */}
            <Route path="/garment-costing" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <GarmentCostingDashboard />
                </ModernMainLayout>
              </ProtectedRoute>
            } />
            <Route path="/garment-costing/*" element={
              <ProtectedRoute>
                <ModernMainLayout>
                  <Routes>
                    <Route path="workspace" element={<GarmentCostingWorkspace />} />
                    <Route path="workspace-v2" element={<GarmentCostingWorkspaceV2 />} />
                    <Route path="styles" element={<StyleCostingsList />} />
                    <Route path="dashboard" element={<GarmentCostingDashboard />} />
                    <Route path="masters" element={<GcCommercialMastersPage />} />
                    <Route path="results" element={<GcCostingResultsPage />} />
                  </Routes>
                </ModernMainLayout>
              </ProtectedRoute>
            } />

            {/* E-Commerce Routes */}
            <Route path="/shop" element={
              <StorefrontLayout>
                <StorefrontHomePage />
              </StorefrontLayout>
            } />
            <Route path="/shop/login" element={
              <StorefrontLayout themed>
                <ShopLoginPage />
              </StorefrontLayout>
            } />
            <Route path="/shop/register" element={
              <StorefrontLayout themed>
                <ShopLoginPage />
              </StorefrontLayout>
            } />
            <Route path="/shop/products" element={
              <StorefrontLayout themed>
                <ProductListPage />
              </StorefrontLayout>
            } />
            <Route path="/shop/products/:slug" element={
              <StorefrontLayout themed>
                <ProductDetailPage />
              </StorefrontLayout>
            } />
            <Route path="/shop/policies/:type" element={
              <StorefrontLayout themed>
                <PolicyPage />
              </StorefrontLayout>
            } />
            <Route path="/shop/cart" element={
              <StorefrontLayout themed>
                <CartPage />
              </StorefrontLayout>
            } />
            <Route path="/shop/checkout" element={
              <StorefrontLayout themed>
                <CheckoutPage />
              </StorefrontLayout>
            } />
            <Route path="/shop/order/confirmation" element={
              <StorefrontLayout themed>
                <OrderConfirmationPage />
              </StorefrontLayout>
            } />

            {/* Protected E-Commerce Routes */}
            <Route path="/shop/my-account" element={
              <ProtectedRoute>
                <StorefrontLayout themed>
                  <MyAccountPage />
                </StorefrontLayout>
              </ProtectedRoute>
            } />
            <Route path="/shop/my-orders" element={
              <ProtectedRoute>
                <StorefrontLayout themed>
                  <MyOrdersPage />
                </StorefrontLayout>
              </ProtectedRoute>
            } />
            <Route path="/shop/my-orders/:orderId" element={
              <ProtectedRoute>
                <StorefrontLayout themed>
                  <OrderDetailPage />
                </StorefrontLayout>
              </ProtectedRoute>
            } />
            <Route path="/shop/my-addresses" element={
              <ProtectedRoute>
                <StorefrontLayout themed>
                  <MyAddressesPage />
                </StorefrontLayout>
              </ProtectedRoute>
            } />
            <Route path="/shop/wishlist" element={
              <ProtectedRoute>
                <StorefrontLayout themed>
                  <WishlistPage />
                </StorefrontLayout>
              </ProtectedRoute>
            } />
            <Route path="/shop/comparison" element={
              <StorefrontLayout themed>
                <ComparisonPage />
              </StorefrontLayout>
            } />

            {/* Customer Portal (E12).

                No ProtectedRoute: that guard is built around the ERP session
                (localStorage `token`, must_change_password, ERP permissions).
                A portal user holds none of those, so it would bounce every one
                of them to the staff login. PortalLayout does the equivalent
                check against the portal's own session and redirects to
                /portal/login itself. */}
            <Route path="/portal/login" element={<PortalLoginPage />} />
            <Route path="/portal/activate" element={<PortalActivatePage />} />
            <Route path="/portal" element={
              <PortalLayout><PortalDashboardPage /></PortalLayout>
            } />
            <Route path="/portal/orders" element={
              <PortalLayout><PortalOrdersPage /></PortalLayout>
            } />
            <Route path="/portal/orders/:orderId" element={
              <PortalLayout><PortalOrderDetailPage /></PortalLayout>
            } />
            <Route path="/portal/shipments" element={
              <PortalLayout><PortalShipmentsPage /></PortalLayout>
            } />
            <Route path="/portal/shipments/:shipmentId" element={
              <PortalLayout><PortalShipmentsPage /></PortalLayout>
            } />
            <Route path="/portal/finance" element={
              <PortalLayout><PortalFinancePage /></PortalLayout>
            } />
            <Route path="/portal/documents" element={
              <PortalLayout><PortalDocumentsPage /></PortalLayout>
            } />
            <Route path="/portal/analytics" element={
              <PortalLayout><PortalAnalyticsPage /></PortalLayout>
            } />
            <Route path="/portal/chat" element={
              <PortalLayout><PortalChatPage /></PortalLayout>
            } />
            <Route path="/portal/support" element={
              <PortalLayout><PortalSupportPage /></PortalLayout>
            } />
            <Route path="/portal/support/:ticketId" element={
              <PortalLayout><PortalSupportPage /></PortalLayout>
            } />
            <Route path="/portal/notifications" element={
              <PortalLayout><PortalNotificationsPage /></PortalLayout>
            } />
            <Route path="/portal/profile" element={
              <PortalLayout><PortalProfilePage /></PortalLayout>
            } />
          </Routes>
          </Suspense>
        </Router>
        <InstallPrompt />
        <UpdateNotification />
        <MessageBoxHost />
      </ThemeProvider>
    </Provider>
  );
}

export default App;